// src/components/ChatInterface.js
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FiPaperclip, FiCopy, FiDownload, FiShare2, FiFileText, FiFolder, FiMic, FiList, FiCornerUpLeft, FiLayers, FiPlus, FiChevronRight, FiChevronDown, FiCheck, FiLock, FiSlash } from 'react-icons/fi';
import { CUSTOM_RULES_UPDATED_EVENT } from './CustomRulesPanel';
import {
  addFileToKnowledgeScope,
  addFolderToKnowledgeScope,
  removeFileFromKnowledgeScope,
  removeFolderFromKnowledgeScope,
  scopeAfterPicks,
  withoutPick,
} from '../utils/knowledgeScopePicks';
import { KNOWLEDGE_HOLD_UPDATED_EVENT, KNOWLEDGE_SCOPE_UPDATED_EVENT, PROJECT_UPDATED_EVENT, PROJECT_FILES_UPDATED_EVENT } from './ProjectPanel';
import { useVirtualizer } from '@tanstack/react-virtual';
import './ChatInterface.css';
import { getApiBaseUrl, authenticatedFetch } from '../utils/api';
import { shareConversation, getPdfPageDrawing, listProjects, listProjectFiles, setConversationProject, cancelDirectDocumentUpload, startDirectDocumentIndexing, finalUploadError, listHeldFiles, answerHeldFiles, loadConversation, addBackToGroupChat, pollGroupChatActivity } from '../utils/conversationApi';
import { withoutOpeningNotes } from '../utils/openingNotes';
import {
  Avatar,
  GroupChatHeaderBits,
  GroupChatLeaveModal,
  GroupChatOriginalCopyModal,
  GroupChatPeopleModal,
  GroupChatQueue,
  heldTurnIds,
  GroupChatTurnBanner,
  GroupChatTyping,
  continuedNoteText,
  isMemberNote,
  knowledgeScopeNoteText,
  LeftGroupChatBar,
  ContinuedGroupChatBar,
  memberNoteText,
  MemberPhotosProvider,
  NewMessagesPill,
  authorOf,
  pendingFiles,
  pendingReplyContext,
  projectForChatInvite,
  replyLabel,
  useGroupChatActivity,
  useTypingSignal,
  useStableGroup,
} from './GroupChat';
import { OPUS_5_5_MODEL_ID } from '../utils/modelSelection';
import { focusModeDisplayName, groupChatFocusMode, withChatFocusMode } from '../utils/focusModes';
import ShareConversationModal from './ShareConversationModal';
import WebsitePreviewPanel from './WebsitePreviewPanel';
import HandoverModal from './HandoverModal';
import QuickOptionsCard, { mergeKaiOptionsCards } from './QuickOptionsCard';
import { useDarkMode } from '../contexts/DarkModeContext';
import { getGraphFileToken, useAuth } from '../auth/AuthContext';
import { useSpeechInput } from '../hooks/useSpeechInput';
import rocketLaunchIcon from '../assets/rocket-launch.webp';

// Group chats this visit has already asked about the user's own copy of
// (GroupChatOriginalCopyModal) -- module-level, since App remounts the chat
// screen (chatSessionKey) and that alone shouldn't ask again.
const askedOriginalCopies = new Set();

// ---------------------------------------------------------------------------
// Module-level pure helpers.
//
// These used to live inside ChatInterface which meant they got a fresh
// identity on every render. That in turn meant ReactMarkdown's `components`
// prop changed every time the user pressed a key in the input, forcing all
// messages (including expensive code blocks / tables) to re-render. Moving
// them out makes their identity stable and lets MessageItem be memoized.
// ---------------------------------------------------------------------------

// A short, unguessable client-minted id -- used for the durable session id
// and the ephemeral .docx template-profile token alike, just with a
// different prefix and lifetime managed by the caller. Both are bearer
// values a server-side cache trusts on sight (the docx-template token in
// particular gates a profile that can carry a header logo and full verbatim
// document paragraphs), so this needs a cryptographically secure source,
// not Math.random() -- which is predictable and never meant for anything
// resembling a credential.
const mintOpaqueId = (prefix) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${prefix}_${hex}`;
  }
  // Last-resort fallback for an environment with no Web Crypto API at all
  // (very old browser, non-secure context) -- not cryptographically secure.
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Same "in scope" rule as ProjectPanel.js's own isFileInScope, but file-only
// (mentioning never touches folder_ids, so there's nothing to resolve here) --
// used purely to show a checkmark on a file already added via a prior mention.
function isFileMentioned(scope, fileId) {
  if (!scope || scope.mode !== 'selected') return true;
  return (scope.file_ids || []).includes(fileId);
}

function isFolderMentioned(scope, folderId) {
  if (!scope || scope.mode !== 'selected') return true;
  return (scope.folder_ids || []).includes(folderId);
}

// `item` placed just before the message with id `beforeId` (at the end if
// that message is gone).
function insertBefore(messages, beforeId, item) {
  const at = messages.findIndex((m) => m.id === beforeId);
  return at === -1 ? [...messages, item] : [...messages.slice(0, at), item, ...messages.slice(at)];
}

// Splits the plain <textarea>'s own text into plain/mention runs for the
// highlight backdrop below it to render (see .message-input-highlight in
// ChatInterface.css) -- an "@" is only ever treated as a mention if the text
// right after it is an EXACT, whole match for "all" or one of this render's
// known project file names (mentionableNames, longest name first so a name
// that happens to prefix a longer one never wins by accident), starting at
// the beginning of the text or right after whitespace. This re-derives
// mentions from the text on every render rather than tracking insertion
// ranges as separate state -- simpler, and self-healing if the surrounding
// text is edited -- at the cost of one known false positive: text a user
// types by hand that happens to exactly match "@<a real file name>" gets
// highlighted too, same as a real mention would.
function buildMentionHighlightSegments(text, mentionableNames) {
  const segments = [];
  let i = 0;
  const n = text.length;
  while (i < n) {
    if (text[i] === '@' && (i === 0 || /\s/.test(text[i - 1]))) {
      let matchedName = null;
      for (const name of mentionableNames) {
        if (!text.startsWith(name, i + 1)) continue;
        // Require a real word boundary right after the candidate name too --
        // otherwise a short mentionable name that also happens to be a
        // prefix of a longer typed word (a file literally named "a", the
        // user typing the unrelated word "apple") would match just its own
        // length and leave the rest of the word dangling as plain text
        // immediately after it, with no space in between.
        const afterIdx = i + 1 + name.length;
        if (afterIdx < n && !/\s/.test(text[afterIdx])) continue;
        matchedName = name;
        break;
      }
      if (matchedName) {
        segments.push({ mention: true, text: `@${matchedName}` });
        i += 1 + matchedName.length;
        continue;
      }
    }
    let j = i + 1;
    while (j < n && !(text[j] === '@' && /\s/.test(text[j - 1]))) j += 1;
    segments.push({ mention: false, text: text.slice(i, j) });
    i = j;
  }
  return segments;
}

// Whether `caret` (a collapsed cursor position, not a selection) sits at, or
// just past, the right-hand edge of a complete "@mention" -- used by
// handleComposerKeyDown so Backspace there deletes the whole pill in one
// keystroke instead of eating it back one character at a time. Same
// longest-name-first matching as buildMentionHighlightSegments, so this
// always agrees with whatever the backdrop is actually showing as a pill.
function findMentionBeforeCaret(text, caret, mentionableNames) {
  // A completed mention is always followed by a few spaces of pure visual
  // padding (see selectMentionCandidate) that exist only so the pill's own
  // rounded-border bleed doesn't paint over the caret/next character. To the
  // user that padding reads as part of the pill's border, not as separate
  // characters they typed -- so walk left over any such run of plain spaces
  // (never newlines, which mark a real line break the user made on purpose)
  // before checking for a mention. Backspace then treats the pill AND its
  // padding as one atomic unit, matching what it visually looks like,
  // instead of requiring a throwaway keystroke per invisible padding space
  // before the "real" delete lands. Capped at 4 -- the widest gap
  // selectMentionCandidate ever inserts (two adjacent mentions) -- so a
  // deliberately typed run of spaces further back is never silently eaten.
  const MAX_TRAILING_PAD = 4;
  let end = caret;
  while (end > 0 && caret - end < MAX_TRAILING_PAD && text[end - 1] === ' ') end -= 1;
  // The (possibly padding-stripped) boundary must sit at a real token
  // boundary -- end of the text, or followed by whitespace -- not embedded
  // inside a longer contiguous run of characters. Without this, moving the
  // caret back into the middle of an already-typed "@applesauce" (right
  // after "apple", with a mentionable file literally named "apple") would
  // still find "@apple" ending exactly there and treat it as a complete,
  // atomic mention, even though "sauce" immediately follows it.
  if (end < text.length && !/\s/.test(text[end])) return null;
  for (const name of mentionableNames) {
    const start = end - 1 - name.length;
    if (start < 0) continue;
    if (text[start] !== '@') continue;
    if (text.slice(start + 1, end) !== name) continue;
    if (start !== 0 && !/\s/.test(text[start - 1])) continue;
    return { start, end: caret, name };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Rich prompt input helpers (contentEditable box used only while a custom
// rule is attached -- see richInputRef in the component). Kept as pure
// functions operating on an explicit element rather than closures, same
// reasoning as the other module-level helpers above: stable identity, and
// easy to reason about since none of this touches React state directly.
//
// A plain <textarea> cannot show the rule pill inline with the first line of
// typed text while still giving wrapped lines the full box width -- a
// textarea is one fixed-width rectangle, so reserving room for a pill on
// line 1 (via padding) would reserve that same room on every line. A
// contentEditable box can, because the pill is a genuine inline DOM element
// the browser reflows text around, the same way Claude Code's own input
// does it. The tradeoff: once mounted, this box's contents are intentionally
// NOT re-rendered by React on every keystroke (that would fight the
// browser's own caret handling); it's synced FROM React only when
// `inputMessage` changes for a reason other than the user typing directly
// into it (send, Escape, dictation, a quick-fill button) -- see the
// "sync text" effect below, which only touches the DOM when it and
// `inputMessage` have actually diverged.
// ---------------------------------------------------------------------------

const isRulePillNode = (node) =>
  node.nodeType === Node.ELEMENT_NODE && node.hasAttribute && node.hasAttribute(RULE_PILL_ATTR);

// Plain text currently in the box, excluding the rule pill chip(s).
const extractRichInputText = (el) => {
  if (!el) return '';
  let text = '';
  el.childNodes.forEach((node) => {
    if (isRulePillNode(node)) return;
    text += node.textContent;
  });
  return text;
};

// The live caret position, expressed as an index into extractRichInputText's
// return value (i.e. the same coordinate space as inputMessage/a plain
// <textarea>'s own selectionStart) -- lets the "@" mention feature
// (updateMentionFromCaret/findMentionBeforeCaret, both written against a
// textarea's selectionStart) work against the rich contentEditable box too,
// which has no such property of its own. A rule pill contributes nothing
// (pills are excluded from the extracted text, same as extractRichInputText
// itself), so the caret's offset is just how far into the trailing plain-text
// node it sits, plus the length of any plain-text node before it (there
// normally is none -- pills always come first -- but this doesn't assume that).
const getRichInputCaretTextOffset = (el) => {
  if (!el) return null;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return null;
  const { startContainer, startOffset } = selection.getRangeAt(0);
  if (startContainer !== el && !el.contains(startContainer)) return null;

  let offset = 0;
  for (const node of el.childNodes) {
    if (node === startContainer) {
      return offset + (isRulePillNode(node) ? 0 : startOffset);
    }
    if (node.contains && node.contains(startContainer)) {
      if (isRulePillNode(node)) {
        // Caret landed inside a pill's own subtree (pills are marked
        // contenteditable="false", but a click can still anchor a selection
        // somewhere inside them) -- treat it as sitting right before the
        // pill, since pill text isn't part of the extracted text at all.
        return offset;
      }
      // A mention span (see buildRichTextNodes) -- unlike a rule pill, it IS
      // part of the extracted text, and only ever wraps a single plain text
      // node, so startOffset is already a character-accurate index into it.
      return offset + startOffset;
    }
    if (!isRulePillNode(node)) offset += (node.textContent || '').length;
  }
  // startContainer === el itself (e.g. right after placeCaretAtEndIfFocused's
  // selectNodeContents+collapse(false)): startOffset then counts CHILD NODES,
  // not characters.
  if (startContainer === el) {
    let acc = 0;
    for (let i = 0; i < startOffset && i < el.childNodes.length; i += 1) {
      const node = el.childNodes[i];
      if (!isRulePillNode(node)) acc += (node.textContent || '').length;
    }
    return acc;
  }
  return offset;
};

// The inverse of getRichInputCaretTextOffset -- collapses the selection to
// the DOM position corresponding to a given character offset into the rich
// input's extracted text (same coordinate space as inputMessage/a textarea's
// .selectionStart). Used after a mention insertion/deletion rebuilds this
// box's text nodes wholesale (see syncRichInputText's own effect below),
// since that rebuild otherwise leaves the caret wherever the browser
// happens to collapse it -- typically the very end of the box, not the
// point the edit actually landed on.
const setRichInputCaretTextOffset = (el, offset) => {
  if (!el) return;
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  let remaining = offset;
  for (const node of el.childNodes) {
    if (isRulePillNode(node)) continue;
    const len = (node.textContent || '').length;
    if (remaining <= len) {
      if (node.nodeType === Node.TEXT_NODE) {
        range.setStart(node, remaining);
      } else if (node.firstChild && node.firstChild.nodeType === Node.TEXT_NODE) {
        // A mention span (see buildRichTextNodes) -- descend into its one
        // text-node child, same assumption getRichInputCaretTextOffset makes.
        range.setStart(node.firstChild, Math.min(remaining, node.firstChild.textContent.length));
      } else {
        range.setStartAfter(node);
      }
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    remaining -= len;
  }
  // Offset is past all extracted text (or the box is empty) -- land at the
  // very end, same fallback getRichInputCaretTextOffset's own callers use.
  range.selectNodeContents(el);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

const placeCaretAtEndIfFocused = (el) => {
  if (!el || document.activeElement !== el) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
};

// Same segmentation as buildMentionHighlightSegments, but building REAL DOM
// nodes for the rich (contentEditable) input's own plain-text portion
// (after any rule pills) instead of JSX for a separate invisible-text/
// visible-backdrop pair -- there's no such pair here, this box IS the
// visible, editable content, so a mention just becomes an actual <span>
// with its own pill styling (see ".rich-input .message-input-mention" in
// ChatInterface.css, which drops the plain composer's caret-alignment
// margin-cancelling trick since nothing needs to stay pixel-aligned with an
// invisible twin in this mode). Left as an ordinary (not
// contenteditable="false") span, same as a rule pill is NOT -- unlike a
// rule, a mention is still just plain characters the user can arrow through
// or edit letter-by-letter; only Backspace right at its end treats it as
// atomic (see findMentionBeforeCaret/handleComposerKeyDown).
const buildRichTextNodes = (text, mentionableNames) => (
  buildMentionHighlightSegments(text, mentionableNames || []).map((seg) => {
    if (!seg.mention) return document.createTextNode(seg.text);
    const span = document.createElement('span');
    span.className = 'message-input-mention';
    span.textContent = seg.text;
    // The pill itself truncates a long filename with an ellipsis (see the
    // CSS) -- the native tooltip is the only way to still see the full name.
    span.title = seg.text;
    return span;
  })
);

// Replaces only the editable text after the pill(s), leaving the pill nodes
// themselves untouched. Used to pull a programmatic `inputMessage` change
// (send/Escape/dictation/a mention just picked from the popover) into the
// DOM without disturbing the pills.
const syncRichInputText = (el, text, mentionableNames) => {
  if (!el) return;
  Array.from(el.childNodes).forEach((node) => {
    if (!isRulePillNode(node)) el.removeChild(node);
  });
  if (text) {
    buildRichTextNodes(text, mentionableNames).forEach((node) => el.appendChild(node));
  }
  placeCaretAtEndIfFocused(el);
};

// Rebuilds the pill chip(s) from scratch (cheap: this only runs when the
// attached-rule set itself changes, not on every keystroke) and re-appends
// whatever text was already there. `onRemove(ruleId)` is called via a ref
// the caller keeps current, so a pill created in an earlier render never
// invokes a stale closure.
//
// `lockedRuleIds` (a project's default_rule_ids -- see the projectDefaultRules
// section below) render without a remove button: unlike an ad-hoc attached
// rule, a project default isn't something THIS conversation can detach --
// it's applied to every conversation in the project until someone changes it
// in Project Settings, so offering a button that looked like it would remove
// the rule but actually wouldn't (or worse, silently re-added it to this
// conversation's own attached-rule list) would just be confusing.
const rebuildRichInputPills = (el, rules, currentText, onRemoveRef, lockedRuleIds = [], mentionableNames) => {
  if (!el) return;
  el.innerHTML = '';
  rules.forEach((rule) => {
    const locked = lockedRuleIds.includes(rule.rule_id);
    const pill = document.createElement('span');
    pill.setAttribute(RULE_PILL_ATTR, 'true');
    pill.setAttribute('contenteditable', 'false');
    pill.className = locked ? 'custom-rule-pill custom-rule-pill-locked' : 'custom-rule-pill';
    pill.title = locked
      ? `${rule.text}\n\n(Always on in this project -- manage it from Project Settings)`
      : rule.text;

    const titleSpan = document.createElement('span');
    titleSpan.className = 'custom-rule-pill-title';
    titleSpan.textContent = rule.title || rule.text;
    pill.appendChild(titleSpan);

    if (!locked) {
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'custom-rule-pill-remove';
      removeBtn.setAttribute('aria-label', 'Detach rule');
      removeBtn.title = 'Detach rule';
      removeBtn.textContent = '×';
      // Same reasoning as the mention-menu's own onMouseDown below (see
      // selectMentionCandidate's button) -- this sits inside a live
      // contentEditable region, so without preventing the browser's default
      // mousedown handling first, that default (collapsing the selection/
      // caret to the click point, inside a parent that's still editable
      // even though this pill itself is contenteditable="false") can run
      // before the click ever reaches this listener, silently swallowing
      // it in some browsers. Stopping it at mousedown guarantees the click
      // actually lands here instead of just moving the caret.
      removeBtn.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        onRemoveRef.current(rule.rule_id);
      });
      pill.appendChild(removeBtn);
    }

    el.appendChild(pill);
  });
  if (currentText) {
    buildRichTextNodes(currentText, mentionableNames).forEach((node) => el.appendChild(node));
  }
  placeCaretAtEndIfFocused(el);
};

const MODEL_NAMES = {
  'us.anthropic.claude-3-7-sonnet-20250219-v1:0': 'Claude 3.7 Sonnet',
  'us.anthropic.claude-sonnet-4-20250514-v1:0': 'Claude Sonnet 4',
  'us.anthropic.claude-sonnet-4-5-20250929-v1:0': 'Claude Sonnet 4.5',
  'us.anthropic.claude-opus-4-5-20251101-v1:0': 'Claude Opus 4.5',
  'us.anthropic.claude-opus-4-8': 'Claude Opus 4.8',
  'us.anthropic.claude-opus-5-5': 'Claude Opus 5.5',
  'us.anthropic.claude-sonnet-5': 'Claude Sonnet 5',
  'us.anthropic.claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'us.anthropic.claude-opus-4-6-v1': 'Claude Opus 4.6',
  'anthropic.claude-3-5-sonnet-20241022-v2:0': 'Claude 3.5 Sonnet',
  'anthropic.claude-3-haiku-20240307-v1:0': 'Claude 3 Haiku',
  'us.amazon.nova-pro-v1:0': 'Nova Pro',
  'us.amazon.nova-lite-v1:0': 'Nova Lite',
  'us.amazon.nova-micro-v1:0': 'Nova Micro',
  'azure/gpt-4.1': 'GPT-4.1',
  'azure/gpt-5.1-chat': 'GPT-5.1',
  'azure/gpt-5.2-chat': 'GPT-5.2',
  'azure/gpt-5.4': 'GPT-5.4',
  'gemini-3.1-pro-preview': 'Gemini 3.1 Pro',
  'gemini-2.5-flash': 'Gemini 2.5 Flash',
};

const formatModelName = (modelId) => {
  if (MODEL_NAMES[modelId]) return MODEL_NAMES[modelId];
  return (modelId || '')
    .replace('us.amazon.', '')
    .replace('us.anthropic.', '')
    .replace('anthropic.', '')
    .replace('-v1:0', '')
    .replace('-v2:0', '')
    .replace('-20250219', '')
    .replace('-20250514', '')
    .replace('-20241022', '')
    .replace('-20240307', '')
    .replace('-20250929', '')
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Short nickname shown on the composer's model-picker button only (e.g.
// "Sonnet 5" instead of "Claude Sonnet 5") -- the dropdown menu and every
// other place a model name appears keep using formatModelName in full.
const MODEL_NICKNAMES = {
  'us.anthropic.claude-sonnet-5': 'Sonnet 5',
  'us.anthropic.claude-opus-5-5': 'Opus 5.5',
  'azure/gpt-5.4': 'GPT-5.4',
  'gemini-3.1-pro-preview': 'Gemini 3.1',
};

const formatModelNickname = (modelId) => MODEL_NICKNAMES[modelId] || formatModelName(modelId);

// Short "when to use this" hint shown under each model's name in the
// dropdown only -- helps users pick between models without needing to
// already know each vendor's strengths.
const MODEL_TAGLINES = {
  'us.anthropic.claude-sonnet-5': 'Everyday · Fast',
  'us.anthropic.claude-opus-5-5': 'Complex tasks · Deep thinking',
  'azure/gpt-5.4': 'All-rounder · Brainstorming',
  'gemini-3.1-pro-preview': 'Long files · Big content',
};

// Opus is the priciest model on offer -- switching to it (from any other
// model) asks for confirmation first; every other switch stays instant.
// (OPUS_5_5_MODEL_ID itself now lives in utils/modelSelection so App.js's
// new-conversation reset can share the same value.)

// Vendor prefix for the model-picker dropdown only (e.g. "OpenAI GPT-5.4",
// "Google Gemini 3.1 Pro") -- the plain name from formatModelName is used
// everywhere else (header subtitle, message badges) and stays unprefixed.
const getModelVendorLabel = (modelId) => {
  const name = formatModelName(modelId);
  if (modelId?.startsWith('azure/')) return `OpenAI ${name}`;
  if (modelId?.startsWith('gemini')) return `Google ${name}`;
  return name;
};

// Maps a failed sendMessage() error to copy that an end user can act on, instead
// of surfacing raw transport text like "HTTP error! status: 504".
const getFriendlyErrorMessage = (error) => {
  const status = error?.status;
  if (status === 504 || status === 502 || status === 503) {
    return "KAI is taking longer than usual to respond. Please try again.";
  }
  if (typeof status === 'number' && status >= 500) {
    return "Something went wrong on KAI's end. Please try again.";
  }
  if (error?.name === 'TypeError') {
    // Fetch itself failed to complete (offline, DNS, CORS) — no HTTP status at all.
    return "Couldn't reach KAI. Check your connection and try again.";
  }
  return "Something went wrong sending your message. Please try again.";
};

const formatMessageTime = (timestamp) => {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '—';
  const now = new Date();
  const isToday = date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatTokenCount = (count) => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
  return count.toString();
};

const getContextLevel = (percentage) => {
  if (percentage >= 95) return 'critical';
  if (percentage >= 90) return 'warning';
  if (percentage >= 75) return 'caution';
  return 'normal';
};

// Attachment guardrails. Without these, dropping a large batch of files
// (e.g. 100+ at once) silently fills the model's context window and tanks
// response quality/latency with no feedback to the user.
const MAX_ATTACHMENTS_PER_PROMPT = 20;
const MAX_ATTACHMENT_FILE_SIZE = 25 * 1024 * 1024; // 25MB, matches backend MAX_FILE_SIZE (PDFs included, outside a project)
const MAX_PROJECT_DOCUMENT_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB, matches backend DOCUMENT_UPLOAD_MAX_BYTES (S3's own limit for one upload; any knowledge-base document)
const MAX_VIDEO_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB, matches backend MAX_VIDEO_FILE_SIZE (AWS Transcribe's own hard ceiling)
// PDFs previously carried their own raised ceilings (100MB upload, 900k
// attachment tokens) as a stopgap for heavy DD sets. Both are gone now that
// indexed PDF retrieval has shipped: a heavy PDF is replaced by a ~40-token
// pointer, so it can no longer approach either cap in the first place, and
// PDFs are back on the same limits as every other document.
// See .claude/plan/plan_pdfhandle.md.
const ATTACHMENT_TOKEN_WARNING = 100000; // warn (but still attach) past this
const MAX_ATTACHMENT_TOKENS_TOTAL = 250000; // hard cap on summed tokens across attached files

// Matches backend config.MAX_ACTIVE_RULES_PER_CONVERSATION. The backend
// re-enforces this on both the PATCH endpoint and the chat turn itself, so a
// drift here is only a UX inconvenience, never a way to bypass the cap.
const MAX_ACTIVE_RULES_PER_CONVERSATION = 5;
// Turns that could connect a SharePoint source are the only ones that ask MSAL
// for a Graph file token (see getGraphFileToken). Deliberately loose: a false
// positive costs one silent, cached token acquisition, while a false negative
// silently skips the access check -- so this errs towards matching.
const SHAREPOINT_LINK_RE = /https?:\/\/[^\s]*(sharepoint\.com|onedrive\.live\.com|1drv\.ms)/i;

// How far back to look for a link. Connecting a source is rarely one turn:
// KAI answers a pasted link by asking which worksheet, which cells, or which
// file inside a folder, and the reply that actually triggers the connect is
// just "Sheet1" -- no URL in it at all. Matching only the outgoing message
// meant the access check silently did not run on precisely the turn that
// created the source, and a resumed conversation never ran it at all.
const SHAREPOINT_LOOKBACK_MESSAGES = 12;

const messageMentionsSharePoint = (message) => {
  const content = message?.content;
  if (typeof content === 'string') return SHAREPOINT_LINK_RE.test(content);
  // Assistant turns carry content as blocks; only the text ones can hold a
  // link, and anything else stringifies to noise rather than a false match.
  if (Array.isArray(content)) {
    return content.some(
      (part) => typeof part?.text === 'string' && SHAREPOINT_LINK_RE.test(part.text)
    );
  }
  return false;
};

// True when this turn might connect a SharePoint source, and so should carry a
// delegated token for the backend's access check.
const turnMayConnectSharePoint = (outgoing, history) => {
  if (SHAREPOINT_LINK_RE.test(outgoing || '')) return true;
  const recent = Array.isArray(history)
    ? history.slice(-SHAREPOINT_LOOKBACK_MESSAGES)
    : [];
  return recent.some(messageMentionsSharePoint);
};

// Client-side ceiling on PATCH /conversations/{id}/active-rules. Comfortably
// above the server's own worst case (its rule-vs-rule conflict pass is
// deadline-bounded, see custom_rules_validator.find_conflict_within_set) so
// this only ever fires on a genuinely stalled connection -- but these
// requests are serialized, so without it one stalled toggle would block every
// later one indefinitely.
const ACTIVE_RULES_PATCH_TIMEOUT_MS = 45000;

// Marks a pill <span> inside the rich (contentEditable) prompt input so the
// text-extraction/rebuild helpers below can tell "a rule chip" apart from
// "text the user typed" when walking the box's child nodes.
const RULE_PILL_ATTR = 'data-rule-pill';

// .mp4/.mp3/.m4a all go through the same async upload + AWS Transcribe job
// pipeline (matches backend TRANSCRIBABLE_MEDIA_EXTENSIONS in config.py).
const TRANSCRIBABLE_MEDIA_EXTENSIONS = ['.mp4', '.mp3', '.m4a'];
const isTranscribableMedia = (file) => (
  TRANSCRIBABLE_MEDIA_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))
);

const isPdfFile = (file) => file.name.toLowerCase().endsWith('.pdf');

// Mirrors backend config.PDF_INDEX_GENERIC_EXTENSIONS -- the non-PDF formats
// that go through the same indexing pipeline a PDF does. Used below to decide
// whether a project-scoped upload is eligible for the direct-to-S3 path (see
// uploadDocumentDirect) instead of being proxied through our own backend.
const INDEXABLE_GENERIC_EXTENSIONS = ['.txt', '.md', '.markdown', '.csv', '.docx', '.xlsx', '.pptx', '.eml', '.msg'];
const isIndexableDocument = (file) => (
  isPdfFile(file) || INDEXABLE_GENERIC_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))
);

// Audio/video files get a larger cap than regular documents. Outside a
// project a PDF gets the same 25MB as everything else, checked here so a
// 150MB PDF isn't uploaded in full only to be refused at the end (the alert
// points at a project instead -- see projectSizeSuggestion). Passing it is
// NOT the same as "always accepted", though: the server can still reject a
// non-project PDF once its actual
// page/token count is known (see chat_routes.py's PDF_PROJECT_HINT_TOKENS/
// PAGES check) -- this function has no way to know that ahead of the upload,
// since only the server extracts pages/tokens. A project-scoped document of
// any indexable format (PDF, Word, Excel, PowerPoint, text, email) is held
// only to MAX_PROJECT_DOCUMENT_FILE_SIZE, S3's own limit for one upload (see
// chat_routes.py's /upload-file): the whole point of adding something to a
// project's library is index-once-search-forever regardless of source size,
// the server reads every format from disk rather than memory, and a project
// upload also skips that later page/token check entirely on the server.
// isProjectUpload is the caller's best guess at upload time (the
// conversation's own project_id, or the one about to be created) -- NOT a
// guarantee the file will actually end up scoped there, since the user
// still gets asked (see askAddToKnowledgeBase); callers that go on to ask
// must re-check against isProjectUpload=false if declined.
const getMaxSizeForFile = (file, isProjectUpload = false) => {
  if (isTranscribableMedia(file)) return MAX_VIDEO_FILE_SIZE;
  if (isProjectUpload && isIndexableDocument(file)) return MAX_PROJECT_DOCUMENT_FILE_SIZE;
  return MAX_ATTACHMENT_FILE_SIZE;
};

// Appended to a size-rejection message only when the upload ISN'T already
// project-scoped (callers check -- a project wouldn't help a file that's
// already too big for one) and is a document a project's knowledge base
// takes (audio/video has its own, unrelated 2GB cap that a project doesn't
// change). projectHint is projectAttachHint's: inside a project the user can
// only view, it points at someone who can add the file instead.
const projectSizeSuggestion = (file, projectHint = null) => {
  if (!isIndexableDocument(file)) return '';
  if (projectHint === 'view') {
    return `\n\nFiles in this project's Knowledge base can be up to ${formatSize(MAX_PROJECT_DOCUMENT_FILE_SIZE)}, `
      + 'but you have view access only -- ask the project owner or an editor to add it.';
  }
  return '\n\nWant KAI to work with this file anyway? Create a Personal Project and add it to that '
    + `project's Knowledge base -- documents added there can be up to ${formatSize(MAX_PROJECT_DOCUMENT_FILE_SIZE)}.`;
};

// The body of a size-rejection message for one file.
const tooLargeToAttachText = (file, limit) => (
  `It's ${formatSize(file.size)}, over the ${formatSize(limit)} limit for attachments, so it hasn't been attached.`
);

const formatMB = (bytes) => {
  const mb = bytes / (1024 * 1024);
  return (Number.isInteger(mb) ? mb : mb.toFixed(1)).toString();
};

// "25MB", "100MB", "2GB", "5GB" -- a size a person reads, MB below 1GB.
const formatSize = (bytes) => {
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${Number.isInteger(gb) ? gb : gb.toFixed(1)}GB`;
  return `${formatMB(bytes)}MB`;
};

// --- Video upload/transcription ------------------------------------------
//
// A video upload starts an AWS Transcribe job that can legitimately take
// minutes, so the backend never blocks a single request on it — /upload-file
// (or /upload-file/video-start) returns immediately once the job is started,
// and the frontend polls /upload-file/jobs/{job_id} for the result. This is
// what keeps any single request short enough to never hit the production
// load balancer's idle-connection timeout (see backend/config.py and
// video_transcribe_service.py for the server side of this).
//
// Heavy PDFs use the same start-job + poll shape (see plan_pdfhandle.md): the
// backend answers /upload-file with 202 + a job id and indexes the document in
// the background, so the bytes are searchable by the time the user sends their
// first question.
//
// Matches backend config.py defaults; not fetched from the server since it's
// purely a client-side polling cadence, not something that needs to be kept
// in lockstep with the backend.
// One string per knowledge base selection, whatever order its ids or keys
// came in: a poll's copy and the opened one compare equal when they match.
function knowledgeScopeKey(scope) {
  if (!scope || scope.mode !== 'selected') return 'all';
  const ids = (list) => [...(list || [])].sort().join(',');
  return `selected:${ids(scope.file_ids)}|${ids(scope.folder_ids)}`;
}

// The note above a question that ran with project instructions changed
// since the conversation's previous answer, by its instructions_change.kind.
const INSTRUCTIONS_CHANGE_TEXT = {
  added: 'Project instructions added. Answers from here on follow them.',
  updated: 'Project instructions updated. Answers from here on follow the new version.',
  removed: "Project instructions removed. Answers from here on don't follow them.",
};

const JOB_POLL_INTERVAL_MS = 5000;
// How long a group chat's "Unread messages" line stays once the chat is open
// and in view, then how long it takes to fade and fold away.
const UNREAD_DIVIDER_MS = 12000;
const UNREAD_DIVIDER_LEAVE_MS = 400;
// How often a read-only copy of a group chat looks whether its owner has been
// added back to the chat.
const BACK_IN_GROUP_CHAT_CHECK_MS = 10000;
const JOB_POLL_CEILING_MS = 30 * 60 * 1000;

const safeJsonParse = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

// Uploads the file body straight to S3 via a presigned POST obtained from the
// backend, so the (up to 2GB) bytes never pass through the ALB/Apache/
// gunicorn app tier at all. Uses XMLHttpRequest (not fetch) specifically
// because fetch cannot report upload progress.
const uploadVideoDirect = async (file, onProgress, signal, projectId) => {
  const apiBase = getApiBaseUrl();
  const initResponse = await authenticatedFetch(`${apiBase}/upload-file/video-init`, {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, project_id: projectId || null }),
  });
  if (!initResponse.ok) {
    throw new Error(`Direct upload unavailable (status ${initResponse.status})`);
  }
  const { job_id: jobId, upload_url: uploadUrl, fields } = await initResponse.json();

  await new Promise((resolve, reject) => {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
    formData.append('file', file); // must be the last field per S3's POST policy rules

    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadUrl);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100); // browsers don't guarantee a final 100% progress event
        resolve();
      } else {
        reject(new Error(`S3 upload failed (status ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('S3 upload network error'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));
    if (signal) {
      if (signal.aborted) xhr.abort();
      else signal.addEventListener('abort', () => xhr.abort());
    }
    xhr.send(formData);
  });

  const startResponse = await authenticatedFetch(`${apiBase}/upload-file/video-start/${jobId}`, {
    method: 'POST',
  });
  if (!startResponse.ok) {
    const error = await safeJsonParse(startResponse);
    throw new Error(error?.detail || `Could not start transcription (status ${startResponse.status})`);
  }
  return jobId;
};

// Fallback path: proxy the video body through our own backend, exactly like
// every other file type. Used when the direct-to-S3 path is disabled or
// fails for any reason (CORS not yet configured, network issue, etc).
const uploadVideoProxied = async (file, projectId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (projectId) formData.append('project_id', projectId);
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/upload-file`, {
    method: 'POST',
    headers: {}, // Don't set Content-Type for FormData
    body: formData,
  });
  if (!response.ok) {
    const error = await safeJsonParse(response);
    throw new Error(error?.detail || 'Upload failed');
  }
  const { job_id: jobId } = await response.json();
  return jobId;
};

// Polls the job status endpoint until the result is ready. Shared by both
// background upload paths — audio/video transcription and heavy-PDF indexing —
// since the backend routes them by job-id prefix behind one endpoint. The
// endpoint always responds 200 while a job exists (IN_PROGRESS or COMPLETED
// shape); non-2xx means the job failed, was never found, or was invalid.
//
// projectId must match whatever project_id (if any) the /upload-file POST
// that started this job was given: for a heavy-PDF job, meta lives under
// that project's own scope key (see chat_routes.py's upload_file_job_status),
// so polling without it looks in the wrong place and 404s as "no upload job
// found" even though the job is running fine. A video/audio transcription
// job's own meta isn't partitioned by project the same way -- this param is
// harmless to omit for locating one -- but its STORED project_id (set at
// upload time, see uploadVideoDirect/uploadVideoProxied) is what decides
// whether the finished transcript gets indexed into that project's library
// instead of staying an ephemeral, conversation-only attachment.
const pollUploadJob = async (jobId, signal, projectId) => {
  const apiBase = getApiBaseUrl();
  const deadline = Date.now() + JOB_POLL_CEILING_MS;
  const jobUrl = `${apiBase}/upload-file/jobs/${jobId}${projectId ? `?project_id=${encodeURIComponent(projectId)}` : ''}`;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new Error('Upload cancelled');

    const response = await authenticatedFetch(jobUrl, { method: 'GET' });
    const data = await safeJsonParse(response);
    if (!response.ok) {
      throw new Error(data?.detail || `Processing failed (status ${response.status})`);
    }
    if (!data) {
      throw new Error('Malformed response while checking job status');
    }
    if (data.success) {
      return data; // COMPLETED: {name, content, size, type, tokens, success}
    }

    await new Promise((resolve) => setTimeout(resolve, JOB_POLL_INTERVAL_MS));
  }
  throw new Error('Timed out waiting for the upload to finish processing.');
};

// Orchestrates the whole video upload: try the direct-to-S3 path first,
// automatically fall back to proxying through our backend on any failure,
// then poll until the transcript is ready.
const uploadVideo = async (file, { onProgress, signal, projectId } = {}) => {
  let jobId;
  try {
    jobId = await uploadVideoDirect(file, onProgress, signal, projectId);
  } catch (directError) {
    // A cancelled upload stays cancelled, not re-sent the proxied way.
    if (signal?.aborted) throw directError;
    console.warn('Direct video upload failed, falling back to proxied upload:', directError);
    onProgress?.(null); // proxied path can't report upload progress
    jobId = await uploadVideoProxied(file, projectId);
  }
  return pollUploadJob(jobId, signal, projectId);
};

// --- Document (project knowledge-base) upload ----------------------------
//
// Same shape as the video direct-upload above: a presigned POST sends the
// file body straight to S3, so a huge drawing set or spec book never passes
// through the ALB/Apache/gunicorn app tier (and its LimitRequestBody
// ceiling) at all. Only used for a project-scoped PDF/indexable document
// (see isIndexableDocument) -- a project's knowledge base is the one case
// that actually needs this, since every other document upload already has a
// real, much smaller byte cap (25MB) that the proxied path
// handles fine, and recordings have their own direct path above.
const uploadDocumentDirect = async (file, onProgress, signal, projectId) => {
  const apiBase = getApiBaseUrl();
  const initResponse = await authenticatedFetch(`${apiBase}/upload-file/document-init`, {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, project_id: projectId || null }),
  });
  if (initResponse.status === 403 || initResponse.status === 404) {
    const error = await safeJsonParse(initResponse);
    throw finalUploadError(error?.detail || `Upload failed (status ${initResponse.status})`);
  }
  if (!initResponse.ok) {
    throw new Error(`Direct upload unavailable (status ${initResponse.status})`);
  }
  const { job_id: jobId, upload_url: uploadUrl, fields } = await initResponse.json();

  try {
    await new Promise((resolve, reject) => {
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
      formData.append('file', file); // must be the last field per S3's POST policy rules

      const xhr = new XMLHttpRequest();
      xhr.open('POST', uploadUrl);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(100); // browsers don't guarantee a final 100% progress event
          resolve();
        } else {
          reject(new Error(`S3 upload failed (status ${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error('S3 upload network error'));
      xhr.onabort = () => reject(finalUploadError('Upload cancelled'));
      if (signal) {
        if (signal.aborted) xhr.abort();
        else signal.addEventListener('abort', () => xhr.abort());
      }
      xhr.send(formData);
    });
  } catch (error) {
    // Clear the half-made job (before any fallback to the proxied upload,
    // or it lingers beside the copy that does get indexed).
    await cancelDirectDocumentUpload(jobId, projectId || null);
    throw error;
  }
  // The file is in S3 now: never cancelled or re-sent from here on.
  await startDirectDocumentIndexing(apiBase, jobId, projectId || null);
  return jobId;
};

// Fallback path: proxy the document body through our own backend, exactly
// like every other file type. Used when the direct-to-S3 path is disabled or
// itself fails (CORS not yet configured, network issue, etc) -- never after a
// cancel or a refusal (see finalUploadError). Always polls -- a
// project-scoped indexable document always indexes in the background (see
// chat_routes.py's should_index), never returns inline.
const uploadDocumentProxied = async (file, projectId, signal) => {
  const formData = new FormData();
  formData.append('file', file);
  if (projectId) formData.append('project_id', projectId);
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/upload-file`, {
    method: 'POST',
    headers: {}, // Don't set Content-Type for FormData
    body: formData,
    signal,
  });
  if (!response.ok) {
    const error = await safeJsonParse(response);
    throw new Error(error?.detail || 'Upload failed');
  }
  const { job_id: jobId } = await response.json();
  return jobId;
};

// Orchestrates a project-scoped document upload: try the direct-to-S3 path
// first, fall back to proxying through our backend if the direct path itself
// fails, then poll until indexing is done. A cancel or a refusal is final --
// re-sending the file would index a cancelled upload anyway, or upload a
// refused one twice.
const uploadDocument = async (file, { onProgress, signal, projectId } = {}) => {
  let jobId;
  try {
    jobId = await uploadDocumentDirect(file, onProgress, signal, projectId);
  } catch (directError) {
    if (directError.noFallback || signal?.aborted) throw directError;
    console.warn('Direct document upload failed, falling back to proxied upload:', directError);
    onProgress?.(null); // proxied path can't report upload progress
    jobId = await uploadDocumentProxied(file, projectId, signal);
  }
  return pollUploadJob(jobId, signal, projectId);
};

const filterImageUrls = (content) => {
  if (!content) return content;
  return content.replace(/\[Image urls:\s*https:\/\/kpf-kai\.s3\.amazonaws\.com\/[^\]]+\]/g, '').trim();
};

const downloadImage = async (imageUrl, imageName) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = imageName || 'image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Failed to download image:', error);
    window.open(imageUrl, '_blank');
  }
};

const copyCodeToClipboard = async (codeText, buttonElement) => {
  const showFeedback = () => {
    if (!buttonElement) return;
    const originalHTML = buttonElement.innerHTML;
    buttonElement.innerHTML = '<svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none"><polyline points="20 6 9 17 4 12"></polyline></svg>';
    buttonElement.style.color = '#10b981';
    setTimeout(() => {
      buttonElement.innerHTML = originalHTML;
      buttonElement.style.color = '';
    }, 2000);
  };

  try {
    await navigator.clipboard.writeText(codeText);
    showFeedback();
  } catch (error) {
    console.error('Failed to copy code:', error);
    try {
      const textArea = document.createElement('textarea');
      textArea.value = codeText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showFeedback();
    } catch (fallbackError) {
      console.error('Failed to copy code with fallback method:', fallbackError);
    }
  }
};

const copyMarkdownTableToClipboard = async (tableElement) => {
  if (!tableElement) return;
  try {
    const rows = tableElement.querySelectorAll('tr');
    const headerRow = rows[0];
    const headers = Array.from(headerRow.querySelectorAll('th, td')).map(cell =>
      cell.textContent.trim()
    );
    const tableData = [];
    for (let i = 1; i < rows.length; i++) {
      const cells = rows[i].querySelectorAll('td');
      const rowData = Array.from(cells).map(cell => {
        const link = cell.querySelector('a');
        if (link) {
          if (link.href.startsWith('mailto:')) {
            return link.href.replace('mailto:', '');
          }
          return link.href;
        }
        return cell.textContent.trim();
      });
      tableData.push(rowData);
    }
    const tsvContent = [
      headers.join('\t'),
      ...tableData.map(row => row.join('\t'))
    ].join('\n');
    await navigator.clipboard.writeText(tsvContent);
  } catch (error) {
    console.error('Failed to copy markdown table:', error);
    try {
      const textContent = tableElement.textContent;
      const textArea = document.createElement('textarea');
      textArea.value = textContent;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    } catch (fallbackError) {
      console.error('Failed to copy markdown table with fallback method:', fallbackError);
    }
  }
};

// Pure image utility helpers. Hoisted to module scope so they have stable
// identities and don't bloat the dependency arrays of useCallback hooks
// that wrap handlers depending on them (handleImageSelect / handlePaste).
const resizeImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              dataUrl: e.target.result,
              blob: blob,
              width: width,
              height: height,
              originalSize: file.size,
              newSize: blob.size,
            });
          };
          reader.readAsDataURL(blob);
        },
        file.type,
        quality
      );
    };

    img.src = URL.createObjectURL(file);
  });
};

const generateUniqueFilename = (originalName) => {
  const randomString = Math.random().toString(36).substring(2, 10);
  const lastDotIndex = originalName.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `${originalName}_${randomString}`;
  }
  const nameWithoutExt = originalName.substring(0, lastDotIndex);
  const extension = originalName.substring(lastDotIndex);
  return `${nameWithoutExt}_${randomString}${extension}`;
};

const copyTableToClipboard = async (tableData) => {
  if (!tableData || tableData.length === 0) return;
  const buildTsv = () => {
    const headers = Object.keys(tableData[0]);
    return [
      headers.join('\t'),
      ...tableData.map(row =>
        headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined) return '';
          return String(value);
        }).join('\t')
      )
    ].join('\n');
  };

  try {
    await navigator.clipboard.writeText(buildTsv());
  } catch (error) {
    console.error('Failed to copy table data:', error);
    try {
      const textArea = document.createElement('textarea');
      textArea.value = buildTsv();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    } catch (fallbackError) {
      console.error('Failed to copy table data with fallback method:', fallbackError);
    }
  }
};

// ---------------------------------------------------------------------------
// MessageItem: a single chat bubble.
//
// Wrapped in React.memo so that typing in the composer doesn't re-render
// every message (which is what caused the input lag on long conversations
// with code blocks / tables). Also memoizes the ReactMarkdown `components`
// object so Prism highlighting isn't rebuilt on every parent render.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// HandoverSeedBanner: renders a "Continued from previous conversation" card
// in place of a normal chat bubble when a message carries
// ``additional_data.kind === "handover_seed"``. Seed pairs are (user, assistant)
// on the backend; we render the banner from the user seed and hide the
// assistant acknowledgement.
// ---------------------------------------------------------------------------

const HandoverSeedBanner = memo(function HandoverSeedBanner({ message, onOpenSource }) {
  const [expanded, setExpanded] = useState(false);
  const data = message.additional_data || {};
  const sourceId = data.source_conversation_id;
  const sourceName = data.source_conversation_name;
  const content = typeof message.content === 'string' ? message.content : '';

  return (
    <div className="handover-seed-banner">
      <div className="handover-seed-banner-header">
        <span>Continued from {sourceName ? `"${sourceName}"` : 'a previous conversation'}</span>
        {sourceId && onOpenSource && (
          <button
            type="button"
            className="handover-seed-banner-link"
            onClick={() => onOpenSource(sourceId)}
          >
            Open original →
          </button>
        )}
      </div>
      <div className="handover-seed-banner-body">
        KAI was seeded with a handover summary. You can keep chatting as if the original context was still present.
      </div>
      {content && (
        <>
          <button
            type="button"
            className="handover-seed-banner-toggle"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Hide summary' : 'Show summary'}
          </button>
          {expanded && <div className="handover-seed-expanded">{content}</div>}
        </>
      )}
    </div>
  );
});

// Whether a message's kai-options cards are read-only: on any message but the
// latest, and on the latest until its reply finishes (another card may still
// be on its way, and it gets merged into this one -- mergeKaiOptionsCards).
// Read through context, not closed over in markdownComponents: a change
// there rebuilds every renderer and remounts the message's code blocks and
// tables, once when a reply finishes and again when the next one starts.
const KaiOptionsLockedContext = React.createContext(true);

function LockableQuickOptionsCard(props) {
  const locked = React.useContext(KaiOptionsLockedContext);
  return <QuickOptionsCard {...props} disabled={locked} />;
}

const MessageItem = memo(function MessageItem({
  message,
  answerText,
  isLastMessage,
  lastMessageRef,
  sharedConversation,
  isDarkMode,
  copiedMessageId,
  onCopyMessage,
  onRetry,
  isRetrying,
  onOpenSource,
  websitePreview,
  setWebsitePreview,
  onQuickSubmit,
  onQuoteMessage,
  onPreviewPdf,
  mentionableNames,
  // A group chat's details (GroupChat.js): who wrote each question, and
  // whose question each answer is for (answeringName).
  groupChat,
  // A copy of a group chat you're no longer in: why you have it, and whose
  // its unattributed early questions were (GroupChat.js's leftGroupText).
  leftGroup = null,
  answeringName,
  onCancelQueued,
  onSendAgain,
  // The signed-in user's id: a conversation that used to be a group chat
  // still names whoever else asked its earlier questions.
  myUserId,
  // A group chat, for those who can change who's in it: who's in it now, and
  // adding back someone whose note says they've rejoined the project -- with
  // why it couldn't, if it couldn't.
  groupMemberIds = null,
  onAddBack,
  addBackError = null,
}) {
  const isHandoverSeed = message.additional_data && message.additional_data.kind === 'handover_seed';
  // A single persisted note (see agent_service.append_conversation_note /
  // PUT .../knowledge-scope) marking a mid-conversation scope change --
  // rendered as a plain divider, not a chat bubble, so a reload still shows
  // why later answers differ. See .claude/plan/plan_knowledge_scope.md.
  const isKnowledgeScopeNote = message.additional_data && message.additional_data.type === 'knowledge_scope_changed';
  // Same kind of divider, for held knowledge base files the user added to
  // this conversation ("Use in this chat") -- its text names them.
  // Also for "@" picks that couldn't be applied to the question they went
  // with (shown on this screen only, never stored).
  const isKnowledgeFilesNote = message.additional_data
    && ['knowledge_files_added', 'knowledge_picks_not_applied'].includes(message.additional_data.type);
  // And for a conversation added to a project from Share's "Add to a
  // project": where the project part of it starts.
  const isAddedToProjectNote = message.additional_data && message.additional_data.type === 'added_to_project';
  // And in a group chat, where the people in it changed: someone added,
  // removed or leaving, or the conversation becoming a group chat.
  const isPeopleNote = isMemberNote(message);
  // And where someone continued this group chat as a new one, from its
  // handover: a link there, though anyone can carry on here.
  const isContinuedNote = message.additional_data && message.additional_data.type === 'group_chat_continued';

  const markdownComponents = useMemo(() => {
  // Everything a FENCED code block renders as. Lives here rather than in the
  // `code` component below because only `pre` can tell a block from an inline
  // span -- see the `pre` entry.
  const renderCodeBlock = ({ className, children }) => {
    const match = /language-([\w-]+)/.exec(className || '');
    const language = match ? match[1] : 'text';
    const codeText = String(children).replace(/\n$/, '');

    // KAI preference asks: a ```kai-options JSON block renders as an
    // interactive quick-options card instead of raw code. While the block is
    // still streaming in the JSON is incomplete, so parsing fails -> show a
    // muted placeholder (never the raw JSON). Interactive only for the latest
    // message once its reply has finished (KaiOptionsLockedContext).
    if (language === 'kai-options') {
      try {
        const config = JSON.parse(codeText);
        return (
          <LockableQuickOptionsCard
            config={config}
            onSubmit={onQuickSubmit}
            answerText={answerText}
          />
        );
      } catch (e) {
        return <div className="kai-options-loading">Preparing options…</div>;
      }
    }

    if (!codeText.includes('\n')) {
      return (
        <div className="markdown-code-single-line">
          <code className={className}>{children}</code>
          <button
            className="copy-code-btn-inline"
            onClick={(e) => copyCodeToClipboard(codeText, e.currentTarget)}
            title="Copy code"
          >
            <FiCopy size={11} />
          </button>
        </div>
      );
    }

    return (
      <div className="markdown-code-wrapper">
        <div className="markdown-code-header">
          <span className="markdown-code-language">{language}</span>
          <button
            className="copy-code-btn"
            onClick={(e) => copyCodeToClipboard(codeText, e.currentTarget)}
            title="Copy code to clipboard"
          >
            <FiCopy size={12} />
          </button>
        </div>
        <SyntaxHighlighter
          language={language}
          style={isDarkMode ? oneDark : oneLight}
          customStyle={{
            margin: 0,
            borderRadius: 0,
            background: 'transparent',
            padding: '14px',
            fontSize: '13px',
            lineHeight: '1.6'
          }}
          codeTagProps={{
            style: {
              fontFamily: "'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', monospace"
            }
          }}
        >
          {codeText}
        </SyntaxHighlighter>
      </div>
    );
  };

  return {
    p: ({ node, children, ...props }) => {
      const hasMultiLineCodeBlock = node?.children?.some(child => {
        if (child.tagName !== 'code') return false;
        const hasLanguage = child.properties?.className?.some(c => c.startsWith('language-'));
        if (!hasLanguage) return false;
        const codeContent = child.children?.[0]?.value || '';
        return codeContent.includes('\n');
      });
      if (hasMultiLineCodeBlock) {
        return <div {...props}>{children}</div>;
      }
      return <p {...props}>{children}</p>;
    },
    a: ({ node, children, href, ...props }) => {
      // A query_pdf_index citation's "open page N" link (see
      // pdf_index_service._page_preview_link) tags itself with a
      // "#kai-pdf-preview&..." fragment carrying the page/filename it points
      // to -- a fragment because it rides free on the presigned URL without
      // touching (and invalidating) its signed query string. Shown as a
      // small button, not a plain inline link, specifically so a "proof"
      // citation stands out from the surrounding prose instead of reading as
      // just another word -- and clicking it opens a preview here rather
      // than a new tab, so confirming the proof exists doesn't cost the
      // user their place in the conversation.
      const hashIndex = href ? href.indexOf('#kai-pdf-preview') : -1;
      if (hashIndex !== -1 && onPreviewPdf) {
        const params = new URLSearchParams(href.slice(hashIndex + '#kai-pdf-preview'.length));
        const page = parseInt(params.get('page'), 10);
        return (
          <button
            type="button"
            className="pdf-preview-citation-link"
            onClick={() => onPreviewPdf({
              url: href.slice(0, hashIndex),
              page: Number.isFinite(page) ? page : 1,
              name: params.get('name') || 'document.pdf',
              fileId: params.get('file') || null,
              projectId: params.get('project') || null,
            })}
          >
            <FiFileText size={12} /> {children}
          </button>
        );
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    },
    img: ({ node, src, alt, ...props }) => {
      const filename = src?.split('/').pop()?.split('?')[0] || alt || 'image.png';
      return (
        <div className="markdown-image-wrapper">
          <img src={src} alt={alt} {...props} />
          <button
            className="download-image-btn"
            onClick={(e) => {
              e.preventDefault();
              downloadImage(src, filename);
            }}
            title="Download image"
          >
            <FiDownload size={14} />
          </button>
        </div>
      );
    },
    // Only a FENCED block is wrapped in <pre>, which is the one place that
    // distinction still exists. react-markdown dropped the `inline` prop in
    // v9 (this project is on v10), so the `code` component below had been
    // receiving `inline === undefined` on every single call -- sending every
    // `like this` span down the block path, wrapping it in a <div>, and
    // putting that div inside the paragraph React was warning about. Deciding
    // here instead puts the choice where the information actually is.
    //
    // The <pre> element itself is deliberately dropped: it may legally
    // contain only phrasing content, so the wrapper div the block renders has
    // never belonged inside it.
    pre: ({ children }) => {
      const codeElement = React.Children.toArray(children).find(React.isValidElement);
      return renderCodeBlock(codeElement?.props || {});
    },
    // Inline code, and nothing else. Plain <code>, so it can sit inside a
    // paragraph the way markdown intends.
    code: ({ node, className, children, ...props }) => (
      <code className={className} {...props}>{children}</code>
    ),
    table: ({ children, ...props }) => (
      <div className="markdown-table-wrapper">
        <div className="markdown-table-header">
          <span className="markdown-table-title">📊 Table</span>
          <button
            className="copy-markdown-table-btn"
            onClick={(e) => {
              const table = e.target.closest('.markdown-table-wrapper').querySelector('table');
              copyMarkdownTableToClipboard(table);
            }}
            title="Copy table data to clipboard"
          >
            <FiCopy size={12} />
          </button>
        </div>
        <table {...props}>{children}</table>
      </div>
    ),
    td: ({ node, children, ...props }) => {
      const content = children?.toString() || '';
      const imgTagMatch = content.match(/<img\s+[^>]*src=["']([^"']+)["'][^>]*>/i);
      if (imgTagMatch) {
        const imgTag = content.match(/<img\s+([^>]*)>/i)?.[1] || '';
        const srcMatch = imgTag.match(/src=["']([^"']+)["']/i);
        const altMatch = imgTag.match(/alt=["']([^"']+)["']/i);
        const widthMatch = imgTag.match(/width=["']?(\d+)["']?/i);
        const heightMatch = imgTag.match(/height=["']?(\d+)["']?/i);
        const src = srcMatch?.[1];
        const alt = altMatch?.[1] || '';
        const width = widthMatch?.[1];
        const height = heightMatch?.[1];
        if (src) {
          const imgProps = { src, style: {} };
          if (width) imgProps.style.width = `${width}px`;
          if (height) imgProps.style.height = `${height}px`;
          return <td {...props}><img alt={alt || ''} {...imgProps} /></td>;
        }
      }
      return <td {...props}>{children}</td>;
    },
    th: ({ node, children, ...props }) => {
      const content = children?.toString() || '';
      const imgTagMatch = content.match(/<img\s+[^>]*src=["']([^"']+)["'][^>]*>/i);
      if (imgTagMatch) {
        const imgTag = content.match(/<img\s+([^>]*)>/i)?.[1] || '';
        const srcMatch = imgTag.match(/src=["']([^"']+)["']/i);
        const altMatch = imgTag.match(/alt=["']([^"']+)["']/i);
        const widthMatch = imgTag.match(/width=["']?(\d+)["']?/i);
        const heightMatch = imgTag.match(/height=["']?(\d+)["']?/i);
        const src = srcMatch?.[1];
        const alt = altMatch?.[1] || '';
        const width = widthMatch?.[1];
        const height = heightMatch?.[1];
        if (src) {
          const imgProps = { src, style: {} };
          if (width) imgProps.style.width = `${width}px`;
          if (height) imgProps.style.height = `${height}px`;
          return <th {...props}><img alt={alt || ''} {...imgProps} /></th>;
        }
      }
      return <th {...props}>{children}</th>;
    }
  };
  }, [isDarkMode, onQuickSubmit, answerText, onPreviewPdf]);

  const remarkPlugins = useMemo(() => [remarkGfm], []);

  // Render handover seed messages as a dedicated banner instead of a normal
  // chat bubble. This must come AFTER all hook calls above to keep the
  // rules-of-hooks happy.
  if (isHandoverSeed) {
    if (message.role !== 'user') {
      return <div ref={isLastMessage ? lastMessageRef : null} className="handover-seed-ack" style={{ display: 'none' }} />;
    }
    return (
      <div ref={isLastMessage ? lastMessageRef : null} className="message system handover-seed-message">
        <HandoverSeedBanner message={message} onOpenSource={onOpenSource} />
      </div>
    );
  }

  if (isKnowledgeScopeNote) {
    return (
      <div ref={isLastMessage ? lastMessageRef : null} className="knowledge-scope-note">
        <span>
          {knowledgeScopeNoteText(message.additional_data, groupChat?.my_user_id || myUserId)
            || 'Knowledge base updated. Later answers reflect this.'}
        </span>
      </div>
    );
  }

  if (isKnowledgeFilesNote) {
    return (
      <div ref={isLastMessage ? lastMessageRef : null} className="knowledge-scope-note">
        <span>{typeof message.content === 'string' ? message.content : 'New files added to this conversation.'}</span>
      </div>
    );
  }

  if (isAddedToProjectNote) {
    const projectName = message.additional_data.project_name;
    return (
      <div ref={isLastMessage ? lastMessageRef : null} className="knowledge-scope-note">
        <span>
          {projectName ? `Added to the project "${projectName}". ` : 'Added to a project. '}
          From here on, answers start with the project&apos;s files and earlier conversations.
        </span>
      </div>
    );
  }

  if (isPeopleNote) {
    const data = message.additional_data;
    // Back in the project but not this chat: it's for its people to add them.
    const addBackId = data.type === 'group_member_rejoined_project' && onAddBack && groupMemberIds
      && data.user_id && !groupMemberIds.has(data.user_id) ? data.user_id : null;
    return (
      <div ref={isLastMessage ? lastMessageRef : null} className="knowledge-scope-note">
        <span>{memberNoteText(data, groupChat?.my_user_id || myUserId)}</span>
        {addBackId && (
          <button type="button" className="knowledge-scope-note-link" onClick={() => onAddBack(addBackId)}>
            Add back
          </button>
        )}
        {addBackId && addBackError?.userId === addBackId && (
          <span className="knowledge-scope-note-error" role="alert">{addBackError.message}</span>
        )}
      </div>
    );
  }

  if (isContinuedNote) {
    const data = message.additional_data;
    return (
      <div ref={isLastMessage ? lastMessageRef : null} className="knowledge-scope-note">
        <span>{continuedNoteText(data, groupChat?.my_user_id || myUserId)}</span>
        {data.conversation_id && onOpenSource && (
          <button
            type="button"
            className="knowledge-scope-note-link"
            onClick={() => onOpenSource(data.conversation_id)}
          >
            Open →
          </button>
        )}
      </div>
    );
  }

  // The same divider above a message whose turn ran in a different focus mode
  // from the turn before (saved on the message, see streaming.py).
  const focusModeSwitch = message.role === 'user' ? message.focusModeSwitch : null;
  // And one above the first question to run after the project's
  // instructions were edited (saved on the message, see streaming.py).
  const instructionsChangeText = message.role === 'user'
    ? INSTRUCTIONS_CHANGE_TEXT[message.instructions_change?.kind] : null;

  // In a group chat, KAI's reply to our own question that's still waiting
  // for its turn shows nothing yet -- the question says it's waiting.
  if (message.awaitingTurn) return null;
  // Someone else's question from when this conversation was a group chat
  // (everyone else has since left it), or in a copy of one you're no longer
  // in (leftGroup), keeps their name -- in a copy, one with no stored author
  // was its starter's, from before it was a group chat.
  const formerAuthor = !groupChat && !sharedConversation && message.role === 'user' && myUserId
    && (message.author?.user_id || leftGroup?.created_by)
    ? authorOf(message, {
      my_user_id: myUserId, created_by: leftGroup?.created_by, imported_from: leftGroup?.imported_from,
    }) : null;
  const formerGroupAuthor = formerAuthor && !formerAuthor.isMe ? formerAuthor : null;
  const groupAuthor = groupChat && message.role === 'user' ? authorOf(message, groupChat) : formerGroupAuthor;

  return (
    <>
    {focusModeSwitch && focusModeSwitch.to_name && (
      <div className="knowledge-scope-note">
        <span>
          {focusModeSwitch.from_name
            ? `Switched from ${focusModeSwitch.from_name} to ${focusModeSwitch.to_name}.`
            : `Switched to ${focusModeSwitch.to_name}.`}
        </span>
      </div>
    )}
    {instructionsChangeText && (
      <div className="knowledge-scope-note">
        <span>{instructionsChangeText}</span>
      </div>
    )}
    <div
      className={`message ${message.role}${message.groupQueue ? ' gc-waiting' : ''}`}
      ref={isLastMessage ? lastMessageRef : null}
    >
      <div className="message-header">
        {groupAuthor ? (
          <div className="message-role gc-author">
            <Avatar userId={groupAuthor.userId} name={groupAuthor.isMe ? (groupChat?.members || []).find((m) => m.user_id === groupAuthor.userId)?.display_name || 'You' : groupAuthor.name} size={28} />
            {groupAuthor.isMe ? 'You' : groupAuthor.name}
          </div>
        ) : groupChat ? (
          <div className="gc-role-wrap">
            <div className={`message-role ${message.role === 'assistant' ? 'kai-role' : ''}`}>
              {message.role === 'user' ? 'You' : 'KAI'}
            </div>
            {message.role === 'assistant' && answeringName && (
              <span className="gc-answering">
                answering {answeringName}
                {message.isStreaming && (
                  <span className="gc-dots gc-dots--muted" aria-hidden="true"><span /><span /><span /></span>
                )}
              </span>
            )}
          </div>
        ) : (
          <div className={`message-role ${message.role === 'assistant' ? 'kai-role' : ''}`}>
            {message.role === 'user'
              ? (sharedConversation ? (sharedConversation.ownerName || sharedConversation.ownerEmail || 'Shared by') : 'You')
              : 'KAI'}
          </div>
        )}
        <div className="message-meta">
          {message.model && (
            <span className="message-model">{formatModelName(message.model)}</span>
          )}
          <span className="message-time">
            {formatMessageTime(message.timestamp)}
          </span>
        </div>
      </div>
      <div className={`message-content ${message.isError ? 'error' : ''}`} data-message-id={message.id} data-role={message.role}>
        {message.replyContext && (
          <div className="message-reply-caption">
            <FiCornerUpLeft size={12} />
            <div className="reply-context-info">
              <div className="reply-context-label">
                {replyLabel(message.replyContext, Boolean(groupChat))}
              </div>
              <div className="reply-context-snippet">{message.replyContext.text}</div>
            </div>
          </div>
        )}
        {message.files && message.files.length > 0 && (
          <div className="message-files">
            <div className="uploaded-files-label">📎 Uploaded files:</div>
            {message.files.map((file, index) => (
              <div key={index} className="uploaded-file">
                <span className="file-icon">
                  {file.type === 'pdf' && '📄'}
                  {file.type === 'docx' && '📝'}
                  {file.type === 'xlsx' && '📊'}
                  {file.type === 'csv' && '📋'}
                  {file.type === 'txt' && '📃'}
                  {file.type === 'md' && '📑'}
                  {(file.type === 'eml' || file.type === 'msg') && '✉️'}
                  {!['pdf', 'docx', 'xlsx', 'csv', 'txt', 'md', 'eml', 'msg'].includes(file.type) && '📎'}
                </span>
                <span className="file-name">{file.name}</span>
                <span className="file-meta">
                  ({(file.type || 'file').toUpperCase()}{file.tokens != null ? `, ~${file.tokens} tokens` : ''})
                </span>
              </div>
            ))}
          </div>
        )}

        {message.images && message.images.length > 0 && (
          <div className="message-images">
            {message.images.map((image, index) => (
              <img
                key={index}
                src={image.url}
                alt={image.name || `Image ${index + 1}`}
                className="message-image"
              />
            ))}
          </div>
        )}

        {message.role === 'assistant' ? (
          <KaiOptionsLockedContext.Provider value={!isLastMessage || Boolean(message.isStreaming)}>
            <ReactMarkdown
              remarkPlugins={remarkPlugins}
              components={markdownComponents}
            >
              {filterImageUrls(mergeKaiOptionsCards(message.content))}
            </ReactMarkdown>
          </KaiOptionsLockedContext.Provider>
        ) : (
          buildMentionHighlightSegments(filterImageUrls(message.content), mentionableNames || []).map((seg, idx) => (
            seg.mention
              ? <span key={idx} className="message-sent-mention">{seg.text}</span>
              : <React.Fragment key={idx}>{seg.text}</React.Fragment>
          ))
        )}
        {message.isStreaming && message.toolUseText && (
          <div
            className="progress-line progress-stage-tool-use"
            role="status"
            aria-live="polite"
          >
            <span className="progress-text">{message.toolUseText}</span>
          </div>
        )}

        {message.isStreaming && message.progressText && (
          <div
            className={`progress-line progress-stage-${message.progressStage || 'unknown'}`}
            role="status"
            aria-live="polite"
          >
            <span className="progress-text">{message.progressText}</span>
          </div>
        )}

        {message.isStreaming && (
          <span className="streaming-cursor">▋</span>
        )}

        {message.role === 'user' && message.groupQueue && (
          <div className="gc-waiting-note">
            <span>
              Waiting for KAI
              {message.groupQueue.ahead ? ` · ${message.groupQueue.ahead} ahead` : ''}
            </span>
            {onCancelQueued && message.pendingId && (
              <button type="button" onClick={() => onCancelQueued(message)}>Cancel</button>
            )}
          </div>
        )}
        {message.role === 'user' && message.notSent && (
          <div className="gc-waiting-note">
            <span>
              {message.notSent === 'continued'
                ? 'Not sent: this chat continued in a new group chat. Ask there.'
                : <>Not sent: KAI didn&apos;t get to it.</>}
            </span>
            {onSendAgain && (
              <button type="button" onClick={() => onSendAgain(message)}>Send again</button>
            )}
          </div>
        )}

        {message.isError && message.isRecoverable && onRetry && !sharedConversation && (
          <div className="recoverable-error-actions">
            <button
              className="retry-btn"
              onClick={() => onRetry(message.id)}
              disabled={isRetrying}
              title="Clean up the corrupted turn and resend your last message"
            >
              {isRetrying ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}

        {message.additional_data && !message.isStreaming && (
          <div className="additional-data" data-type={message.additional_data.data_type}>
            <div className="additional-data-header">
              <span className="additional-data-type">{message.additional_data.data_type}</span>
            </div>
            <div className="additional-data-content">
              {message.additional_data.data_type === 'images' ? (
                <div className="image-grid">
                  {message.additional_data.content.map((item, index) => (
                    <div key={index} className="image-grid-item">
                      <a
                        href={item.oalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="image-grid-link"
                      >
                        <img
                          src={item.imagelink}
                          alt={item.projectname}
                          className="image-grid-thumbnail"
                        />
                        <div className="image-grid-info">
                          <div className="image-grid-title">{item.projectname}</div>
                          <div className="image-grid-access">{item.access_level}</div>
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              ) : message.additional_data.data_type === 'table' ? (
                <div className="table-result-container">
                  {message.additional_data.chain_of_thought && (
                    <div className="reasoning-panel">
                      <div className="panel-header">
                        <span className="panel-icon">🧠</span>
                        <span className="panel-title">Reasoning</span>
                      </div>
                      <div className="panel-content">
                        {message.additional_data.chain_of_thought}
                      </div>
                    </div>
                  )}

                  {message.additional_data.content && message.additional_data.content.length > 0 ? (
                    <div className="data-table-container">
                      <div className="table-header">
                        <div className="table-header-left">
                          <span className="table-icon">📊</span>
                          <span className="table-title">Results ({message.additional_data.content.length} rows)</span>
                        </div>
                        <button
                          className="copy-table-btn"
                          onClick={() => copyTableToClipboard(message.additional_data.content)}
                          title="Copy table data to clipboard"
                        >
                          <FiCopy size={14} />
                        </button>
                      </div>
                      <div className="table-wrapper">
                        <table className="data-table">
                          <thead>
                            <tr>
                              {Object.keys(message.additional_data.content[0] || {}).map((column, index) => (
                                <th key={index}>{column}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {message.additional_data.content.map((row, rowIndex) => (
                              <tr key={rowIndex}>
                                {Object.values(row).map((value, cellIndex) => {
                                  if (value === null || value === undefined) {
                                    return <td key={cellIndex}>—</td>;
                                  }
                                  const stringValue = String(value);
                                  const isUrl = stringValue.match(/^https?:\/\/.*$/i);
                                  const isDate = stringValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/);
                                  let displayValue = stringValue;
                                  if (isDate) {
                                    try {
                                      const date = new Date(stringValue);
                                      displayValue = date.toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      });
                                    } catch (e) {
                                      displayValue = stringValue;
                                    }
                                  }
                                  return (
                                    <td key={cellIndex}>
                                      {isUrl ? (
                                        <a
                                          href={stringValue.replace(/ /g, '%20')}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="table-link"
                                        >
                                          Link
                                        </a>
                                      ) : (
                                        displayValue
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="data-table-container">
                      <div className="no-results-message">
                        <span className="no-results-text">The query did not return any results</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : message.additional_data.data_type === 'website_preview' ? (
                <div className="website-preview-inline-indicator">
                  <span className="website-preview-indicator-icon">&#9654;</span>
                  <span className="website-preview-indicator-text">
                    {websitePreview ? 'Website updated — preview is shown in the side panel' : 'Website preview available'}
                  </span>
                  {!websitePreview && setWebsitePreview && (
                    <button
                      className="website-preview-indicator-btn website-preview-indicator-btn-primary"
                      onClick={() => setWebsitePreview({
                        project_id: message.additional_data.project_id,
                        url: message.additional_data.url,
                        files: message.additional_data.files,
                        refreshKey: Date.now(),
                      })}
                      title="Show preview panel"
                    >
                      Show preview
                    </button>
                  )}
                  <button
                    className="website-preview-indicator-btn"
                    onClick={() => window.open(
                      `${getApiBaseUrl()}/websites/${message.additional_data.project_id}/index.html`, '_blank'
                    )}
                    title="Open in new tab"
                  >
                    Open in new tab
                  </button>
                  <button
                    className="website-preview-indicator-btn"
                    onClick={() => navigator.clipboard.writeText(
                      `${getApiBaseUrl()}/websites/${message.additional_data.project_id}/index.html`
                    )}
                    title="Copy link"
                  >
                    Copy link
                  </button>
                </div>
              ) : (
                message.additional_data.content && message.additional_data.content.map((item, index) => (
                  <div key={index}>
                    <pre>{JSON.stringify(item, null, 2)}</pre>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      {message.role === 'assistant' && !message.isStreaming && (
        <div className="message-footer">
          <button
            className={`copy-message-btn ${copiedMessageId === message.id ? 'copied' : ''}`}
            onClick={() => onCopyMessage(message.id)}
            title="Copy message"
          >
            {copiedMessageId === message.id ? (
              <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2" fill="none"><polyline points="20 6 9 17 4 12"></polyline></svg>
            ) : (
              <FiCopy size={14} />
            )}
          </button>
          {onQuoteMessage && (
            <button
              className="quote-message-btn"
              onClick={() => onQuoteMessage(message)}
              title="Quote this message"
            >
              <FiCornerUpLeft size={14} />
            </button>
          )}
          {message.tokens && (
            <span className="token-info">
              {message.tokens.input}→{message.tokens.output} ({message.tokens.total} tokens)
            </span>
          )}
        </div>
      )}
      {message.role !== 'assistant' && (onQuoteMessage || message.tokens) && (
        <div className="message-footer message-footer-user">
          {onQuoteMessage && !message.isStreaming && (
            <button
              className="quote-message-btn"
              onClick={() => onQuoteMessage(message)}
              title="Quote this message"
            >
              <FiCornerUpLeft size={14} />
            </button>
          )}
          {message.tokens && (
            <span className="token-info">
              {message.tokens.input}→{message.tokens.output} ({message.tokens.total} tokens)
            </span>
          )}
        </div>
      )}
    </div>
    </>
  );
});

const ChatInterface = ({
  messages,
  setMessages,
  selectedModel,
  setSelectedModel,
  selectedFocusMode,
  tokenCount,
  setTokenCount,
  rightSidebarOpen,
  toggleRightSidebar,
  activeConversation,
  setActiveConversation,
  onMessageSent,
  sharedConversation,
  onImportShared,
  isImportingShared = false,
  initialContextUsage,
  websitePreview,
  setWebsitePreview,
  onConversationContinued,
  onOpenConversation,
  // Opens a group chat in place of the read-only copy of it on screen, once
  // the user has been added back to it.
  onBackInGroupChat,
  pendingProjectForNextConversation,
  pendingKnowledgeScope,
  onPendingKnowledgeScopeChange,
  onProjectConversationFiled,
  // Share's "Add to a project" moved this conversation into the project
  // passed: App.js opens the project's shell around it.
  onConversationAddedToProject,
  onOpenProjectView,
  // Opens a project's Manage members dialog (App.js's), from Share's
  // "no members yet" note.
  onManageProjectMembers,
  pendingProjectFirstMessage,
  // True when this chat is rendered inside ProjectView's shell, which
  // already shows the project's breadcrumb and name directly above. Its own
  // title would only repeat that, so it's suppressed -- the model/message-
  // count subtitle still renders underneath, and the context meter/handover/
  // share controls move up into the breadcrumb row instead (see
  // headerActionsPortalTarget).
  hideHeaderTitle = false,
  // A DOM node (ProjectView's own breadcrumb-row slot, via App.js) to portal
  // the context meter/Handover/Share controls into instead of rendering them
  // in this component's own header -- see the .chat-header-right render
  // below. Null/undefined outside the project shell, where they render
  // inline exactly as before. Set once ProjectView's slot div has actually
  // mounted (a ref callback, not available on the very first render), so
  // this can start as null even when hideHeaderTitle is already true.
  headerActionsPortalTarget = null,
  // Same, for the model/message-count subtitle: ProjectView's slot directly
  // under its title. Once both targets are set, this component's own header
  // row has nothing left to show and isn't rendered at all.
  headerSubtitlePortalTarget = null,
  // Opens the full Custom rules panel (the same one under the user menu),
  // for the "+" beside "Attach rules to this conversation" -- creating a
  // new rule happens there, not in this attach-only checklist.
  onOpenCustomRules,
  // The caller has just left the group chat on screen: App.js lets it go.
  onGroupChatLeft,
  // Everyone else left the group chat on screen, so it's the caller's own
  // conversation again (reopened in place): (id) => void.
  onGroupChatEnded,
  // Share's "Make group chat" just made the open conversation one: (id) => void.
  onMadeGroupChat,
  // Opening a group chat made from a copy of the user's own conversation,
  // they chose to have just the group chat: theirs was deleted, (id) => void.
  onOriginalCopyRemoved,
}) => {
  const { isDarkMode } = useDarkMode();
  const { user: authUser } = useAuth();
  // Seeded from whatever the user typed into ProjectView.js's (or the
  // project sidebar's) composer before App.js remounted this component fresh
  // via chatSessionKey -- actually sent by the auto-send effect below, not
  // just pre-filled.
  const [inputMessage, setInputMessage] = useState(() => pendingProjectFirstMessage || '');

  // Read by handleSendMessage to decide whether this turn needs a Graph token
  // (see turnMayConnectSharePoint). A ref rather than the `messages` prop
  // itself: that handler is a useCallback with a long dependency list, and
  // adding a prop that changes on every single message would rebuild it --
  // and everything memoized on its identity -- on each keystroke of a
  // streaming reply.
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Microsoft's refresh token for a browser app lasts 24 hours, so a day after
  // signing in the silent Graph token fails on every SharePoint turn. Two ways
  // back, neither of them signing out of KAI:
  //   - sendMessage opens Microsoft's window itself while the Send click is
  //     still a user gesture (it usually closes again on its own), and
  //   - if that window was blocked or closed and the connect was refused, the
  //     backend ends the turn with `graph_sign_in_required` and this holds
  //     what to resend once "Confirm sign-in" gets a token.
  // { phase: 'needed' | 'busy', resend, error } or null.
  const [graphSignIn, setGraphSignIn] = useState(null);
  // Set once Microsoft's window was blocked or closed, so later turns in this
  // session don't keep opening it unasked -- the button takes over from there.
  const graphPopupDeclinedRef = useRef(false);
  // Bumped whenever a pending "Confirm sign-in" must be abandoned (another
  // conversation, or unmount). An attempt still waiting on Microsoft's window
  // compares against it before it touches state or resends anything.
  const graphSignInAttemptRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // A group chat: one conversation every member of its project can read and
  // ask KAI in (see GroupChat.js). Null for every other conversation.
  const groupChat = useStableGroup(sharedConversation ? null : activeConversation);
  // A read-only copy of a group chat the user is no longer in: readable, but
  // with a bar saying why in place of the composer. Null otherwise.
  const leftGroup = (!sharedConversation && activeConversation?.left_group) || null;
  // Added back to that chat, the copy goes -- the chat has all of it -- so
  // the chat opens in its place. Until then the chat itself is a 404: a
  // quiet look every so often, while the tab is in view.
  const leftGroupChatId = leftGroup && leftGroup.reason !== 'project_deleted' ? leftGroup.chat_id : null;
  const onBackInGroupChatRef = useRef(onBackInGroupChat);
  onBackInGroupChatRef.current = onBackInGroupChat;
  useEffect(() => {
    if (!leftGroupChatId) return undefined;
    let stopped = false;
    const timer = setInterval(async () => {
      if (stopped || document.hidden) return;
      try {
        await pollGroupChatActivity(leftGroupChatId);
      } catch {
        return; // still not back in it, or offline: look again later
      }
      if (stopped) return;
      stopped = true;
      clearInterval(timer);
      onBackInGroupChatRef.current?.(leftGroupChatId);
    }, BACK_IN_GROUP_CHAT_CHECK_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [leftGroupChatId]);
  // Which conversation is on screen now, for a reload that finishes after
  // the user has moved on.
  const activeConversationRef = useRef(activeConversation);
  activeConversationRef.current = activeConversation;
  // A group chat made from a copy of the user's own conversation asks, as
  // they open it, whether they still want theirs -- once a visit, so "Not
  // now" asks again next time (askedOriginalCopies).
  const [originalOffer, setOriginalOffer] = useState(null);
  const askOriginalId = groupChat?.ask_original ? activeConversation?.conversation_id : null;
  useEffect(() => {
    if (!askOriginalId || askedOriginalCopies.has(askOriginalId)) return;
    askedOriginalCopies.add(askOriginalId);
    setOriginalOffer({ conversation_id: askOriginalId, name: activeConversationRef.current?.name });
  }, [askOriginalId]);
  // Set by the poll just before other people's messages arrive while the
  // reader is scrolled up, so the scroll-to-new-message effect below leaves
  // them where they are (the "N new messages" pill shows instead).
  const holdScrollRef = useRef(false);
  const isNearBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 160;
  }, []);
  // After a repair (or anything else that rewrote earlier messages), the
  // poll asks for the whole transcript again rather than appending to it.
  const reloadGroupChat = useCallback(async () => {
    const id = activeConversation?.conversation_id;
    if (!id) return;
    try {
      const data = await loadConversation(id);
      if (activeConversationRef.current?.conversation_id !== id) return;
      setMessages(data.messages || []);
      setActiveConversation?.((prev) => (prev && prev.conversation_id === id ? { ...prev, ...data.conversation } : prev));
    } catch (e) {
      // The next poll tries again.
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?.conversation_id, setMessages, setActiveConversation]);
  // The poll found the chat gone. If everyone else left it, it's now the
  // caller's own conversation: reopen it in place as one. False when it's
  // really gone (deleted, or access lost).
  const reopenAsOwnConversation = useCallback(async () => {
    const id = activeConversation?.conversation_id;
    if (!id) return false;
    try {
      const data = await loadConversation(id);
      if (!data.conversation || data.conversation.is_group) return false;
      if (activeConversationRef.current?.conversation_id === id) {
        setMessages(data.messages || []);
        setActiveConversation?.((prev) => (prev && prev.conversation_id === id ? { ...prev, ...data.conversation } : prev));
      }
      onGroupChatEnded?.(id);
      return true;
    } catch (e) {
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?.conversation_id, setMessages, setActiveConversation, onGroupChatEnded]);
  // Someone renamed the open group chat (or its first question named it).
  const renameGroupChatInPlace = useCallback((id, name) => {
    setActiveConversation?.((prev) => (
      prev && prev.conversation_id === id && prev.name !== name ? { ...prev, name } : prev
    ));
  }, [setActiveConversation]);
  // Kept on the conversation, not the group, so the poll's state isn't reset.
  const setGroupFocusModeInPlace = useCallback((id, mode) => {
    setActiveConversation?.((prev) => (
      prev && prev.conversation_id === id && groupChatFocusMode(prev)?.id !== mode.id
        ? { ...prev, group_focus_mode: mode } : prev
    ));
  }, [setActiveConversation]);
  // Someone else changed the chat's knowledge base: sent on as the event a
  // change made here sends, so the Knowledge base tab's selection and an "@"
  // pick start from the chat's files as they are now.
  const setGroupKnowledgeScopeInPlace = useCallback((id, scope) => {
    const current = activeConversationRef.current;
    if (!current || current.conversation_id !== id) return;
    if (knowledgeScopeKey(current.knowledge_scope) === knowledgeScopeKey(scope)) return;
    window.dispatchEvent(new CustomEvent(KNOWLEDGE_SCOPE_UPDATED_EVENT, {
      detail: { conversation_id: id, knowledge_scope: scope },
    }));
  }, []);
  const groupActivity = useGroupChatActivity({
    conversation: activeConversation,
    group: groupChat,
    messagesRef,
    setMessages,
    onReload: reloadGroupChat,
    isNearBottom,
    holdScrollRef,
    onRenamed: renameGroupChatInPlace,
    onGone: reopenAsOwnConversation,
    onFocusMode: setGroupFocusModeInPlace,
    onKnowledgeScope: setGroupKnowledgeScopeInPlace,
  });
  const { pollNow: pollGroupChatNow } = groupActivity;
  // Someone continued this group chat as a new one: closed for everyone --
  // readable, but nobody asks KAI here again (a bar links to the new one).
  const groupContinuedIn = groupChat ? groupActivity.continuedIn : null;
  // Someone else's answer growing below the transcript keeps in view, as our
  // own streaming one does -- if we were at the bottom when it grew.
  const liveAnswer = groupActivity.live;
  useEffect(() => {
    if (!liveAnswer?.stick) return;
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [liveAnswer]);
  // Someone else's question just showed up below the transcript while we
  // were at the bottom: bring it into view (after it's laid out).
  const groupReveal = groupActivity.reveal;
  useEffect(() => {
    if (!groupReveal) return undefined;
    const frame = requestAnimationFrame(() => {
      const el = messagesContainerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(frame);
  }, [groupReveal]);
  // Everyone else in the chat sees "… is typing" while we type.
  useTypingSignal({
    conversationId: groupChat && !groupActivity.gone && !groupContinuedIn ? activeConversation?.conversation_id : null,
    projectId: activeConversation?.project_id,
    text: inputMessage,
  });
  // "Who's in this chat", opened from the header's faces.
  const [showGroupPeople, setShowGroupPeople] = useState(false);
  const [leavingGroup, setLeavingGroup] = useState(false);
  // Someone back in the project after leaving it isn't back in its chats
  // until someone in one adds them: the note saying they've rejoined has Add
  // back for whoever can add people (its manager, or Edit access).
  const groupMembers = groupActivity.people?.members || groupChat?.members;
  const canAddBack = groupActivity.people
    ? Boolean(groupActivity.people.canAddPeople)
    : Boolean(groupChat?.can_add_people);
  // Whoever adds and removes people in it -- told so, should they leave it.
  const managesGroupPeople = groupActivity.people
    ? Boolean(groupActivity.people.canManagePeople)
    : Boolean(groupChat?.can_manage_people);
  const [addBackError, setAddBackError] = useState(null);
  const groupMemberIds = useMemo(
    () => (canAddBack ? new Set((groupMembers || []).map((m) => m.user_id)) : null),
    [canAddBack, groupMembers],
  );
  const groupChatId = groupChat ? activeConversation?.conversation_id : null;
  const handleAddBack = useCallback(async (userId) => {
    if (!groupChatId) return;
    setAddBackError(null);
    try {
      const result = await addBackToGroupChat(groupChatId, userId);
      setActiveConversation?.((prev) => (
        prev && prev.conversation_id === groupChatId ? { ...prev, group: result.group } : prev
      ));
      pollGroupChatNow?.();
    } catch (err) {
      setAddBackError({ userId, message: err.message });
    }
  }, [groupChatId, setActiveConversation, pollGroupChatNow]);
  // Our own questions this screen already shows -- the queue below lists
  // everyone else's (and any of ours it doesn't).
  const localPendingIds = useMemo(
    () => new Set(messages.filter((m) => m.role === 'user' && m.pendingId).map((m) => m.pendingId)),
    [messages],
  );
  // A new group chat's first question, still waiting or being answered, sits
  // in the queue below an empty transcript -- show it in the chat's normal
  // layout, not under the "Start a conversation" screen.
  const showEmptyChat = messages.length === 0 && !(
    groupChat && (groupActivity.pending || []).some((p) => !localPendingIds.has(p.pending_id))
  );

  const inputRef = useRef(null);
  // Only mounted when a custom rule is attached (see the render below) --
  // a contentEditable box so the attached-rule pill(s) can sit inline with
  // the prompt text itself (matching Claude Code's own chat input) while
  // wrapped lines still use the full box width, which a plain <textarea>
  // cannot do (a textarea is one fixed-width rectangle; every line, wrapped
  // or not, gets the same width). When no rule is attached, the plain
  // <textarea> below is used instead and none of this is touched at all.
  const richInputRef = useRef(null);
  // A character offset (into the rich input's extracted text) to restore
  // the caret to the next time the "pull inputMessage into DOM" effect
  // below rebuilds this box's text nodes -- set by selectMentionCandidate
  // and the atomic-Backspace handler right before their setInputMessage
  // call, since (unlike the plain <textarea>) a wholesale contentEditable
  // rebuild doesn't preserve caret position on its own. See
  // setRichInputCaretTextOffset.
  const pendingRichCaretOffsetRef = useRef(null);
  const fileInputRef = useRef(null);
  const rulesPopoverRef = useRef(null);
  // The "+" menu, its Custom rules item, and that item's flyout -- the menu
  // and flyout are portaled to <body>, see positionPlusMenu.
  const plusMenuRef = useRef(null);
  const rulesMenuItemRef = useRef(null);
  const rulesFlyoutRef = useRef(null);
  // Model picker button + its portaled dropdown (same self-flipping
  // positioning approach as the "+" menu above).
  const modelButtonRef = useRef(null);
  const modelMenuRef = useRef(null);
  // The "@" mention popover's own portaled dropdown -- positioned off the
  // composer itself (inputRef/richInputRef), same self-flipping approach as
  // the "+" menu above. See mentionQuery in the component body.
  const mentionMenuRef = useRef(null);
  // The plain textarea's highlight backdrop (see buildMentionHighlightSegments
  // and .message-input-highlight) -- its scrollTop is kept in sync with the
  // real (invisible-text) textarea's own on every scroll/edit, since it's a
  // separate DOM node the browser won't scroll for us.
  const messageHighlightRef = useRef(null);
  const [, setTotalTokens] = useState({ input: 0, output: 0, total: 0 });
  const [contextUsage, setContextUsage] = useState({ used: 0, limit: 0, percentage: 0 });
  const [, setContextWarningDismissed] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]); // Files currently being processed
  // Uploads that will become attachments of the next message -- these hold
  // the send button. A file going into the project's knowledge base
  // instead (toKnowledgeBase) never becomes one, so it doesn't.
  const attachmentUploads = uploadingFiles.filter((f) => !f.toKnowledgeBase);
  // A document attached inside a project conversation would otherwise commit
  // straight to that project's shared knowledge base (see handleDocumentUpload's
  // uploadProjectId) with no say from the user. This holds the pending choice
  // ({ fileName, resolve }) while that one modal is up; `resolve` is the
  // Promise executor captured by askAddToKnowledgeBase below, so the upload
  // itself simply awaits the user's answer before deciding whether to send
  // project_id at all -- nothing is indexed into the library until they do.
  // `options.reason` picks the prompt: 'choose' (knowledge base or this
  // chat), 'too-large' (over the chat attachment limit: nothing is sent, it
  // says to upload it in the Knowledge base tab; with size/limit) or
  // 'duplicate' (already in the knowledge base
  // under that name, so nothing is sent; with duplicate/canEdit). Resolves
  // 'add', 'chat' or 'cancel'.
  const [kbChoicePrompt, setKbChoicePrompt] = useState(null);
  const askAddToKnowledgeBase = useCallback(
    (fileName, options) => new Promise((resolve) => (
      setKbChoicePrompt({ fileName, ...options, resolve })
    )),
    []
  );
  const answerKbChoicePrompt = (choice) => {
    kbChoicePrompt?.resolve(choice);
    setKbChoicePrompt(null);
  };
  // Upload messages (too large, too many, failed, ...) shown one at a time
  // in the same styled pop-up as the knowledge-base prompt above, instead of
  // the browser's own alert box. A queue, since one drop can raise several.
  const [uploadNotices, setUploadNotices] = useState([]);
  const showUploadNotice = (title, body) => {
    setUploadNotices((prev) => [...prev, { id: `${Date.now()}-${Math.random()}`, title, body }]);
  };
  const dismissUploadNotice = () => setUploadNotices((prev) => prev.slice(1));
  // Mirrors selectedFiles for commitUploadedFile, which runs after an
  // upload's await, so its own render's copy may be out of date.
  const selectedFilesRef = useRef(selectedFiles);
  selectedFilesRef.current = selectedFiles;
  // Holds the target model id while the Opus 5.5 cost-confirmation modal
  // (see OPUS_5_5_MODEL_ID) is up; null the rest of the time, including for
  // every other model switch, which stays instant with no modal at all.
  const [pendingModelSwitch, setPendingModelSwitch] = useState(null);
  // A query_pdf_index citation's "open page N" link (see
  // pdf_index_service._page_preview_link) carries its target page/filename
  // in the URL FRAGMENT rather than a query param, since a query param would
  // be part of the S3 presigned URL's signed canonical request and adding
  // one after the fact would invalidate the signature -- a fragment is never
  // sent to the server at all, so it's free to repurpose. { url, page, name }
  // | null; set by the markdown `a` renderer below instead of letting the
  // link navigate, so the user can confirm a proof exists without losing
  // their place in the conversation.
  const [pdfPreview, setPdfPreview] = useState(null);
  // The previewed page drawn as an image (getPdfPageDrawing): null while
  // loading, {url} when ready, or {failed: true} -- then the PDF itself is
  // shown. Opening the PDF downloads all of it, which for a large drawing set
  // takes minutes; the drawn page takes a second or two.
  const [pdfPageDrawing, setPdfPageDrawing] = useState(null);
  useEffect(() => {
    setPdfPageDrawing(null);
    if (!pdfPreview?.fileId) return undefined;
    let cancelled = false;
    getPdfPageDrawing(pdfPreview.fileId, pdfPreview.page, pdfPreview.projectId)
      .catch(() => null)
      .then((drawn) => {
        if (!cancelled) setPdfPageDrawing(drawn?.url ? { url: drawn.url } : { failed: true });
      });
    return () => { cancelled = true; };
  }, [pdfPreview]);
  // Select-to-reply (see .claude/plan/chatreply.md). replyContext is the
  // committed quote that will ride along on the next send; selectionPopup is
  // the transient floating "Reply" button shown while the user still has an
  // in-progress text selection, before they've clicked it.
  const [replyContext, setReplyContext] = useState(null); // { text, messageId, role, isFullMessage } | null
  const [selectionPopup, setSelectionPopup] = useState(null); // { text, top, left, messageId, role } | null
  // Opaque token scoping the backend's .docx template-profile cache for the
  // active conversation (see services/docx_template_service.py's
  // cache_template_profile/get_cached_template_profiles). The actual profile
  // (fonts/sizes/margins/heading structure -- never document content) is
  // extracted and held SERVER-SIDE, in memory, keyed by this token; the
  // browser never holds or re-sends it. Only this short string travels with
  // every upload and chat request in this conversation, which is what keeps
  // a .docx with a header logo from becoming a multi-hundred-KB payload on
  // every single turn for the rest of the conversation.
  //
  // Deliberately NOT `sessionId` below, which lives in localStorage and
  // survives a refresh -- this token must not, since a lost token is what
  // makes the server-side profile unreachable again (see the regenerate
  // effect below), and "refresh = must re-attach the document" is a
  // deliberate property of this feature, not an accident to preserve.
  // A plain ref, not state -- nothing about it should ever trigger a re-render.
  const docxTemplateTokenRef = useRef(null);
  if (docxTemplateTokenRef.current === null) {
    docxTemplateTokenRef.current = mintOpaqueId('dtt');
  }
  const videoUploadControllers = useRef(new Map()); // uploadId -> AbortController, so unmounting stops in-flight polling
  const [isDragging, setIsDragging] = useState(false); // Whether a file is being dragged over the chat
  const dragCounter = useRef(0); // Guards against dragenter/dragleave flicker across child elements
  const [focusModes, setFocusModes] = useState([]);
  const [focusModesLoaded, setFocusModesLoaded] = useState(false);
  // Project name lookup for the header badge below -- fetched lazily (not on
  // mount like focusModes) since most conversations aren't in a project at
  // all, and cached by id so switching back to a conversation already seen
  // this session doesn't refetch.
  const [projectsById, setProjectsById] = useState({});
  // Custom rules (see .claude/plan/plan_custom_rules.md): customRules is the
  // user's full saved library (fetched once, like focusModes above);
  // attachedRuleIds is which of those are attached to the CURRENT
  // conversation for the next turn. Chips are rendered from the intersection
  // of the two (see attachedRules below) so a rule deleted from the library
  // disappears from the chip row instead of rendering blank.
  const [customRules, setCustomRules] = useState([]);
  const [customRulesLoadFailed, setCustomRulesLoadFailed] = useState(false);
  const [attachedRuleIds, setAttachedRuleIds] = useState([]);
  // The composer's "+" menu (Attach files / Custom rules). rulesPopoverOpen
  // is now its Custom rules flyout, only ever open while this is.
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [rulesPopoverOpen, setRulesPopoverOpen] = useState(false);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  // "@" mention popover -- lets the user narrow (or, via "@all", reset) this
  // conversation's knowledge-base scope right from the composer, instead of
  // only through ProjectPanel's own Files-tab checkboxes. null = closed;
  // {start, text} while open, where `start` is the '@' character's index
  // within inputMessage and `text` is whatever was typed after it (the
  // filter query). See addFileToKnowledgeScope/isFileMentioned above and
  // .claude/plan/plan_knowledge_scope.md.
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  // One project's file list, for the "@" popover's own candidate list.
  // Fetched once per project id, eagerly as soon as a project is active --
  // same cache-once-per-id shape (and timing) as projectsById above. NOT
  // gated on mentionQuery actually being open: an earlier version fetched
  // lazily on first "@", but a later keystroke re-running that effect could
  // cancel the one in-flight request that would have cleared its own
  // "loading" flag, leaving the popover stuck on "Loading files…"
  // permanently. Fetching once per project id up front (cheap -- same class
  // of cost as the project-name fetch above) sidesteps that race entirely.
  const [projectFilesById, setProjectFilesById] = useState({});
  // project_ids whose fetch above failed -- so the popover can say so
  // instead of showing "Loading files…" forever for a project with zero
  // files cached (indistinguishable from "still loading" otherwise).
  const [projectFilesFailedIds, setProjectFilesFailedIds] = useState(() => new Set());
  // Set when attaching a rule was blocked because it conflicts with one
  // already attached (see toggleAttachedRule below); shown inline in the
  // popover until dismissed or the popover is reopened/closed.
  const [rulesAttachError, setRulesAttachError] = useState(null);
  const [checkingAttachConflict, setCheckingAttachConflict] = useState(false);
  const prevMessageCountRef = useRef(0);
  const newMessageStartRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showShareModal, setShowShareModal] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(null); // null = use CSS flex default
  const [isResizingPreview, setIsResizingPreview] = useState(false);
  const wrapperRef = useRef(null);
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [retryingMessageId, setRetryingMessageId] = useState(null);
  const {
    isListening,
    hasTranscript,
    showSpeechInput,
    speechError,
    startListening,
    stopListening,
    clearSpeechError,
  } = useSpeechInput();

  // Stop polling any in-flight video transcription jobs if this component
  // unmounts (e.g. the user navigates away mid-upload) instead of leaving
  // an orphaned poll loop running.
  useEffect(() => {
    const controllers = videoUploadControllers.current;
    return () => {
      controllers.forEach(controller => controller.abort());
      controllers.clear();
    };
  }, []);

  // Models offered by the model-picker button beside the composer.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apiBase = getApiBaseUrl();
        const response = await fetch(`${apiBase}/models`);
        const data = await response.json();
        const models = Array.isArray(data) ? data : (data.models || []);
        if (!cancelled) setAvailableModels(models);
      } catch (error) {
        console.error('Error fetching models:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Generate or get session ID
  const [sessionId] = useState(() => {
    let id = localStorage.getItem('chat_session_id');
    if (!id) {
      id = mintOpaqueId('session');
      localStorage.setItem('chat_session_id', id);
    }
    return id;
  });

  // Mint a fresh .docx template-profile token on a genuine conversation switch --
  // a profile captured in conversation A must never be reachable from conversation
  // B just because both were viewed in this tab. Regenerating the token orphans
  // whatever conversation A cached it under server-side, which is the isolation;
  // nothing needs to be deleted from that cache directly. Deliberately does NOT
  // regenerate on the undefined -> real-id transition: that happens once per fresh
  // chat the moment the backend auto-creates a conversation on your first message,
  // and the file you just attached (whose profile is already cached under the
  // CURRENT token) belongs to THIS conversation, not a different one --
  // regenerating there was a bug that made the profile unreachable right after the
  // very first send, not a deliberate isolation.
  const prevConversationIdForTemplatesRef = useRef(activeConversation?.conversation_id);
  useEffect(() => {
    const prevId = prevConversationIdForTemplatesRef.current;
    const nextId = activeConversation?.conversation_id;
    const isFreshAutoCreate = !prevId && !!nextId;
    if (prevId !== nextId && !isFreshAutoCreate) {
      docxTemplateTokenRef.current = mintOpaqueId('dtt');
    }
    prevConversationIdForTemplatesRef.current = nextId;
  }, [activeConversation?.conversation_id]);

  // A pending "Confirm sign-in" resends a turn of the conversation it came
  // from, so it must not follow the user into another one -- including one
  // already waiting on Microsoft's window when the user switched.
  //
  // Clearing the notice here isn't enough on its own: a turn of the old
  // conversation can still be streaming, and its `graph_sign_in_required`
  // may arrive after this ran. So the notice also records which conversation
  // it belongs to, and both the stream handler and "Confirm sign-in" check
  // it against this ref, the conversation on screen now.
  const prevConversationIdForGraphSignInRef = useRef(activeConversation?.conversation_id);
  const activeConversationIdRef = useRef(activeConversation?.conversation_id || null);
  useEffect(() => {
    const prevId = prevConversationIdForGraphSignInRef.current;
    const nextId = activeConversation?.conversation_id;
    activeConversationIdRef.current = nextId || null;
    if (prevId && prevId !== nextId) {
      setGraphSignIn(null);
    }
    prevConversationIdForGraphSignInRef.current = nextId;
    const attempts = graphSignInAttemptRef;
    return () => { attempts.current += 1; };
  }, [activeConversation?.conversation_id]);

  // Set to the new conversation's id by the `conversation_created` stream
  // handler, and consumed once by the hydration effect below.
  //
  // The one case that must NOT hydrate is the backend auto-creating a
  // conversation on the first message of a brand-new chat: the selection the
  // user made before sending is the truth, and the freshly-built
  // activeConversation object carries no active_rule_ids yet, so hydrating
  // there would wipe that selection.
  //
  // That case used to be *inferred* from "prevId was empty and nextId isn't",
  // which was wrong: <ChatInterface> is rendered without a key (App.js), so
  // it never remounts on a conversation switch, and the very first
  // conversation opened after a page load produces that exact same
  // undefined -> id transition. Its saved rules therefore never hydrated: no
  // chips, active_rule_ids: [] on the next message, and -- worse -- the next
  // toggle computed its set from [] and PATCHed that, wiping the row. Since
  // this component builds the auto-created conversation object itself, flag
  // the case explicitly instead of guessing at it.
  const justAutoCreatedConversationIdRef = useRef(null);

  // Mirrors the prop into a ref so the conversation_created handler below --
  // defined inside a stream callback that can outlive the render that created
  // it -- always reads the CURRENT value rather than one captured at stream
  // start. Cleared after firing once, so only the very next auto-created
  // conversation after "New chat in project" is filed, not every conversation
  // for the rest of the session.
  const pendingProjectForNextConversationRef = useRef(null);
  useEffect(() => {
    pendingProjectForNextConversationRef.current = pendingProjectForNextConversation || null;
  }, [pendingProjectForNextConversation]);

  // The Knowledge base subset picked in ProjectPanel.js's own Files tab
  // (checkboxes, mirroring the Rules tab) for the conversation about to be
  // created -- App.js owns this (see pendingKnowledgeScope there) since it's
  // shared between ProjectPanel (which writes it, as a sibling of this
  // component under ProjectView) and sendMessage here (which reads it at the
  // very first turn). Same ref-mirroring reasoning as
  // pendingProjectForNextConversationRef. See .claude/plan/plan_knowledge_scope.md.
  const pendingKnowledgeScopeRef = useRef(null);
  useEffect(() => {
    pendingKnowledgeScopeRef.current = pendingKnowledgeScope || null;
  }, [pendingKnowledgeScope]);

  // "@" picks made in an existing conversation since its last question,
  // not yet sent: { conversationId, picks: [{ op, id }] } or null. They go
  // with the next question (knowledge_scope_change), which applies them when
  // it runs -- see scopeAfterPicks. Mirrored in a ref for sendMessage, which
  // must read the latest picks without being rebuilt on every one.
  const [mentionDraft, setMentionDraft] = useState(null);
  const mentionDraftRef = useRef(null);
  const updateMentionDraft = useCallback((conversationId, update) => {
    const current = mentionDraftRef.current?.conversationId === conversationId ? mentionDraftRef.current.picks : [];
    const picks = update(current);
    const next = conversationId && picks.length ? { conversationId, picks } : null;
    mentionDraftRef.current = next;
    setMentionDraft(next);
  }, []);

  // Tracks which file/folder ids the "@" mention flow ITSELF actually added
  // to the knowledge scope, as opposed to ids that were already selected
  // some other way (ProjectPanel's checkboxes, or an earlier mention).
  // Backspace on a mention pill should only undo that mention's own
  // addition -- addFileToKnowledgeScope/addFolderToKnowledgeScope already
  // no-op when the id is already selected, but without this, backspacing a
  // mention of an already-selected file would still unconditionally remove
  // it via removeFileFromKnowledgeScope, silently un-checking a selection
  // the mention never actually made. Keyed by conversation (or
  // '__pending__' before one exists) so switching conversations doesn't let
  // a stale ownership claim from a different conversation's files/folders
  // leak in. See markMentionOwned/consumeMentionOwned below.
  const mentionOwnedIdsRef = useRef({ key: null, fileIds: new Set(), folderIds: new Set() });
  const getMentionOwnershipKey = () => activeConversation?.conversation_id || '__pending__';
  const markMentionOwned = (kind, id) => {
    const key = getMentionOwnershipKey();
    if (mentionOwnedIdsRef.current.key !== key) {
      mentionOwnedIdsRef.current = { key, fileIds: new Set(), folderIds: new Set() };
    }
    (kind === 'folder' ? mentionOwnedIdsRef.current.folderIds : mentionOwnedIdsRef.current.fileIds).add(id);
  };
  const consumeMentionOwned = (kind, id) => {
    const key = getMentionOwnershipKey();
    if (mentionOwnedIdsRef.current.key !== key) return false;
    const set = kind === 'folder' ? mentionOwnedIdsRef.current.folderIds : mentionOwnedIdsRef.current.fileIds;
    if (!set.has(id)) return false;
    set.delete(id);
    return true;
  };

  // Same reasoning, for the callback itself: App.js doesn't (and shouldn't
  // need to) wrap it in useCallback, so referencing the prop directly inside
  // the streaming handler below would pull it into that handler's own
  // dependency array and force it to be rebuilt on every parent render.
  const onProjectConversationFiledRef = useRef(onProjectConversationFiled);
  useEffect(() => {
    onProjectConversationFiledRef.current = onProjectConversationFiled;
  }, [onProjectConversationFiled]);

  // Deliberately initialized to undefined rather than the current id, so a
  // conversation that is already active on mount (a session restored from a
  // previous page load) still hydrates on the first run.
  const prevConversationIdForRulesRef = useRef(undefined);
  useEffect(() => {
    const prevId = prevConversationIdForRulesRef.current;
    const nextId = activeConversation?.conversation_id;
    const isFreshAutoCreate = !!nextId && justAutoCreatedConversationIdRef.current === nextId;
    if (isFreshAutoCreate) justAutoCreatedConversationIdRef.current = null;
    if (prevId !== nextId && !isFreshAutoCreate) {
      setAttachedRuleIds(activeConversation?.active_rule_ids || []);
      // A quote is scoped to the conversation it was made in -- switching
      // chats (or starting a New Chat) must not carry it into the next one.
      setReplyContext(null);
      setSelectionPopup(null);
      // Unsent "@" picks belong to the conversation they were made in.
      mentionDraftRef.current = null;
      setMentionDraft(null);
    }
    prevConversationIdForRulesRef.current = nextId;
  }, [activeConversation?.conversation_id, activeConversation?.active_rule_ids]);

  useEffect(() => {
    if (initialContextUsage && initialContextUsage.context_window) {
      const used = (initialContextUsage.cumulative_input || 0) + (initialContextUsage.cumulative_output || 0);
      const limit = initialContextUsage.context_window;
      setContextUsage({ used, limit, percentage: Math.round((used / limit) * 100) });
      setContextWarningDismissed(false);
    }
  }, [initialContextUsage]);

  useEffect(() => {
    if (isLoading && isListening) {
      stopListening();
    }
  }, [isLoading, isListening, stopListening]);

  // Focus input after sending message
  const focusInput = useCallback(() => {
    setTimeout(() => {
      // Only one of these is ever mounted at a time -- see richInputRef.
      const el = inputRef.current || richInputRef.current;
      if (el) {
        el.focus();
      }
    }, 100);
  }, []);

  const handleMicToggle = useCallback(async () => {
    clearSpeechError();
    if (isListening) {
      await stopListening();
      focusInput();
      return;
    }
    await startListening(inputMessage, setInputMessage);
  }, [clearSpeechError, focusInput, inputMessage, isListening, startListening, stopListening]);

  // Auto-resize textarea function
  const autoResizeTextarea = (textarea) => {
    if (!textarea) return;
    
    // Reset height to calculate new height
    textarea.style.height = 'auto';
    
    // Set new height based on scroll height
    const newHeight = Math.min(textarea.scrollHeight, 200); // Max 200px
    textarea.style.height = newHeight + 'px';
  };

  // Handle image file selection with resizing.
  // Wrapped in useCallback (with stable setSelectedImages) so handlePaste
  // — which depends on it — can also be stable, keeping the global
  // 'paste' useEffect's dependency array stable across renders.
  const handleImageSelect = useCallback(async (files) => {
    const imageFiles = Array.from(files).filter(file =>
      file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024 // 10MB limit
    );

    for (const file of imageFiles) {
      try {
        const resizedImage = await resizeImage(file, 1200, 1200, 0.8);

        const imageData = {
          id: Date.now() + Math.random(),
          file: file,
          url: resizedImage.dataUrl,
          name: generateUniqueFilename(file.name),
          originalSize: resizedImage.originalSize,
          newSize: resizedImage.newSize,
          dimensions: `${resizedImage.width}x${resizedImage.height}`,
        };

        setSelectedImages(prev => [...prev, imageData]);

      } catch (error) {
        console.error('Error resizing image:', error);

        // Fallback to original file if resizing fails
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageData = {
            id: Date.now() + Math.random(),
            file: file,
            url: e.target.result,
            name: generateUniqueFilename(file.name),
          };
          setSelectedImages(prev => [...prev, imageData]);
        };
        reader.readAsDataURL(file);
      }
    }
  }, []);

  // The project an attachment in this conversation could be added to (the
  // conversation's own, or the one about to be created -- see
  // pendingProjectForNextConversation), or null. Null too for a View member:
  // adding to a project's knowledge base needs Edit (the server refuses it
  // otherwise), so their file is always an ordinary attachment -- no "add to
  // knowledge base?" prompt, no project_id, the normal size cap. An unknown
  // role (the project list not loaded yet) is left for the server to decide.
  const knowledgeBaseProjectId = () => {
    if (getActiveProject()?.my_role === 'view') return null;
    return chatProjectId();
  };

  // The project this chat is in (or is about to be created in), whatever
  // this user's role there, or null.
  const chatProjectId = () => (
    activeConversation?.project_id
      || (!activeConversation ? pendingProjectForNextConversation?.project_id : null)
  );

  // How a too-large refusal words its advice in this chat: 'kb' when the
  // file could go into this project's knowledge base instead, 'view' when
  // the chat is in a project this user can only view, null outside a
  // project. Sent to /upload-file as project_hint so the server's own
  // refusal (the token check, which only it can run) says the same.
  const projectAttachHint = () => {
    if (knowledgeBaseProjectId()) return 'kb';
    return chatProjectId() ? 'view' : null;
  };

  // Route an incoming FileList/array (from the picker OR a drag-and-drop) into
  // the existing image + document handlers. Single code path for both entry
  // points.
  const processIncomingFiles = (fileList) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;

    // Cap total attachments per prompt (existing + incoming) so a large drop
    // can't silently fill the model's context window.
    const alreadyAttached = selectedImages.length + selectedFiles.length + attachmentUploads.length;
    const availableSlots = MAX_ATTACHMENTS_PER_PROMPT - alreadyAttached;
    if (availableSlots <= 0) {
      showUploadNotice(
        'Too many attachments',
        `You can attach up to ${MAX_ATTACHMENTS_PER_PROMPT} files per prompt. Remove some attachments before adding more.`
      );
      return;
    }

    let incoming = files;
    if (files.length > availableSlots) {
      showUploadNotice(
        'Some files were skipped',
        `Only ${availableSlots} more file(s) can be attached (max ${MAX_ATTACHMENTS_PER_PROMPT} per prompt). The first ${availableSlots} were added; the rest were skipped.`
      );
      incoming = files.slice(0, availableSlots);
    }

    // Whether THIS conversation could scope an upload into a project's
    // library at all (see getMaxSizeForFile) -- just a candidate at this
    // point, same as handleDocumentUpload's own uploadProjectId: the user
    // still gets asked per file before anything is actually sent that way.
    const isProjectUploadCandidate = Boolean(knowledgeBaseProjectId());

    const oversized = incoming.filter(file => file.size > getMaxSizeForFile(file, isProjectUploadCandidate));
    const withinSizeLimit = incoming.filter(file => file.size <= getMaxSizeForFile(file, isProjectUploadCandidate));
    if (oversized.length > 0) {
      const limitOf = (f) => getMaxSizeForFile(f, isProjectUploadCandidate);
      const lines = oversized.map(f => `• ${f.name} - ${formatSize(f.size)} (limit ${formatSize(limitOf(f))})`);
      // The project suggestion only when this conversation isn't already in
      // one (a file too big even for a project's knowledge base gets nothing
      // from it). Only shown once, for a document in the batch a project's
      // knowledge base would take (if any).
      const documentOversized = oversized.find(isIndexableDocument);
      showUploadNotice(
        oversized.length === 1
          ? `"${oversized[0].name}" is too large to attach`
          : 'Some files are too large to attach',
        (oversized.length === 1
          ? tooLargeToAttachText(oversized[0], limitOf(oversized[0]))
          : `These files are over the limit for attachments, so they haven't been attached:\n\n${lines.join('\n')}`)
        + (documentOversized && !isProjectUploadCandidate
          ? projectSizeSuggestion(documentOversized, projectAttachHint())
          : '')
      );
    }

    const MAX_IMAGE_FILE_SIZE = 10 * 1024 * 1024; // keep in sync with handleImageSelect

    const tooLargeImages = withinSizeLimit.filter(file => file.type.startsWith('image/') && file.size > MAX_IMAGE_FILE_SIZE);
    const imageFiles = withinSizeLimit.filter(file => file.type.startsWith('image/') && file.size <= MAX_IMAGE_FILE_SIZE);
    const documentFiles = withinSizeLimit.filter(file => !file.type.startsWith('image/'));

    if (tooLargeImages.length > 0) {
      const lines = tooLargeImages.map(f => `• ${f.name} — ${formatMB(f.size)}MB, limit is 10MB`);
      showUploadNotice(
        tooLargeImages.length === 1 ? 'Image too large to attach' : 'Images too large to attach',
        `These image file(s) were skipped:\n\n${lines.join('\n')}`
      );
    }
    if (imageFiles.length > 0) {
      handleImageSelect(imageFiles);
    }

    if (documentFiles.length > 0) {
      handleDocumentUpload(documentFiles);
    }
  };

  // Handle file input change - both images and documents
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      processIncomingFiles(e.target.files);
      e.target.value = ''; // Reset file input
    }
  };

  // Handle paste event for images. useCallback so the global 'paste'
  // listener in the keyboard shortcuts useEffect doesn't re-register on
  // every keystroke.
  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          await handleImageSelect([file]);
        }
        break;
      }
    }
  }, [handleImageSelect]);

  // Remove selected image
  const removeImage = (imageId) => {
    setSelectedImages(prev => prev.filter(img => img.id !== imageId));
  };

  // Handle document file upload
  // Shared by both the video and non-video upload paths: moves a finished
  // upload from the "uploading" chip list into selectedFiles, unless it
  // would push the prompt's total attached-file tokens past the hard cap.
  const commitUploadedFile = (uploadId, file, result) => {
    const fileData = {
      id: uploadId,
      name: result.name,
      content: result.content,
      size: result.size,
      type: result.type,
      tokens: result.tokens,
      // Set only for a very large indexed PDF: a one-line nudge toward
      // Projects, rendered under the file chip. Never sent to the model.
      notice: result.notice || null
    };

    // No client-side bookkeeping needed for .docx template profiles: the backend
    // already cached it (if any) under docxTemplateTokenRef's current value,
    // keyed by filename, available for the rest of this conversation without
    // this component holding or re-sending it. See docxTemplateTokenRef above.

    setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
    // Checked against the ref, not inside a setSelectedFiles updater: an
    // updater can run twice (StrictMode), which would show its message twice.
    const current = selectedFilesRef.current;
    const currentTotal = current.reduce((sum, f) => sum + (f.tokens || 0), 0);
    if (currentTotal + (result.tokens || 0) > MAX_ATTACHMENT_TOKENS_TOTAL) {
      // Inside a project chat this points at that project's knowledge
      // base (a file added there from the chat is never attached).
      const projectHint = projectAttachHint();
      let suggestion = '';
      if (projectHint === 'kb') {
        suggestion = "\n\nAdd it to this project's Knowledge base instead: attach it again and "
          + 'choose "Add to knowledge base". Files there aren\'t attached to your message, '
          + 'so they don\'t count toward this limit.';
      } else if (projectHint === 'view') {
        suggestion = "\n\nAn indexed document in this project's Knowledge base counts as only a few "
          + 'dozen tokens per prompt, but you have view access only -- ask the project owner or '
          + 'an editor to add it.';
      } else {
        suggestion = '\n\nWant KAI to work with this file anyway? Create a Personal Project and add it to '
          + "that project's Knowledge base -- an indexed document there counts as only a few dozen "
          + 'tokens per prompt, however large the source file is.';
      }
      showUploadNotice(
        `"${file.name}" wasn't attached`,
        `Adding it (~${result.tokens.toLocaleString()} tokens) would exceed the ` +
        `${MAX_ATTACHMENT_TOKENS_TOTAL.toLocaleString()}-token limit per prompt ` +
        `(~${currentTotal.toLocaleString()} tokens already attached). Remove some attachments first.${suggestion}`
      );
      return;
    }
    if (result.tokens > ATTACHMENT_TOKEN_WARNING) {
      showUploadNotice(
        `"${file.name}" is large`,
        `It's ~${result.tokens.toLocaleString()} tokens, over the ` +
        `${ATTACHMENT_TOKEN_WARNING.toLocaleString()}-token guideline. It was attached, but ` +
        `very large files can crowd out context, reduce response quality, and model responses ` +
        `may be delayed.`
      );
    }
    selectedFilesRef.current = [...current, fileData];
    setSelectedFiles(prev => [...prev, fileData]);
  };

  const handleDocumentUpload = async (files) => {
    for (const file of files) {
      // Generate a unique ID for this upload
      const uploadId = Date.now() + Math.random();
      const isMedia = isTranscribableMedia(file);
      // Outside the try so a failure can still refresh the knowledge base.
      let uploadProjectId = null;

      try {
        // A file (document OR audio/video, hence computed before the isMedia
        // split below) attached inside a project's conversation CAN index
        // into that project's shared library, not just the user's personal
        // scope -- that's what makes it visible from every other
        // conversation in the project instead of just this one. Falls back
        // to the about-to-be-created conversation's target project when
        // there's no activeConversation yet (see pendingProjectForNextConversation).
        // Computed before the size check below (not just before the KB-choice
        // prompt further down): a candidate project scope raises the size cap
        // (see getMaxSizeForFile), so a large file shouldn't be rejected here
        // before the user's even had the chance to choose "add to knowledge
        // base" for it. Null for a View member -- see knowledgeBaseProjectId.
        uploadProjectId = knowledgeBaseProjectId();
        const projectHint = projectAttachHint();

        // Check file size (audio/video files get a larger cap than regular
        // documents; a project-scoped document only S3's own limit).
        const maxSize = getMaxSizeForFile(file, Boolean(uploadProjectId));
        if (file.size > maxSize) {
          // Only when this isn't already a project-scoped candidate -- if it
          // were, the file is already too big for a project's knowledge base
          // (or is a too-large recording), which a project can't help with.
          const suggestion = uploadProjectId ? '' : projectSizeSuggestion(file, projectHint);
          showUploadNotice(
            `"${file.name}" is too large to attach`,
            `${tooLargeToAttachText(file, maxSize)}${suggestion}`
          );
          continue;
        }

        // Add to uploading files immediately to show loading state
        const uploadingFile = {
          id: uploadId,
          name: file.name,
          size: file.size,
          type: file.name.split('.').pop().toLowerCase(),
          progress: null,
          stage: isMedia ? 'uploading' : null,
        };
        setUploadingFiles(prev => [...prev, uploadingFile]);

        // Inside a project chat, nothing is sent until this is settled:
        //  - A file whose name is already in the knowledge base isn't
        //    uploaded or attached again -- KAI can use the copy there.
        //  - Otherwise an Edit member chooses: add it to the knowledge base
        //    (then it is NOT attached to this message, so it costs this chat
        //    no tokens; KAI finds it there) or attach it to this chat only.
        //    A document over the chat attachment limit is neither: the user
        //    is told to upload it in the Knowledge base tab themselves.
        // Only toKnowledgeBase sends project_id, the thing that actually
        // files the upload into the project's library (see chat_routes.py's
        // should_index and video_transcribe_service's project-scoped indexing).
        // Anything else (a 3D model) can't go into the knowledge base, so it
        // is always an ordinary attachment.
        let toKnowledgeBase = false;
        const projectId = chatProjectId();
        if (!isIndexableDocument(file) && !isMedia) uploadProjectId = null;
        if (projectId && (isIndexableDocument(file) || isMedia)) {
          setUploadingFiles(prev => prev.map(f => (
            f.id === uploadId ? { ...f, stage: 'awaiting-kb-choice' } : f
          )));
          const duplicate = await findKnowledgeBaseFile(projectId, file.name);
          let choice = 'chat';
          if (duplicate) {
            choice = await askAddToKnowledgeBase(file.name, {
              reason: 'duplicate', duplicate, canEdit: Boolean(uploadProjectId),
            });
          } else if (uploadProjectId) {
            const chatLimit = getMaxSizeForFile(file, false);
            choice = await askAddToKnowledgeBase(file.name, file.size > chatLimit
              ? { reason: 'too-large', size: file.size, limit: chatLimit }
              : { reason: 'choose' });
          }
          if (choice === 'cancel') {
            setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
            continue;
          }
          toKnowledgeBase = choice === 'add';
          if (!toKnowledgeBase) uploadProjectId = null;
          setUploadingFiles(prev => prev.map(f => (
            f.id === uploadId ? { ...f, stage: isMedia ? 'uploading' : null, toKnowledgeBase } : f
          )));
        }

        let result;
        if (isMedia) {
          const controller = new AbortController();
          videoUploadControllers.current.set(uploadId, controller);
          try {
            result = await uploadVideo(file, {
              signal: controller.signal,
              projectId: uploadProjectId,
              onProgress: (progress) => {
                setUploadingFiles(prev => prev.map(f => (
                  f.id === uploadId
                    ? { ...f, progress, stage: progress === null ? 'transcribing' : (progress >= 100 ? 'transcribing' : 'uploading') }
                    : f
                )));
              },
            });
          } finally {
            videoUploadControllers.current.delete(uploadId);
          }
        } else if (uploadProjectId && isIndexableDocument(file)) {
          // The one case that actually needs the direct-to-S3 path: a
          // project's knowledge base has no size limit, but the proxied
          // /upload-file route still buffers the whole body through our own
          // backend first -- see uploadDocumentDirect. Every other upload
          // (images, 3D models, a non-project PDF, a small attachment)
          // already has a real, much smaller cap the proxied path below
          // handles fine, so this is scoped to just the case that matters.
          const controller = new AbortController();
          videoUploadControllers.current.set(uploadId, controller);
          try {
            result = await uploadDocument(file, {
              signal: controller.signal,
              projectId: uploadProjectId,
              onProgress: (progress) => {
                setUploadingFiles(prev => prev.map(f => (
                  f.id === uploadId
                    ? { ...f, progress, stage: progress === null ? 'indexing' : (progress >= 100 ? 'indexing' : 'uploading') }
                    : f
                )));
              },
            });
          } finally {
            videoUploadControllers.current.delete(uploadId);
          }
        } else {
          // Create FormData for file upload
          const formData = new FormData();
          formData.append('file', file);
          // Scopes a .docx's extracted template profile to this conversation on
          // the backend (see docxTemplateTokenRef above); harmless no-op for
          // every other file type.
          formData.append('page_token', docxTemplateTokenRef.current);
          if (uploadProjectId) {
            formData.append('project_id', uploadProjectId);
          } else if (projectHint) {
            // A project chat's file kept out of the knowledge base: if it's
            // too heavy to attach, the refusal should point at this
            // project's knowledge base, not at creating a new project.
            formData.append('project_hint', projectHint);
          }

          const apiBase = getApiBaseUrl();
          const response = await authenticatedFetch(`${apiBase}/upload-file`, {
            method: 'POST',
            headers: {}, // Don't set Content-Type for FormData
            body: formData
          });

          if (!response.ok) {
            const error = await safeJsonParse(response);
            throw new Error(error?.detail || `Upload failed (status ${response.status})`);
          }

          result = await response.json();

          // A heavy PDF isn't extracted inline — the backend answers 202 with a
          // job id and indexes it in the background, so poll the same job
          // endpoint the video path uses. Because this whole call is awaited
          // before the file is added to selectedFiles, the indexing overlaps
          // with the user typing their question rather than delaying it.
          if (response.status === 202 && result?.job_id && !result?.success) {
            setUploadingFiles(prev => prev.map(f => (
              f.id === uploadId ? { ...f, stage: 'indexing' } : f
            )));
            result = await pollUploadJob(result.job_id, undefined, uploadProjectId);
          }
        }

        if (toKnowledgeBase) {
          // In the knowledge base, not attached to this message.
          setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
          await finishKnowledgeBaseUpload(uploadProjectId, result.file_id);
          continue;
        }
        commitUploadedFile(uploadId, file, result);

      } catch (error) {
        console.error('Error uploading file:', error);
        // Remove from uploading files on error
        videoUploadControllers.current.delete(uploadId);
        setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
        // Indexing may still be running (or have left a failed entry), so
        // the Knowledge base list should show it either way.
        if (uploadProjectId) notifyProjectFilesChanged(uploadProjectId);
        showUploadNotice(`Couldn't upload "${file.name}"`, error.message);
      }
    }
  };

  // Tells an open Knowledge base panel (and the "@" file list) to reload --
  // a file added from the chat otherwise only shows up after a refresh.
  const notifyProjectFilesChanged = (projectId) => {
    window.dispatchEvent(new CustomEvent(PROJECT_FILES_UPDATED_EVENT, { detail: { project_id: projectId } }));
  };

  // A file already in the project's knowledge base under the same name
  // (ignoring case), or null -- the same name-only check ProjectPanel's own
  // upload runs. A failed lookup just skips the check.
  const findKnowledgeBaseFile = async (projectId, fileName) => {
    try {
      const lower = fileName.toLowerCase();
      const files = await listProjectFiles(projectId);
      return files.find((f) => f.name?.toLowerCase() === lower) || null;
    } catch (error) {
      console.warn('Could not check the knowledge base for a file with the same name:', error);
      return null;
    }
  };

  // After a chat upload went into the project's knowledge base: a
  // conversation limited to selected files gets it added to its selection
  // (otherwise KAI would refuse the very file just added from here), and the
  // Knowledge base panel reloads to show it.
  const finishKnowledgeBaseUpload = async (projectId, fileId) => {
    if (fileId && currentKnowledgeScope?.mode === 'selected') {
      await applyMentionScopeChange((scope) => (
        scope?.mode === 'selected' ? addFileToKnowledgeScope(scope, fileId) : scope
      ));
    }
    notifyProjectFilesChanged(projectId);
  };

  // Remove selected file
  const removeFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  // --- Custom rules attach/detach ---------------------------------------
  // Toggling updates local state immediately (so the checkbox/chip responds
  // without waiting on a round-trip) and, when a conversation already
  // exists, persists via PATCH so the selection survives a reload. For a
  // brand-new chat there's nothing to PATCH yet -- the current selection
  // rides along on the very next /chat POST via active_rule_ids, and the
  // backend persists it onto the conversation it auto-creates for that turn.
  //
  // Detaching a rule can never introduce a conflict, so it never needs the
  // check below. Attaching one might: two rules can each individually pass
  // the save-time policy gate and still contradict each other (e.g. "always
  // sound human" vs "sound robotic for generic messages"), and having both
  // in play at once is exactly the scenario that would confuse KAI. So
  // attaching runs a conflict check against whatever's already attached
  // FIRST and blocks with a clear message if one is found, rather than
  // silently letting both into the same conversation.
  // conversationId is passed in, never read off activeConversation here: the
  // caller queues these behind one another, so by the time this actually runs
  // the user may have switched conversations -- reading it now would write
  // one conversation's rule set onto another.
  const persistActiveRuleIds = async (ruleIds, conversationId) => {
    if (!conversationId) return { ok: true };
    // These PATCHes are serialized (see queueActiveRulesPatch), so one that
    // hangs blocks every later toggle behind it. The server bounds its own
    // work (find_conflict_within_set has a deadline), but a stalled
    // connection isn't the server's to bound -- so give up client-side too
    // and let the normal failure path revert the chip.
    const abort = new AbortController();
    const timeoutId = setTimeout(() => abort.abort(), ACTIVE_RULES_PATCH_TIMEOUT_MS);
    try {
      const apiBase = getApiBaseUrl();
      const res = await authenticatedFetch(
        `${apiBase}/conversations/${conversationId}/active-rules`,
        {
          method: 'PATCH',
          body: JSON.stringify({ rule_ids: ruleIds }),
          signal: abort.signal,
        }
      );
      if (!res.ok) {
        let detail = 'Failed to save attached rules.';
        try {
          const body = await res.json();
          if (body?.detail) detail = body.detail;
        } catch {
          // ignore parse failure, use default message
        }
        console.error('Failed to persist attached rules:', detail);
        return { ok: false, status: res.status, detail };
      }
      return { ok: true };
    } catch (error) {
      if (error?.name === 'AbortError') {
        console.error('Timed out persisting attached rules');
      } else {
        console.error('Error persisting attached rules:', error);
      }
      return { ok: false };
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // Serialize the PATCHes and coalesce superseded ones. Each request sends the
  // FULL desired set rather than a delta, so two quick toggles were racing:
  // detaching A then B fires [B] then [], and independent HTTP requests have
  // no ordering guarantee -- if [B] landed last the server kept B while the
  // UI showed none, and B reappeared on the next reload. Chaining means a
  // request only starts once the previous one has settled, so the server's
  // final state always matches the last click; the seq check then skips any
  // request already superseded while it waited, since the newer one carries
  // the full truth anyway.
  const activeRulesPatchChainRef = useRef(Promise.resolve());
  const activeRulesSeqRef = useRef(0);

  const queueActiveRulesPatch = (ruleIds, seq, conversationId) => {
    const run = activeRulesPatchChainRef.current.then(() => {
      if (seq !== activeRulesSeqRef.current) return { ok: true, superseded: true };
      return persistActiveRuleIds(ruleIds, conversationId);
    });
    // Keep the chain usable regardless of how this link settled.
    activeRulesPatchChainRef.current = run.then(() => undefined, () => undefined);
    return run;
  };

  // Mirrors attachedRuleIds so a toggle computes its next set from the LATEST
  // selection rather than the value captured when its render ran -- the
  // payload we send has to match what the user currently sees, not what was
  // on screen when they clicked.
  const attachedRuleIdsRef = useRef(attachedRuleIds);
  useEffect(() => {
    attachedRuleIdsRef.current = attachedRuleIds;
  });

  const toggleAttachedRule = async (ruleId) => {
    setRulesAttachError(null);

    // Pinned now so the PATCH lands on the conversation the user actually
    // toggled, even if they switch away while it is queued or in flight.
    const conversationId = activeConversation?.conversation_id;
    const current = attachedRuleIdsRef.current;
    const isAttached = current.includes(ruleId);

    if (!isAttached && current.length >= MAX_ACTIVE_RULES_PER_CONVERSATION) return;

    // Detaching can never introduce a conflict, so only attaching pays for
    // the check. Two rules can each pass the save-time policy gate and still
    // contradict each other, and having both in play at once is exactly the
    // scenario that would confuse KAI. Same reasoning extends to this
    // conversation's OWN project (if any): its free-text instructions apply
    // to every conversation in it, so a personal rule that contradicts them
    // is exactly as confusing as two contradictory personal rules -- see
    // check-attach-conflict's project_id handling.
    if (!isAttached) {
      const projectId = activeConversation?.project_id
        || (!activeConversation ? pendingProjectForNextConversation?.project_id : null);
      setCheckingAttachConflict(true);
      try {
        const apiBase = getApiBaseUrl();
        const res = await authenticatedFetch(`${apiBase}/custom-rules/check-attach-conflict`, {
          method: 'POST',
          body: JSON.stringify({
            candidate_rule_id: ruleId,
            already_attached_rule_ids: current,
            project_id: projectId || undefined,
          }),
        });
        if (res.ok) {
          const body = await res.json();
          if (body.conflict) {
            setRulesAttachError(
              body.conflicting_rule_id === '__project_instructions__'
                ? `Can't attach: conflicts with this project's instructions.`
                : `Can't attach: conflicts with "${body.conflicting_rule_title}".`
            );
            return;
          }
        }
        // If the check call itself failed (network error, 5xx), fail open --
        // an outage in this best-effort check should never block attaching.
        // The PATCH-level check is a backstop for anything this misses.
      } catch (error) {
        console.error('Error checking rule attach conflict (failing open):', error);
      } finally {
        setCheckingAttachConflict(false);
      }
    }

    // Recomputed from the ref, not `current`: the conflict check above is an
    // await, so the selection may have moved on while it was in flight.
    const base = attachedRuleIdsRef.current;
    const next = isAttached
      ? base.filter((id) => id !== ruleId)
      : (base.includes(ruleId) ? base : [...base, ruleId]);

    const seq = ++activeRulesSeqRef.current;
    attachedRuleIdsRef.current = next;
    setAttachedRuleIds(next);

    const result = await queueActiveRulesPatch(next, seq, conversationId);

    // A newer toggle has taken over: its own PATCH carries the authoritative
    // set, so this one's outcome is neither ours to report nor to roll back.
    if (result.superseded || seq !== activeRulesSeqRef.current) return;

    if (!result.ok) {
      const reverted = isAttached
        ? [...next, ruleId]
        : next.filter((id) => id !== ruleId);
      attachedRuleIdsRef.current = reverted;
      setAttachedRuleIds(reverted);
      setRulesAttachError(
        result.status === 409
          // The PATCH-level backstop caught a conflict the pre-check missed
          // (race, stale data) -- surface it the same way.
          ? result.detail
          : isAttached
            ? "Couldn't detach that rule. Please try again."
            : "Couldn't attach that rule. Please try again."
      );
    }
  };

  // This project's own standing rule ids -- default_rule_ids on the project
  // object itself (see ProjectResponse on the backend), not a separate fetch.
  // Mirrors getActiveProjectName's two branches exactly: with no active
  // conversation yet, pendingProjectForNextConversation already carries its
  // own default_rule_ids synchronously (it came straight from listProjects()/
  // create_project's response in ConversationsSidebar.js), so there's no
  // fetch-completion race to show a momentarily-empty rule list during, the
  // same reason getActiveProjectName reads that object directly instead of
  // through projectsById in this branch.
  const projectDefaultRuleIds = (
    activeConversation?.project_id
      ? projectsById[activeConversation.project_id]?.default_rule_ids
      : (!activeConversation ? pendingProjectForNextConversation?.default_rule_ids : null)
  ) || [];
  const projectDefaultRules = projectDefaultRuleIds
    .map((id) => customRules.find((r) => r.rule_id === id))
    .filter(Boolean);

  // Derived from the intersection with customRules, not attachedRuleIds
  // alone: a rule deleted from the library still leaves its id on the
  // conversation row (the backend drops it silently at chat time), so
  // rendering the raw id list would show a blank chip. This makes a deleted
  // rule simply disappear from the chip row instead.
  //
  // Project defaults are folded in here TOO, ahead of the conversation's own
  // ad-hoc attachments -- same order resolve_effective_rule_ids applies them
  // in on the backend (agent_service.py) -- so a project's standing rules
  // show up as pills the moment the conversation opens, not only once the
  // user manually attaches something. Without this, a project's rules were
  // real and active but had no visible presence in the composer at all,
  // which is exactly the "is my rule actually doing anything?" gap this
  // fixes -- see rebuildRichInputPills' lockedRuleIds for why they render
  // without a remove button.
  const adHocAttachedRules = attachedRuleIds
    .map((id) => customRules.find((r) => r.rule_id === id))
    .filter(Boolean);
  const attachedRules = [
    ...projectDefaultRules,
    ...adHocAttachedRules.filter((r) => !projectDefaultRuleIds.includes(r.rule_id)),
  ];

  // --- Rich (contentEditable) prompt input, active only while a rule is
  // attached -- see richInputRef and the module-level helpers above for why.
  //
  // Always points at the current toggleAttachedRule so a pill's remove
  // button (a raw DOM node created imperatively, not JSX) never calls a
  // stale closure from whenever it happened to be created.
  const toggleAttachedRuleRef = useRef(toggleAttachedRule);
  useEffect(() => {
    toggleAttachedRuleRef.current = toggleAttachedRule;
  });

  // Rebuild the pill chip(s) whenever the attached set OR the displayed
  // content of those rules changes -- NOT on every keystroke. Keyed on a
  // string built from each pill's id + the two fields it actually renders
  // (title as the label, text as the hover tooltip) plus whether it's
  // currently locked (a project default), rather than the `attachedRules`
  // array, whose identity changes on every render regardless of whether
  // anything visible changed. Keying on ids alone left a renamed rule showing
  // its old title in the pill until it was detached and reattached, since
  // editing a rule in CustomRulesPanel refetches customRules without changing
  // which ids are attached -- the locked flag needs the same treatment, so a
  // rule added to (or dropped from) the project's defaults mid-session
  // updates its pill's remove button without requiring an unrelated change.
  const attachedRulePillKey = attachedRules
    .map((r) => `${r.rule_id}:${r.title}:${r.text}:${projectDefaultRuleIds.includes(r.rule_id) ? 'L' : ''}`)
    .join('|');
  useEffect(() => {
    if (attachedRules.length === 0) return;
    rebuildRichInputPills(richInputRef.current, attachedRules, inputMessage, toggleAttachedRuleRef, projectDefaultRuleIds, highlightNames);
    autoResizeTextarea(richInputRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachedRulePillKey]);

  // Pull a programmatic inputMessage change (send/Escape/dictation/a
  // quick-fill button) into the DOM. A no-op while the user is typing
  // directly into the box, since handleRichInput already kept the two in
  // sync -- this only fires real work when they've diverged.
  useEffect(() => {
    if (attachedRules.length === 0) return;
    const el = richInputRef.current;
    if (!el) return;
    if (extractRichInputText(el) === inputMessage) return;
    syncRichInputText(el, inputMessage, highlightNames);
    autoResizeTextarea(el);
    if (pendingRichCaretOffsetRef.current !== null) {
      const offset = pendingRichCaretOffsetRef.current;
      pendingRichCaretOffsetRef.current = null;
      el.focus();
      setRichInputCaretTextOffset(el, offset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputMessage, attachedRules.length]);

  const handleRichInput = () => {
    const el = richInputRef.current;
    if (!el) return;

    // Defensive: a "select all" gesture spans the pill(s) too (contentEditable
    // has no concept of "protected" child nodes), so deleting/typing over a
    // full selection can remove a pill from the DOM without ever going
    // through its own remove button. Treat that as "the DOM lost a pill it
    // shouldn't have" and restore it, rather than silently leaving the rule
    // attached in state (still applied to the next message, still checked in
    // the popover) but invisible in the box.
    const presentPillCount = el.querySelectorAll(`[${RULE_PILL_ATTR}]`).length;
    if (presentPillCount !== attachedRules.length) {
      const text = extractRichInputText(el);
      rebuildRichInputPills(el, attachedRules, text, toggleAttachedRuleRef, projectDefaultRuleIds, highlightNames);
      setInputMessage(text);
      stopListeningIfTyping(text);
      autoResizeTextarea(el);
      return;
    }

    const nextValue = extractRichInputText(el);
    setInputMessage(nextValue);
    stopListeningIfTyping(nextValue);
    // Unlike the plain <textarea> (a replaced element whose CSS height is
    // its own actual box), .rich-input shares .message-input's fixed
    // height: 36px as a literal contentEditable <div> -- nothing but this
    // ever overrides that with an inline style, so without it the box
    // stayed pinned at one line (clipped/scrolling internally via
    // overflow-y: auto) no matter how long the typed question got, instead
    // of growing the way the plain composer does.
    autoResizeTextarea(el);
    // Same "@" detection the plain textarea's own onChange runs -- without
    // this, the mention popover simply never opened while a custom rule was
    // attached (this handler is the rich box's only equivalent of that
    // onChange). Falls back to the end of the text on the rare caret
    // position getRichInputCaretTextOffset can't resolve, rather than
    // silently skipping the check.
    const caretOffset = getRichInputCaretTextOffset(el);
    updateMentionFromCaret(nextValue, caretOffset === null ? nextValue.length : caretOffset);
  };

  // Typing by hand ends dictation, mirroring the plain textarea's onChange.
  // The re-set after stopListening() resolves matters because stopping can
  // flush a final transcript into inputMessage, which would otherwise clobber
  // what the user just typed. Without this, dictation kept running while a
  // rule was attached (the rich input is a different element with its own
  // handler, so it never inherited the textarea's behavior).
  const stopListeningIfTyping = (nextValue) => {
    if (!isListening) return;
    void stopListening().finally(() => setInputMessage(nextValue));
  };

  const handleRichKeyDown = (e) => {
    if (e.key === 'Enter' && e.shiftKey) {
      // Left alone, the browser's default contentEditable newline behavior
      // wraps lines in <div>/<br> elements (browser-dependent), which
      // extractRichInputText would then need to translate back into '\n'.
      // Inserting the character directly keeps the DOM as plain text nodes,
      // matching how a <textarea> newline behaves and keeping extraction simple.
      e.preventDefault();
      document.execCommand('insertText', false, '\n');
      return;
    }
    // Same mention popover keyboard handling (arrow-key nav, Enter/Tab to
    // pick a candidate, Escape to dismiss, atomic Backspace on a completed
    // pill) as the plain textarea's own onKeyDown -- see
    // handleComposerKeyDown's own caret-offset handling for how it reads a
    // caret position from this contentEditable box instead of a textarea's
    // selectionStart.
    handleComposerKeyDown(e);
  };

  const handleRichPaste = (e) => {
    const items = e.clipboardData?.items;
    const hasImage = items && Array.from(items).some((item) => item.type.startsWith('image/'));
    if (hasImage) {
      // Let the existing global 'paste' listener (handlePaste) handle it --
      // it calls preventDefault itself when it finds an image item.
      return;
    }
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain') || '';
    if (text) {
      document.execCommand('insertText', false, text);
    }
  };

  // --- Drag-and-drop attachments ---------------------------------------
  // Users can drop files anywhere over the chat to attach them (no need to
  // hunt through the file picker). Disabled in the shared-conversation view
  // (no composer). We only react to actual file drags so dragging selected
  // text or a message doesn't trigger the overlay.
  const handleDragEnter = (e) => {
    if (sharedConversation || !Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    dragCounter.current += 1;
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    if (sharedConversation || !e.dataTransfer.types?.includes('Files')) return;
    e.preventDefault(); // required so the drop event actually fires
  };

  const handleDragLeave = (e) => {
    if (sharedConversation) return;
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    if (sharedConversation) return;
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      processIncomingFiles(e.dataTransfer.files);
    }
  };

  // Prevent the browser from navigating away / opening a file when one is
  // dropped just outside the chat drop zone (e.g. on the sidebar or window
  // chrome). Without this a near-miss drop replaces the app with the file.
  useEffect(() => {
    const preventDefaultDrop = (e) => {
      if (e.dataTransfer?.types?.includes('Files')) {
        e.preventDefault();
      }
    };
    // Defensive reset: some browsers (and an Esc-cancelled drag) don't reliably
    // fire dragleave on .chat-interface, which would leave the overlay stuck.
    // dragend/drop at the window level guarantee we clear the drag state.
    const resetDragState = () => {
      dragCounter.current = 0;
      setIsDragging(false);
    };
    window.addEventListener('dragover', preventDefaultDrop);
    window.addEventListener('drop', preventDefaultDrop);
    window.addEventListener('dragend', resetDragState);
    window.addEventListener('drop', resetDragState);
    return () => {
      window.removeEventListener('dragover', preventDefaultDrop);
      window.removeEventListener('drop', preventDefaultDrop);
      window.removeEventListener('dragend', resetDragState);
      window.removeEventListener('drop', resetDragState);
    };
  }, []);

  // Fetch focus modes on mount
  useEffect(() => {
    const fetchFocusModes = async () => {
      try {
        const apiBase = getApiBaseUrl();
        const token = localStorage.getItem('access_token');
        
        // Try authenticated request if token exists, otherwise use unauthenticated
        let response;
        if (token) {
          try {
            response = await authenticatedFetch(`${apiBase}/focus-modes`);
          } catch (authError) {
            // If authenticated fetch fails, try unauthenticated (token might be invalid)
            console.warn('Authenticated fetch failed, trying unauthenticated:', authError);
            response = await fetch(`${apiBase}/focus-modes`);
          }
        } else {
          // No token, use unauthenticated request
          response = await fetch(`${apiBase}/focus-modes`);
        }
        
        const data = await response.json();
        setFocusModes(data.focus_modes || []);
        setFocusModesLoaded(true);
      } catch (error) {
        console.error('Error fetching focus modes:', error);
        setFocusModesLoaded(true); // Set loaded even on error to prevent infinite loading
      }
    };
    
    fetchFocusModes();
  }, []);

  // Resolve the active (or about-to-be-created, see pendingProjectForNextConversation
  // below) conversation's project id to a display name for the header badge.
  // One list fetch covers every project id we'll ever need to look up in a
  // session, since projects are few -- refetched only the first time an id
  // isn't already cached, so a rename picked up mid-session still shows the
  // next time this effect re-runs for any conversation switch.
  useEffect(() => {
    const pid = activeConversation?.project_id || (!activeConversation ? pendingProjectForNextConversation?.project_id : null);
    if (!pid || projectsById[pid]) return;
    let cancelled = false;
    listProjects()
      .then((list) => {
        if (cancelled) return;
        setProjectsById((prev) => {
          const next = { ...prev };
          list.forEach((p) => { next[p.project_id] = p; });
          return next;
        });
      })
      .catch((error) => console.error('Error fetching project name:', error));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?.project_id, pendingProjectForNextConversation?.project_id]);

  // Share's "Add to a project" (ShareConversationModal), once the move is
  // done: this conversation is now the project's, and its transcript ends
  // with the note the server added (see move_conversation_to_project).
  const handleAddedToProject = (reply, project) => {
    const moved = reply?.conversation;
    if (!moved || moved.conversation_id !== activeConversation?.conversation_id) return;
    setProjectsById((prev) => ({ ...prev, [project.project_id]: project }));
    setActiveConversation((prev) => (
      prev?.conversation_id === moved.conversation_id ? { ...prev, ...moved } : prev
    ));
    if (reply.note) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: reply.note.text,
        timestamp: new Date().toISOString(),
        additional_data: reply.note.additional_data,
      }]);
    }
    onConversationAddedToProject?.(project);
  };

  // Keep the cached copy above in sync when ProjectSettingsPanel saves a
  // project's rules -- without this, a conversation already open in that
  // project would keep showing its OLD rule pills/popover state (the effect
  // above only fetches once per id, on first sight of it) until the user
  // switched away and back. See PROJECT_UPDATED_EVENT.
  useEffect(() => {
    const onProjectUpdated = (e) => {
      const updated = e.detail;
      if (!updated?.project_id) return;
      setProjectsById((prev) => ({ ...prev, [updated.project_id]: updated }));
    };
    window.addEventListener(PROJECT_UPDATED_EVENT, onProjectUpdated);
    return () => window.removeEventListener(PROJECT_UPDATED_EVENT, onProjectUpdated);
  }, []);

  // Mirrors the effect above, for a conversation's own Knowledge base scope
  // changing from ProjectPanel.js's Files tab checkboxes -- a SIBLING of this
  // component under ProjectView, not a descendant, so it can't reach
  // setActiveConversation/setMessages directly and dispatches this instead
  // (same cross-sibling pattern PROJECT_UPDATED_EVENT already uses). Ignored
  // unless it's for the conversation currently open here. Re-subscribes on
  // activeConversation change so that comparison is always against the
  // current one. See .claude/plan/plan_knowledge_scope.md.
  useEffect(() => {
    const onScopeUpdated = (e) => {
      const { conversation_id: conversationId, knowledge_scope: knowledgeScope, note } = e.detail || {};
      if (!conversationId || conversationId !== activeConversation?.conversation_id) return;
      setActiveConversation((prev) => (prev ? { ...prev, knowledge_scope: knowledgeScope } : prev));
      if (note && activeConversation?.is_group) {
        // Stored in the shared transcript: it arrives with the next poll,
        // for everyone at once, rather than as a second local copy.
        pollGroupChatNow();
      } else if (note) {
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: note.text,
          timestamp: new Date().toISOString(),
          additional_data: note.additional_data,
        }]);
      }
    };
    window.addEventListener(KNOWLEDGE_SCOPE_UPDATED_EVENT, onScopeUpdated);
    return () => window.removeEventListener(KNOWLEDGE_SCOPE_UPDATED_EVENT, onScopeUpdated);
  }, [activeConversation?.conversation_id, activeConversation?.is_group, pollGroupChatNow, setActiveConversation, setMessages]);

  // Project files someone else added after this conversation joined its
  // project, left out of it until the user adds them (see the backend's
  // knowledge_hold_for) -- the "Use in this chat" notice above the composer.
  // There's no push: fetched when the conversation opens and after each
  // reply, which is when a file added meanwhile shows up. Kept in step with
  // the Knowledge base tab through KNOWLEDGE_HOLD_UPDATED_EVENT.
  const [heldFiles, setHeldFiles] = useState([]);
  const [heldFilesBusy, setHeldFilesBusy] = useState(false);
  const [heldFilesError, setHeldFilesError] = useState(null);
  // Bumped when this conversation's file selection changes: picking a held
  // file by name (a checkbox, or "@") adds it, so the notice refetches.
  const [heldRefreshKey, setHeldRefreshKey] = useState(0);
  const heldConversationId = activeConversation?.project_id ? activeConversation.conversation_id : null;
  const heldConversationIdRef = useRef(heldConversationId);
  heldConversationIdRef.current = heldConversationId;

  useEffect(() => {
    setHeldFiles([]);
    setHeldFilesError(null);
  }, [heldConversationId]);

  useEffect(() => {
    if (!heldConversationId || isLoading) return undefined;
    let cancelled = false;
    listHeldFiles(heldConversationId)
      .then((held) => {
        if (cancelled) return;
        window.dispatchEvent(new CustomEvent(KNOWLEDGE_HOLD_UPDATED_EVENT, {
          detail: { conversation_id: heldConversationId, held_files: held },
        }));
      })
      .catch((error) => console.error('Error loading new knowledge base files:', error));
    return () => { cancelled = true; };
  }, [heldConversationId, isLoading, heldRefreshKey]);

  useEffect(() => {
    const onHoldUpdated = (e) => {
      const { conversation_id: conversationId, held_files: held, note } = e.detail || {};
      if (!conversationId || conversationId !== heldConversationIdRef.current) return;
      setHeldFiles(held || []);
      if (note) {
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: note.text,
          timestamp: new Date().toISOString(),
          additional_data: note.additional_data,
        }]);
      }
    };
    const onScopeUpdated = (e) => {
      if (e.detail?.conversation_id && e.detail.conversation_id === heldConversationIdRef.current) {
        setHeldRefreshKey((k) => k + 1);
      }
    };
    window.addEventListener(KNOWLEDGE_HOLD_UPDATED_EVENT, onHoldUpdated);
    window.addEventListener(KNOWLEDGE_SCOPE_UPDATED_EVENT, onScopeUpdated);
    return () => {
      window.removeEventListener(KNOWLEDGE_HOLD_UPDATED_EVENT, onHoldUpdated);
      window.removeEventListener(KNOWLEDGE_SCOPE_UPDATED_EVENT, onScopeUpdated);
    };
  }, [setMessages]);

  const visibleHeldFiles = heldFiles.filter((f) => !f.dismissed);

  // Applies to every file the notice lists. Goes through the event (not
  // straight into state) so this and the Knowledge base tab take the same
  // path, and the note is added to the transcript exactly once.
  const answerVisibleHeldFiles = async (choice) => {
    const conversationId = heldConversationId;
    const ids = visibleHeldFiles.map((f) => f.file_id);
    if (!conversationId || !ids.length || heldFilesBusy) return;
    setHeldFilesBusy(true);
    setHeldFilesError(null);
    try {
      const { held_files: held, note } = await answerHeldFiles(
        conversationId, choice === 'accept' ? { accept: ids } : { dismiss: ids },
      );
      window.dispatchEvent(new CustomEvent(KNOWLEDGE_HOLD_UPDATED_EVENT, {
        detail: { conversation_id: conversationId, held_files: held, note },
      }));
    } catch (error) {
      if (heldConversationIdRef.current === conversationId) {
        setHeldFilesError(error.message || "Couldn't update this conversation's files");
      }
    } finally {
      setHeldFilesBusy(false);
    }
  };

  let heldNotice = null;
  if (visibleHeldFiles.length) {
    const people = [...new Set(visibleHeldFiles.map((f) => f.uploaded_by_name || 'Someone'))];
    const who = people.length === 1 ? people[0]
      : people.length === 2 ? `${people[0]} and ${people[1]}` : `${people.length} people`;
    const count = visibleHeldFiles.length;
    const names = visibleHeldFiles.map((f) => f.name);
    heldNotice = {
      title: `${who} added ${count === 1 ? 'a new file' : `${count} new files`} to the knowledge base`,
      names: count <= 3 ? names.join(', ') : `${names.slice(0, 3).join(', ')} and ${count - 3} more`,
      allNames: names.join('\n'),
      hint: `This conversation started before ${count === 1 ? 'it was' : 'they were'} added, so KAI won't use ${count === 1 ? 'it' : 'them'} here until you say so.`,
    };
  }

  // Fetch the user's custom-rules library once, and again whenever
  // CustomRulesPanel saves/deletes a rule, so the attach popover here stays
  // in sync without requiring a page reload.
  // A failed fetch sets customRulesLoadFailed instead of leaving an empty
  // list behind, so the popover can say "couldn't load" rather than the
  // "No saved rules yet" it would otherwise show -- which reads as "your
  // rules are gone" during an outage. `customRules` is left untouched on
  // failure so an already-loaded library survives a later refetch failing.
  useEffect(() => {
    const fetchCustomRules = async () => {
      try {
        const res = await authenticatedFetch(`${getApiBaseUrl()}/custom-rules`);
        if (!res.ok) {
          console.error('Failed to fetch custom rules:', res.status);
          setCustomRulesLoadFailed(true);
          return;
        }
        const body = await res.json();
        setCustomRules(body.rules || []);
        setCustomRulesLoadFailed(false);
      } catch (error) {
        console.error('Error fetching custom rules:', error);
        setCustomRulesLoadFailed(true);
      }
    };

    fetchCustomRules();
    window.addEventListener(CUSTOM_RULES_UPDATED_EVENT, fetchCustomRules);
    return () => window.removeEventListener(CUSTOM_RULES_UPDATED_EVENT, fetchCustomRules);
  }, []);

  const closePlusMenu = useCallback(() => {
    setPlusMenuOpen(false);
    setRulesPopoverOpen(false);
    setRulesAttachError(null);
  }, []);

  // Close the "+" menu (and its rules flyout) on an outside click or Escape.
  useEffect(() => {
    if (!plusMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      const inside = [rulesPopoverRef, plusMenuRef, rulesFlyoutRef]
        .some((ref) => ref.current && ref.current.contains(event.target));
      if (!inside) closePlusMenu();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closePlusMenu();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [plusMenuOpen, closePlusMenu]);

  // Places the "+" menu and its rules flyout wherever they fully fit in the
  // viewport. The menu opens below the button on a new conversation's
  // welcome screen (composer mid-page, like Gemini) and above it otherwise,
  // flipping when that side is too short. The flyout sits right of "Custom
  // rules" (left if there's no room), growing down or up from the item --
  // whichever fits -- and only shrinks and scrolls if neither does.
  const positionPlusMenu = useCallback(() => {
    const MARGIN = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const clamp = (v, min, max) => Math.max(min, Math.min(v, max));
    const anchor = rulesPopoverRef.current;
    const menu = plusMenuRef.current;
    if (!anchor || !menu) return;

    const b = anchor.getBoundingClientRect();
    const mw = menu.offsetWidth;
    const mh = menu.offsetHeight;
    const spaceBelow = vh - b.bottom - 8 - MARGIN;
    const spaceAbove = b.top - 8 - MARGIN;
    const openDown = messages.length === 0
      ? spaceBelow >= mh || spaceBelow >= spaceAbove
      : spaceAbove < mh && spaceBelow > spaceAbove;
    menu.style.top = `${openDown ? b.bottom + 8 : b.top - 8 - mh}px`;
    menu.style.left = `${clamp(b.left, MARGIN, vw - mw - MARGIN)}px`;

    const item = rulesMenuItemRef.current;
    const flyout = rulesFlyoutRef.current;
    if (!item || !flyout) return;
    const i = item.getBoundingClientRect();
    const fw = Math.min(280, vw - 2 * MARGIN);
    flyout.style.width = `${fw}px`;
    flyout.style.maxHeight = '';
    const fh = Math.min(flyout.offsetHeight, vh - 2 * MARGIN);
    flyout.style.maxHeight = `${fh}px`;

    let left;
    if (i.right + 10 + fw <= vw - MARGIN) left = i.right + 10;
    else if (i.left - 10 - fw >= MARGIN) left = i.left - 10 - fw;
    else left = clamp(i.right + 10, MARGIN, vw - fw - MARGIN);

    const fitsDown = i.top + fh <= vh - MARGIN;
    const fitsUp = i.bottom - fh >= MARGIN;
    let top;
    if (fitsDown && (openDown || !fitsUp)) top = i.top;
    else if (fitsUp) top = i.bottom - fh;
    else top = clamp(i.top, MARGIN, vh - MARGIN - fh);
    flyout.style.left = `${left}px`;
    flyout.style.top = `${top}px`;
  }, [messages.length]);

  // Runs before paint, so the menu never flashes at its unpositioned spot.
  // Re-runs when the flyout's content (and so its height) can change.
  useLayoutEffect(() => {
    if (!plusMenuOpen) return undefined;
    positionPlusMenu();
    window.addEventListener('resize', positionPlusMenu);
    window.addEventListener('scroll', positionPlusMenu, true);
    return () => {
      window.removeEventListener('resize', positionPlusMenu);
      window.removeEventListener('scroll', positionPlusMenu, true);
    };
  }, [
    plusMenuOpen, rulesPopoverOpen, positionPlusMenu, customRules,
    customRulesLoadFailed, attachedRuleIds, rulesAttachError, checkingAttachConflict,
  ]);

  // Close the model picker on an outside click or Escape.
  useEffect(() => {
    if (!modelMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      const inside = [modelButtonRef, modelMenuRef]
        .some((ref) => ref.current && ref.current.contains(event.target));
      if (!inside) setModelMenuOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setModelMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [modelMenuOpen]);

  // Places the model picker dropdown wherever it fully fits in the viewport,
  // same self-flipping approach as positionPlusMenu above.
  const positionModelMenu = useCallback(() => {
    const MARGIN = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const clamp = (v, min, max) => Math.max(min, Math.min(v, max));
    const anchor = modelButtonRef.current;
    const menu = modelMenuRef.current;
    if (!anchor || !menu) return;

    const b = anchor.getBoundingClientRect();
    const mw = menu.offsetWidth;
    const mh = menu.offsetHeight;
    const spaceBelow = vh - b.bottom - 8 - MARGIN;
    const spaceAbove = b.top - 8 - MARGIN;
    const openDown = messages.length === 0
      ? spaceBelow >= mh || spaceBelow >= spaceAbove
      : spaceAbove < mh && spaceBelow > spaceAbove;
    menu.style.top = `${openDown ? b.bottom + 8 : b.top - 8 - mh}px`;
    // Right-align to the button (which holds the chevron) rather than left-align --
    // the nickname label makes the button much narrower than the menu's min-width,
    // so left-aligning left it looking unanchored, spilling out under the mic/send
    // buttons instead of staying under the arrow that opened it.
    menu.style.left = `${clamp(b.right - mw, MARGIN, vw - mw - MARGIN)}px`;
  }, [messages.length]);

  useLayoutEffect(() => {
    if (!modelMenuOpen) return undefined;
    positionModelMenu();
    window.addEventListener('resize', positionModelMenu);
    window.addEventListener('scroll', positionModelMenu, true);
    return () => {
      window.removeEventListener('resize', positionModelMenu);
      window.removeEventListener('scroll', positionModelMenu, true);
    };
  }, [modelMenuOpen, positionModelMenu, availableModels]);

  // Load session history on mount -- deliberately NOT re-run on every
  // selectedModel change (it used to be a dependency here). This hits a
  // legacy, session-id-keyed history endpoint (GET /sessions/{sessionId}/
  // history, backed by an in-memory session_agents cache keyed by
  // f"{sessionId}:{model_id}" -- see backend/routers/session_routes.py and
  // agent_service.py) that's separate from the real, DynamoDB-backed
  // conversation history keyed by conversation_id (see agent_service.py's
  // session_key = f"{user_id}#{conversation_id}", which is what the agent
  // actually uses for context -- that part was always correct). selectedModel
  // changes on ANY model-picker pick AND on every conversation switch that
  // loads a conversation using a different model (handleConversationSelect's
  // own setSelectedModel) -- re-running this legacy fetch either way could
  // silently overwrite the just-loaded, correct conversation's messages
  // with unrelated leftover history from this browser tab's earlier use of
  // that same model. Capturing the model into a ref (read once, at mount)
  // instead of depending on the live value keeps this a true "once per
  // mount" effect, matching its own name, without that risk.
  const initialSelectedModelRef = useRef(selectedModel);
  useEffect(() => {
    const loadSessionHistory = async () => {
      setIsLoadingHistory(true);
      // console.log(`Loading session history for: ${sessionId} with model: ${initialSelectedModelRef.current}`);

      try {
        const apiBase = getApiBaseUrl();
        const url = `${apiBase}/sessions/${sessionId}/history?model_id=${initialSelectedModelRef.current}`;
        // console.log(`Fetching from: ${url}`);
        
        const response = await authenticatedFetch(url);
        // console.log(`Response status: ${response.status}`);
        
        if (response.ok) {
          const data = await response.json();
          // console.log('Session history response:', data);
          
          if (data.exists && data.messages && data.messages.length > 0) {
            setMessages(data.messages);

            // Restore website preview panel from loaded messages
            if (setWebsitePreview) {
              const lastPreview = data.messages
                .filter(m => m.additional_data?.data_type === 'website_preview')
                .pop();
              if (lastPreview) {
                setWebsitePreview({
                  project_id: lastPreview.additional_data.project_id,
                  url: lastPreview.additional_data.url,
                  files: lastPreview.additional_data.files,
                  refreshKey: Date.now(),
                });
              }
            }
          } else {
            // console.log('No messages found or session does not exist');
            // if (data.exists === false) {
            //   console.log('Session does not exist on backend');
            // } else if (!data.messages) {
            //   console.log('No messages property in response');
            // } else if (data.messages.length === 0) {
            //   console.log('Messages array is empty');
            // }
          }
        } else {
          console.error(`Failed to fetch session history: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error loading session history:', error);
      } finally {
        // Ensure loading is visible for at least 500ms
        setTimeout(() => {
          setIsLoadingHistory(false);
        }, 500);
      }
    };

    loadSessionHistory();
    focusInput();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading) {
      focusInput();
    }
  }, [isLoading, focusInput]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // ESC — cancel dictation while listening, otherwise clear/focus input
      if (e.key === 'Escape') {
        e.preventDefault();
        if (isListening) {
          void stopListening().then(() => focusInput());
          return;
        }
        // Only one of these is ever mounted at a time -- see richInputRef.
        const el = inputRef.current || richInputRef.current;
        if (el) {
          if (inputMessage.trim()) {
            setInputMessage('');
            // No-op for the rich (contentEditable) box -- it has no manual
            // height style to reset, it grows/shrinks via CSS. Clearing its
            // actual text is handled by the "sync text" effect reacting to
            // the setInputMessage('') above.
            el.style.height = 'auto';
          }
          el.focus();
        }
        return;
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
      document.removeEventListener('paste', handlePaste);
    };
  }, [inputMessage, handlePaste, isListening, stopListening, focusInput]);

  // Auto-resize textarea on mount and when inputMessage changes
  useEffect(() => {
    if (inputRef.current) {
      autoResizeTextarea(inputRef.current);
    }
  }, [inputMessage]);

  // Track screen size for responsive placeholder
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const stopGeneration = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsLoading(false);
      
      // Mark last message as stopped
      setMessages(prev => prev.map((msg, index) => 
        index === prev.length - 1 && msg.isStreaming
          ? { ...msg, isStreaming: false, content: msg.content + ' [Stopped]' }
          : msg
      ));
      
      focusInput();
    }
  };

  const sendMessage = useCallback(async (retryPayload = null) => {
    // retryPayload: { message, images, files } — used by the Retry button to
    // resend a historical user turn without requiring the original content
    // to still be in the input box. Leaves the input/selection UI untouched.
    const isRetry = retryPayload !== null;
    const outgoingMessage = isRetry ? (retryPayload.message || '') : inputMessage;
    const outgoingImages = isRetry ? (retryPayload.images || []) : selectedImages;
    const outgoingFiles = isRetry ? (retryPayload.files || []) : selectedFiles;

    if ((!outgoingMessage.trim() && outgoingImages.length === 0 && outgoingFiles.length === 0) || isLoading || (!isRetry && uploadingFiles.some((f) => !f.toKnowledgeBase))) return;

    // A retry resends a historical turn as-is, so it must reuse *that* turn's
    // original quote (if any), not the current composer's -- which sendMessage
    // clears before this even runs. Quick-options answers (also sent via
    // retryPayload) never had a quote to begin with, so they fall through to
    // null the same way.
    const outgoingReplyContext = isRetry ? (retryPayload.replyContext || null) : replyContext;
    // "@" picks made since the last question go with this one (see
    // mentionDraft); a resend carries the ones its question went with.
    // Applying them twice changes nothing, so a resend of a question that
    // did get as far as applying them is harmless.
    const draft = mentionDraftRef.current;
    const outgoingPicks = isRetry
      ? (retryPayload.knowledgeScopePicks || null)
      : (activeConversation && draft?.conversationId === activeConversation.conversation_id ? draft.picks : null);
    // The conversation this turn belongs to. A brand-new chat has none until
    // the backend's `conversation_created` names it.
    let requestConversationId = activeConversation?.conversation_id || null;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      // Pairs this question with KAI's reply below, so a group chat's poll
      // can swap both for the stored copies once they arrive (see
      // GroupChat.js's mergeGroupMessages).
      localTurnId: Date.now(),
      type: 'content',
      content: outgoingMessage,
      images: outgoingImages.map(img => ({
        url: img.url,
        name: img.name
      })),
      files: outgoingFiles.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
        tokens: file.tokens
      })),
      // Client-side only, for this session's rendering -- not sent to the
      // backend as part of the message (see quoted_context on requestBody
      // below) and not persisted, so it won't survive a reload. See the
      // "Known limitation" note in .claude/plan/chatreply.md.
      replyContext: outgoingReplyContext,
      // For a resend of this question. Client-side only, like replyContext.
      knowledgeScopePicks: outgoingPicks,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    if (!isRetry) {
      setInputMessage('');
      setSelectedImages([]);
      setSelectedFiles([]);
      setReplyContext(null);
      mentionDraftRef.current = null;
      setMentionDraft(null);
    }
    
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    
    setIsLoading(true);

    // Create initial AI message for streaming
    const aiMessageId = Date.now() + 1;
    const aiMessage = {
      id: aiMessageId,
      role: 'assistant',
      localTurnId: userMessage.localTurnId,
      content: '',
      timestamp: new Date().toISOString(),
      model: selectedModel,
      isStreaming: true
    };

    setMessages(prev => [...prev, aiMessage]);

    // Create abort controller
    const controller = new AbortController();
    setAbortController(controller);

    try {
      const apiBase = getApiBaseUrl();
      
      // Prepare request body with images
      const requestBody = {
        message: outgoingMessage,
        model_id: selectedModel,
        session_id: sessionId,
        focus_mode: selectedFocusMode || 'default',
        active_rule_ids: attachedRuleIds,
      };

      // Select-to-reply quote for this turn only (see .claude/plan/chatreply.md)
      if (outgoingReplyContext) {
        requestBody.quoted_context = {
          text: outgoingReplyContext.text,
          message_id: outgoingReplyContext.messageId,
          role: outgoingReplyContext.role,
          is_full_message: outgoingReplyContext.isFullMessage,
        };
      }

      // Add conversation_id if active conversation exists
      if (activeConversation) {
        requestBody.conversation_id = activeConversation.conversation_id;
        if (outgoingPicks?.length) requestBody.knowledge_scope_change = { picks: outgoingPicks };
      } else if (pendingProjectForNextConversationRef.current) {
        // "New conversation in this project" (or the just-created-project
        // flow) -- the conversation the backend is about to auto-create for
        // this turn should be filed into the project from the moment it's
        // created, not after the fact via a separate PATCH once the stream
        // finishes. That post-hoc move left a real gap: the FIRST turn always
        // ran with no project context (no rules, no file library) because the
        // conversation row didn't have project_id yet when this request's own
        // agent was built. Sending it up front closes that race entirely.
        // Read via the ref (kept in sync by the effect near its declaration),
        // not the prop directly -- sendMessage is a memoized callback and
        // referencing the prop here would need it in the dependency array,
        // rebuilding this large callback on every parent render.
        requestBody.new_conversation_project_id = pendingProjectForNextConversationRef.current.project_id;
        if (pendingKnowledgeScopeRef.current) {
          requestBody.new_conversation_knowledge_scope = pendingKnowledgeScopeRef.current;
        }
      }

      // Add images if any
      if (outgoingImages.length > 0) {
        requestBody.images = outgoingImages.map(img => ({
          data: img.url,
          name: img.name
        }));
      }

      // Add files if any
      if (outgoingFiles.length > 0) {
        requestBody.files = outgoingFiles.map(file => ({
          name: file.name,
          content: file.content,
          size: file.size,
          type: file.type
        }));
      }

      // Lets the backend look up any .docx template profile(s) cached under this
      // conversation (see docxTemplateTokenRef above) so KAI can match an
      // uploaded document's formatting on a later turn without the file being
      // re-attached and without this request carrying the profile itself --
      // just the short opaque token, on every turn, attachment or not.
      requestBody.docx_page_token = docxTemplateTokenRef.current;

      // Only on turns that could connect a SharePoint source -- this message
      // or the recent conversation carries a link: a short-lived Graph token
      // for THIS user, so the backend can check they can open the file before
      // connecting it as a live data source. No unrelated conversation pays
      // for it. A resend from "Confirm sign-in" brings the token it just got,
      // which is sent whether or not a link is in sight.
      const suppliedGraphToken = isRetry ? retryPayload.graphToken : null;
      if (suppliedGraphToken) {
        requestBody.graph_token = suppliedGraphToken;
      } else if (turnMayConnectSharePoint(outgoingMessage, messagesRef.current)) {
        let graphToken = await getGraphFileToken();
        // Silent fails once Microsoft's 24-hour refresh token has lapsed. Ask
        // Microsoft's window now, while the Send click or Enter still counts
        // as a user gesture: nothing above awaits except the silent attempt,
        // which fails fast (it never tries the slow hidden iframe). With the
        // person still signed in to Microsoft the window closes on its own.
        if (!graphToken && !graphPopupDeclinedRef.current) {
          graphToken = await getGraphFileToken({ interactive: true });
          if (!graphToken) {
            graphPopupDeclinedRef.current = true;
          }
        }
        if (graphToken) {
          requestBody.graph_token = graphToken;
        }
      }

      let messageTokens = null;
      // One request's worth of the stream. A group chat's question can
      // outlast a request while it waits for its turn: the server then ends
      // the stream with group_requeue, and the loop below takes the same
      // question up again by its pending_id.
      const streamOnce = async (body) => {
        let requeuePendingId = null;
        const response = await authenticatedFetch(`${apiBase}/chat`, {
          method: 'POST',
          headers: {
            'Accept': 'text/event-stream',
          },
          body: JSON.stringify(body),
          signal: controller.signal
        });

        if (!response.ok) {
          const httpError = new Error(`HTTP error! status: ${response.status}`);
          httpError.status = response.status;
          // A group chat says why it refused (a large file only you can
          // read, KAI busy with a change) -- shown instead of the generic text.
          if (activeConversation?.is_group) {
            const data = await response.json().catch(() => ({}));
            if (typeof data.detail === 'string') httpError.detail = data.detail;
          }
          throw httpError;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedContent = '';
        let accumulatedData = ''; // Buffer for incomplete streaming data

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          accumulatedData += chunk; // Add to buffer
        
          // Process complete lines
          const lines = accumulatedData.split('\n');
          accumulatedData = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                break;
              }

              try {
                const parsed = JSON.parse(data);
              
              
                if (parsed.type === 'content') {
                  accumulatedContent += parsed.content;
                  // Snapshot to a const so the closure passed to setMessages
                  // captures a value that's stable for this loop iteration
                  // (satisfies no-loop-func and avoids closure-over-let bugs).
                  const contentSnapshot = accumulatedContent;
                  setMessages(prev => prev.map(msg =>
                    msg.id === aiMessageId
                      ? {
                          ...msg,
                          content: contentSnapshot,
                          // Clear the live progress + tool-use shimmer lines
                          // once the orchestrator's real reply starts streaming
                          // so the final bubble is clean.
                          progressStage: null,
                          progressText: null,
                          toolUseText: null,
                        }
                      : msg
                  ));
                } else if (parsed.type === 'progress') {
                  // Live status line during long-running specialized agent calls
                  // (e.g. vibecoder builds). Distinct from 'content' so we can
                  // style it dimmed and replace it once the orchestrator's real
                  // reply starts streaming.
                  setMessages(prev => prev.map(msg =>
                    msg.id === aiMessageId
                      ? {
                          ...msg,
                          progressStage: parsed.stage,
                          progressText: parsed.text,
                        }
                      : msg
                  ));
                } else if (parsed.type === 'recoverable_error') {
                  // Backend streamed a tool-pair mismatch / interrupted turn.
                  // Flag the assistant message as an error + offer retry.
                  setMessages(prev => prev.map(msg =>
                    msg.id === aiMessageId
                      ? {
                          ...msg,
                          isError: true,
                          isRecoverable: true,
                          isStreaming: false,
                          recoveryMarker: parsed.marker,
                        }
                      : msg
                  ));
                } else if (parsed.type === 'tool_use') {
                  // Live "Using tool: ..." indicator rendered as a shimmer line
                  // below the message content (same pattern as progressText).
                  // Kept OUT of the message's `content` so the final bubble
                  // stays clean once the tool result arrives.
                  const toolInfo = parsed.input ? ` (${JSON.stringify(parsed.input).slice(0, 50)}...)` : '';
                  const toolName = parsed.tool_name.split('_').join(' ').slice(0, 30);
                  setMessages(prev => prev.map(msg =>
                    msg.id === aiMessageId
                      ? { ...msg, toolUseText: `Using tool: ${toolName}${toolInfo}` }
                      : msg
                  ));
                } else if (parsed.type === 'tool_result') {
                  // Tool result received — append to message content and clear
                  // the live tool-use shimmer line (the result text supersedes it).
                  const resultInfo = parsed.result;
                  accumulatedContent += `${resultInfo}\n\n`;
                  const toolResultSnapshot = accumulatedContent;
                  setMessages(prev => prev.map(msg =>
                    msg.id === aiMessageId
                      ? { ...msg, content: toolResultSnapshot, toolUseText: null }
                      : msg
                  ));
                } else if (parsed.type === 'image') {
                  // Handle image from event_loop_metrics
                  const imageContentSnapshot = accumulatedContent;
                  setMessages(prev => prev.map(msg =>
                    msg.id === aiMessageId
                      ? {
                          ...msg,
                          content: imageContentSnapshot + `\n\n![Generated Image](${parsed.url})`,
                          images: [...(msg.images || []), { url: parsed.url, filename: parsed.filename }]
                        }
                      : msg
                  ));
                } else if (parsed.type === 'metrics') {
                  // Handle metrics information
                  // console.log('Received metrics:', parsed.data);
                } else if (parsed.type === 'instructions_change') {
                  // The project's instructions changed since this
                  // conversation's last answer: the note goes above the
                  // message just sent (the backend saves it with it too).
                  const instructionsChange = parsed.instructions_change;
                  setMessages(prev => prev.map(msg =>
                    msg.id === userMessage.id ? { ...msg, instructions_change: instructionsChange } : msg
                  ));
                } else if (parsed.type === 'knowledge_scope_changed') {
                  // This question's "@" picks, applied as it started. The
                  // Knowledge base tab and the "@" list follow the new
                  // selection. Its note goes above the question -- except
                  // in a group chat, where the stored one arrives with
                  // everyone's next poll.
                  const scopeNote = parsed.note;
                  window.dispatchEvent(new CustomEvent(KNOWLEDGE_SCOPE_UPDATED_EVENT, {
                    detail: { conversation_id: requestConversationId, knowledge_scope: parsed.knowledge_scope || null },
                  }));
                  if (scopeNote && !activeConversation?.is_group) {
                    setMessages(prev => insertBefore(prev, userMessage.id, {
                      id: `${userMessage.id}-scope`,
                      role: 'assistant',
                      content: scopeNote.text,
                      timestamp: new Date().toISOString(),
                      additional_data: scopeNote.additional_data,
                    }));
                  }
                } else if (parsed.type === 'knowledge_scope_change_failed') {
                  setMessages(prev => insertBefore(prev, userMessage.id, {
                    id: `${userMessage.id}-scope`,
                    role: 'assistant',
                    content: "Couldn't apply the files you picked with @. This answer used the files already selected.",
                    timestamp: new Date().toISOString(),
                    additional_data: { type: 'knowledge_picks_not_applied' },
                  }));
                } else if (parsed.type === 'focus_mode_switch') {
                  // This turn runs in a different focus mode from the last one:
                  // the divider goes above the message just sent (the backend
                  // also saves it onto that message, for a reload).
                  const focusModeSwitch = parsed.focus_mode_switch;
                  setMessages(prev => prev.map(msg =>
                    msg.id === userMessage.id ? { ...msg, focusModeSwitch } : msg
                  ));
                } else if (parsed.type === 'additional_data') {
                  // Handle additional data (like images from kpf_data_search_images_tool)
                  setMessages(prev => prev.map(msg =>
                    msg.id === aiMessageId
                      ? {
                          ...msg,
                          additional_data: parsed.additional_data
                        }
                      : msg
                  ));
                  // Push website preview to side panel
                  if (parsed.additional_data?.data_type === 'website_preview' && setWebsitePreview) {
                    setWebsitePreview({
                      project_id: parsed.additional_data.project_id,
                      url: parsed.additional_data.url,
                      files: parsed.additional_data.files,
                      refreshKey: Date.now(),
                    });
                  }
                } else if (parsed.type === 'additional_data_final') {
                  // Handle final additional data sent after streaming completes
                  setMessages(prev => prev.map(msg =>
                    msg.id === aiMessageId
                      ? {
                          ...msg,
                          additional_data: parsed.additional_data,
                          isStreaming: false  // Force streaming to stop when final additional_data arrives
                        }
                      : msg
                  ));
                  // Push website preview to side panel
                  if (parsed.additional_data?.data_type === 'website_preview' && setWebsitePreview) {
                    setWebsitePreview({
                      project_id: parsed.additional_data.project_id,
                      url: parsed.additional_data.url,
                      files: parsed.additional_data.files,
                      refreshKey: Date.now(),
                    });
                  }
                } else if (parsed.type === 'conversation_created') {
                  // Handle auto-created conversation
                  if (parsed.conversation_id && setActiveConversation) {
                    // "New chat in project" (or the just-created-project flow)
                    // left a one-shot pending project here; the request that
                    // triggered this very auto-create already sent it along as
                    // new_conversation_project_id (see sendMessage above), so
                    // the conversation is ALREADY filed into it server-side by
                    // the time this event arrives -- this just mirrors that into
                    // local state, it does not decide anything. Consumed (not
                    // just read) so a second, unrelated conversation later in
                    // the session doesn't inherit it.
                    const pendingProject = pendingProjectForNextConversationRef.current;
                    pendingProjectForNextConversationRef.current = null;
                    const pendingScope = pendingKnowledgeScopeRef.current;
                    pendingKnowledgeScopeRef.current = null;

                    // Create a conversation object with the LLM-generated name
                    const conversation = {
                      conversation_id: parsed.conversation_id,
                      name: parsed.conversation_name || `New Chat ${new Date().toLocaleString()}`,
                      model_id: selectedModel,
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                      message_count: 1,
                      project_id: pendingProject?.project_id || null,
                      knowledge_scope: pendingProject?.project_id ? pendingScope : null,
                    };
                    // Tell the rule-hydration effect this id is an auto-create,
                    // not a conversation switch, so it leaves the selection the
                    // user made before this first message alone. Must be set
                    // BEFORE setActiveConversation, which is what triggers it.
                    justAutoCreatedConversationIdRef.current = parsed.conversation_id;
                    setActiveConversation(conversation);
                    // Set now, not when the effect catches up: a sign-in event
                    // later in this same chunk belongs to this conversation.
                    requestConversationId = parsed.conversation_id;
                    activeConversationIdRef.current = parsed.conversation_id;
                    // console.log(`Auto-created conversation: "${conversation.name}" (${conversation.conversation_id})`);
                    if (pendingProject) {
                      onProjectConversationFiledRef.current?.();
                    }
                  }
                } else if (parsed.type === 'group_queued') {
                  // A group chat: KAI is answering someone else first. The
                  // question stays on screen, marked as waiting.
                  const queue = { ahead: parsed.ahead, holderName: parsed.holder_name };
                  const queuedPendingId = parsed.pending_id;
                  setMessages(prev => prev.map(msg => {
                    if (msg.id === userMessage.id) return { ...msg, pendingId: queuedPendingId, groupQueue: queue };
                    if (msg.id === aiMessageId) return { ...msg, awaitingTurn: true };
                    return msg;
                  }));
                } else if (parsed.type === 'group_turn_started') {
                  const startedPendingId = parsed.pending_id;
                  setMessages(prev => prev.map(msg => {
                    if (msg.id === userMessage.id) return { ...msg, pendingId: startedPendingId, groupQueue: null };
                    if (msg.id === aiMessageId) return { ...msg, awaitingTurn: false };
                    return msg;
                  }));
                } else if (parsed.type === 'group_requeue') {
                  // The wait outlasted one request: take the same question up
                  // again (see streamOnce's caller below).
                  requeuePendingId = parsed.pending_id;
                } else if (parsed.type === 'group_not_sent' || parsed.type === 'group_cancelled') {
                  const cancelled = parsed.type === 'group_cancelled';
                  setMessages(prev => prev
                    .filter(msg => msg.id !== aiMessageId && !(cancelled && msg.id === userMessage.id))
                    .map(msg => (msg.id === userMessage.id
                      ? { ...msg, groupQueue: null, notSent: parsed.reason === 'continued' ? 'continued' : true }
                      : msg)));
                } else if (parsed.type === 'graph_sign_in_required') {
                  // A SharePoint connect was refused for want of a Graph token
                  // (see flag_graph_sign_in_needed). Offer the button that gets
                  // one and resends exactly this turn -- unless the user has
                  // moved to another conversation since, which this turn's
                  // message and attachments must not be resent into.
                  if (requestConversationId === activeConversationIdRef.current) {
                    setGraphSignIn({
                      phase: 'needed',
                      conversationId: requestConversationId,
                      resend: {
                        message: outgoingMessage,
                        images: outgoingImages,
                        files: outgoingFiles,
                        replyContext: outgoingReplyContext,
                        knowledgeScopePicks: outgoingPicks,
                      },
                      error: null,
                    });
                  }
                } else if (parsed.type === 'debug') {
                  // Handle debug messages from backend (silently ignore in production)
                  // Debug messages are only for development debugging
                } else if (parsed.type === 'tokens') {
                  messageTokens = parsed;
                  setTotalTokens(prev => ({
                    input: prev.input + parsed.input,
                    output: prev.output + parsed.output,
                    total: prev.total + parsed.total
                  }));
                  setTokenCount(prev => prev + parsed.total);
                  if (parsed.cumulative_input !== undefined && parsed.context_window) {
                    const used = parsed.cumulative_input + parsed.cumulative_output;
                    const limit = parsed.context_window;
                    setContextUsage({
                      used,
                      limit,
                      percentage: Math.round((used / limit) * 100)
                    });
                  }
                }
              } catch (e) {
                // Ignore JSON parse errors for partial chunks
              }
            }
          }
        }
        return requeuePendingId;
      };
      let resumePendingId = await streamOnce(requestBody);
      while (resumePendingId) {
        // eslint-disable-next-line no-await-in-loop
        resumePendingId = await streamOnce({ ...requestBody, pending_id: resumePendingId });
      }

      // Mark streaming as complete
      setMessages(prev => prev.map(msg =>
        msg.id === aiMessageId
          ? { ...msg, isStreaming: false, tokens: messageTokens, renderKey: Date.now() }
          : msg
      ));

      // Call onMessageSent callback for successful message
      if (onMessageSent) {
        onMessageSent();
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        // console.log('Request aborted');
        return;
      }
      
      console.error('Error sending message to backend:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: error.detail || getFriendlyErrorMessage(error),
        timestamp: new Date().toISOString(),
        model: selectedModel,
        isError: true,
        isRecoverable: true
      };
      setMessages(prev => [...prev.slice(0, -1), errorMessage]);
    } finally {
      setIsLoading(false);
      setAbortController(null);
      focusInput();
    }
  }, [
    inputMessage,
    selectedImages,
    selectedFiles,
    uploadingFiles,
    isLoading,
    selectedModel,
    sessionId,
    selectedFocusMode,
    attachedRuleIds,
    replyContext,
    activeConversation,
    setMessages,
    setActiveConversation,
    setWebsitePreview,
    setTokenCount,
    onMessageSent,
    focusInput,
  ]);

  // Actually send the message the user already typed into ProjectView.js's
  // (or the project sidebar's) composer before App.js remounted this
  // component fresh, scoped to that project -- see App.js's
  // handleStartConversationInProject / pendingProjectFirstMessage.
  // inputMessage's own useState initializer already seeded it above, so by
  // the time this effect runs on mount, sendMessage() reads the right text
  // straight off state; it only needs firing once, hence the ref guard,
  // which is what actually makes this safe under React 18 StrictMode's
  // dev-only double-invoke of mount effects (the ref survives that immediate
  // re-invoke, so the second call sees it already set and skips).
  const autoSentPendingFirstMessageRef = useRef(false);
  useEffect(() => {
    if (autoSentPendingFirstMessageRef.current) return;
    if (!pendingProjectFirstMessage || !pendingProjectForNextConversation || activeConversation) return;
    autoSentPendingFirstMessageRef.current = true;
    sendMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (e) => {
    // Enter without Shift - send message
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading && inputMessage.trim() && attachmentUploads.length === 0) {
        sendMessage();
      }
    }
    // Escape - clear input or focus
    else if (e.key === 'Escape') {
      e.preventDefault();
      if (inputMessage.trim()) {
        setInputMessage('');
        // Reset textarea height
        if (e.target) {
          e.target.style.height = 'auto';
        }
      }
      // Keep focus on input
      e.target.blur();
      setTimeout(() => e.target.focus(), 0);
    }
  };

  // Answer submitted from an in-chat quick-options card. Reuses sendMessage's
  // payload path (like Retry) so the composed answer is sent as a normal user
  // turn without touching the input box.
  // Through a ref: sendMessage changes with every keystroke (it reads the
  // composer's text), and this is one of the things each message's markdown
  // components are built from -- a new one each keystroke rebuilt them, so
  // every image, chart and table in the transcript was drawn afresh as you
  // typed, and the transcript jumped while they did.
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;
  const submitQuickOptions = useCallback((text) => {
    if (text && text.trim()) sendMessageRef.current({ message: text });
  }, []);

  // "Confirm sign-in": nothing may be awaited before getGraphFileToken, which
  // opens Microsoft's window synchronously -- browsers only allow that while
  // this click is still the thing driving the task. On success the refused
  // turn goes again carrying the new token itself: sendMessage only looks for
  // one when it spots a SharePoint link nearby, and the link that started
  // this may be further back than it looks.
  const confirmGraphSignIn = useCallback(async () => {
    const pending = graphSignIn;
    if (pending && pending.conversationId !== activeConversationIdRef.current) {
      setGraphSignIn(null);
      return;
    }
    const attempt = ++graphSignInAttemptRef.current;
    setGraphSignIn((prev) => prev && { ...prev, phase: 'busy', error: null });
    const token = await getGraphFileToken({ interactive: true });
    // The user moved to another conversation (or left) while Microsoft's
    // window was open; this turn belongs to the one they left.
    if (attempt !== graphSignInAttemptRef.current) return;
    if (!token) {
      setGraphSignIn((prev) => prev && {
        ...prev,
        phase: 'needed',
        error: "Microsoft's sign-in window was closed or blocked. Allow pop-ups for KAI, then try again.",
      });
      return;
    }
    graphPopupDeclinedRef.current = false;
    setGraphSignIn(null);
    if (pending?.resend && pending.conversationId === activeConversationIdRef.current) {
      sendMessage({ ...pending.resend, graphToken: token });
    }
  }, [graphSignIn, sendMessage]);

  const retryLastMessage = useCallback(async (errorMessageId) => {
    // Retries after a Bedrock ValidationException / interrupted tool turn:
    //   1. Call /conversations/{id}/repair which prunes dangling toolUse or
    //      excess toolResult blocks in DynamoDB and the in-memory agent.
    //   2. Find the last user turn preceding the error bubble (that's the
    //      prompt that failed to send) and resend it via sendMessage.
    //   3. Remove the error bubble AND the original user bubble from the UI
    //      so sendMessage can re-add a fresh pair.
    if (isLoading || retryingMessageId) return;
    if (!activeConversation?.conversation_id) {
      console.warn('Retry requires an active conversation');
      return;
    }

    let lastUserMessage = null;
    let errorIndex = -1;
    setMessages(prev => {
      errorIndex = prev.findIndex(m => m.id === errorMessageId);
      for (let i = errorIndex - 1; i >= 0; i -= 1) {
        if (prev[i].role === 'user') {
          lastUserMessage = prev[i];
          break;
        }
      }
      return prev;
    });

    if (!lastUserMessage) {
      console.warn('Retry: no preceding user message found');
      return;
    }

    setRetryingMessageId(errorMessageId);
    try {
      const apiBase = getApiBaseUrl();
      const response = await authenticatedFetch(
        `${apiBase}/conversations/${activeConversation.conversation_id}/repair`,
        { method: 'POST' }
      );
      if (!response.ok) {
        throw new Error(`Repair request failed: ${response.status}`);
      }
      const result = await response.json();
      console.log(`Repair removed ${result.removed_count} dangling message(s)`);

      // Drop the error bubble + original user bubble so sendMessage re-adds
      // a clean pair. Anything beyond the error bubble (e.g. a tokens event)
      // is also dropped.
      setMessages(prev => {
        const idx = prev.findIndex(m => m.id === errorMessageId);
        if (idx === -1) return prev;
        const userIdx = (() => {
          for (let i = idx - 1; i >= 0; i -= 1) {
            if (prev[i].role === 'user') return i;
          }
          return -1;
        })();
        if (userIdx === -1) {
          return [...prev.slice(0, idx)];
        }
        return [...prev.slice(0, userIdx)];
      });

      await sendMessage({
        message: lastUserMessage.content || '',
        images: [],
        files: [],
        replyContext: lastUserMessage.replyContext || null,
        knowledgeScopePicks: lastUserMessage.knowledgeScopePicks || null,
      });
    } catch (err) {
      console.error('Retry failed:', err);
      setMessages(prev => prev.map(m =>
        m.id === errorMessageId
          ? { ...m, content: `Retry failed: ${err.message}` }
          : m
      ));
    } finally {
      setRetryingMessageId(null);
    }
  }, [activeConversation, isLoading, retryingMessageId, sendMessage, setMessages]);

  // A group chat's mode may not be in this person's own list (see
  // withChatFocusMode) -- it still has a name.
  const getFocusModeName = () => focusModeDisplayName(
    withChatFocusMode(focusModes, groupChatFocusMode(activeConversation)), selectedFocusMode,
  );

  // Which Project this conversation belongs to, for the header badge. Shows
  // the pending project's name even before the first message actually creates
  // the conversation, so "start a new conversation in this project" gives
  // immediate feedback rather than only after sending something -- reading it
  // straight off pendingProjectForNextConversation rather than through
  // projectsById (as the activeConversation branch does) is deliberate: the
  // pending project object already has its name synchronously, with no
  // fetch-completion race to flash a blank badge during.
  const getActiveProjectName = () => {
    const pid = activeConversation?.project_id;
    if (pid) return projectsById[pid]?.name || null;
    if (!activeConversation && pendingProjectForNextConversation) {
      return pendingProjectForNextConversation.name || null;
    }
    return null;
  };

  // Same two branches as getActiveProjectName, but returns the full project
  // object -- used by the header breadcrumb to jump to the project's full
  // page view (App.js's ProjectView).
  const getActiveProject = () => {
    const pid = activeConversation?.project_id;
    if (pid) return projectsById[pid] || null;
    if (!activeConversation && pendingProjectForNextConversation) {
      return pendingProjectForNextConversation;
    }
    return null;
  };

  // The project's members, to invite someone new, told which chat it's from:
  // in a chat that's everyone's in the project, the invite email links
  // straight to it. Joining the project puts nobody in a chat limited to
  // chosen people: whoever can add people to one (its manager, or Edit
  // access) adds them once they've accepted.
  const inviteFromGroupChat = onManageProjectMembers && getActiveProject()
    ? () => onManageProjectMembers(projectForChatInvite(getActiveProject(), activeConversation, groupChat))
    : undefined;

  // Fetches the active project's file list for the "@" popover -- see
  // projectFilesById's own comment above for why this is eager (once per
  // project id) rather than gated on mentionQuery.
  //
  // The project id comes straight off the conversation (or the pending
  // project), NOT getActiveProject(): that reads projectsById, which loads
  // separately, and this effect doesn't re-run when it lands -- so opening a
  // project conversation before its name had loaded skipped the fetch for
  // good, leaving "@" on "Loading files…". Nor is a response ever dropped
  // as "cancelled": it is cached by project id, so it is still right after
  // the conversation id changes under it (the first message creating the
  // conversation used to cancel the one request in flight, with nothing
  // left to retry). An in-flight set stops duplicate requests instead.
  // Opening the popover retries a project whose files failed to load.
  const projectFilesInFlightRef = useRef(new Set());
  const mentionPopoverOpen = Boolean(mentionQuery);
  useEffect(() => {
    const pid = activeConversation
      ? activeConversation.project_id
      : pendingProjectForNextConversation?.project_id;
    if (!pid || projectFilesById[pid] || projectFilesInFlightRef.current.has(pid)) return;
    projectFilesInFlightRef.current.add(pid);
    setProjectFilesFailedIds((prev) => (prev.has(pid) ? new Set([...prev].filter((id) => id !== pid)) : prev));
    listProjectFiles(pid)
      .then((files) => {
        setProjectFilesById((prev) => ({ ...prev, [pid]: files }));
      })
      .catch((error) => {
        console.error('Error fetching project files for @ mention:', error);
        setProjectFilesFailedIds((prev) => new Set(prev).add(pid));
      })
      .finally(() => {
        projectFilesInFlightRef.current.delete(pid);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?.project_id, pendingProjectForNextConversation?.project_id, mentionPopoverOpen]);

  // Keeps the "@" popover's file cache above from going stale -- without
  // this, uploading/moving/deleting a file in the open project's Knowledge
  // base (via ProjectPanel, a sibling component) left projectFilesById
  // showing the pre-change list until this ChatInterface instance
  // remounted, since the effect above only ever fetches once per project
  // id. Refetches (rather than just invalidating) so the cache is already
  // warm the next time the popover opens. See PROJECT_FILES_UPDATED_EVENT.
  useEffect(() => {
    const onFilesUpdated = (e) => {
      const pid = e.detail?.project_id;
      if (!pid) return;
      listProjectFiles(pid)
        .then((files) => {
          setProjectFilesById((prev) => ({ ...prev, [pid]: files }));
          setProjectFilesFailedIds((prev) => (prev.has(pid) ? new Set([...prev].filter((id) => id !== pid)) : prev));
        })
        .catch((error) => {
          console.error('Error refreshing project files for @ mention:', error);
        });
    };
    window.addEventListener(PROJECT_FILES_UPDATED_EVENT, onFilesUpdated);
    return () => window.removeEventListener(PROJECT_FILES_UPDATED_EVENT, onFilesUpdated);
  }, []);

  // The knowledge-base scope currently in effect -- the active conversation's
  // own knowledge_scope once one exists, or the pending one App.js is holding
  // before the first message is sent. Same "no explicit selection = every
  // file" default as ProjectPanel.js's own savedScope. Used to show a
  // checkmark on a file the "@" popover's own list already covers.
  const currentKnowledgeScope = activeConversation
    ? scopeAfterPicks(
      activeConversation.knowledge_scope || null,
      mentionDraft?.conversationId === activeConversation.conversation_id ? mentionDraft.picks : [],
    )
    : (pendingKnowledgeScope || null);

  // The candidate list the "@" popover renders: a pinned "@all" entry (only
  // while it still matches whatever was typed), then every matching
  // Knowledge base folder (mention a whole folder to add every file filed
  // under it in one go, without needing to "@" each file individually),
  // then every matching individual file -- in the project's own list order.
  // Capped at 20 each -- a quick picker, not a full second copy of the
  // Files tab.
  const activeProjectForMention = getActiveProject();
  // A file already filed under a folder is reachable by "@"-ing that folder
  // (see mentionFolders below) -- listing it AGAIN here, flat, would let the
  // exact same file show up twice in the popover for one query. So the flat
  // list is unfiled files only, same split as ProjectPanel.js's own
  // unfiledFiles/fileFolders (there, folders are a separate section from
  // "Unfiled" for this same reason).
  const mentionFiles = activeProjectForMention
    ? (projectFilesById[activeProjectForMention.project_id] || []).filter((f) => !f.folder_id)
    : [];
  const mentionFolders = activeProjectForMention ? (activeProjectForMention.file_folders || []) : [];
  // Every string an "@" can match as a whole, complete mention right now --
  // shared between the highlight backdrop, findMentionBeforeCaret's own
  // atomic-Backspace check, and the popover's candidate list below, so all
  // three always agree on what counts as "a real mention." Longest name
  // first so a name that happens to prefix a longer one never wins by
  // accident (see buildMentionHighlightSegments/findMentionBeforeCaret).
  const highlightNames = activeProjectForMention
    ? ['all', ...mentionFolders.map((f) => f.name), ...mentionFiles.map((f) => f.name)].sort((a, b) => b.length - a.length)
    : [];
  const mentionQueryText = (mentionQuery?.text || '').trim().toLowerCase();
  const mentionShowAll = 'all'.includes(mentionQueryText);
  const mentionMatchedFolders = mentionQueryText
    ? mentionFolders.filter((f) => f.name.toLowerCase().includes(mentionQueryText))
    : mentionFolders;
  const mentionMatchedFiles = mentionQueryText
    ? mentionFiles.filter((f) => f.name.toLowerCase().includes(mentionQueryText))
    : mentionFiles;
  const mentionCandidates = [
    ...(mentionShowAll ? [{ kind: 'all' }] : []),
    ...mentionMatchedFolders.slice(0, 20).map((f) => ({ kind: 'folder', folder: f })),
    ...mentionMatchedFiles.slice(0, 20).map((f) => ({ kind: 'file', file: f })),
  ];

  // Keeps the keyboard-highlighted row in bounds as the candidate list
  // itself changes out from under it (e.g. a keystroke narrows 8 matches
  // down to 2) -- without this, Up/Down/Enter could target a row that no
  // longer exists.
  useEffect(() => {
    if (!mentionQuery) return;
    setMentionActiveIndex((i) => Math.min(i, Math.max(mentionCandidates.length - 1, 0)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionQuery?.text, mentionCandidates.length]);

  // Before a conversation exists, "@" picks go straight into the pending
  // selection App.js holds (and ProjectPanel shows), which the very first
  // message is sent with. In an existing conversation they wait as unsent
  // picks instead -- see mentionDraft.
  const applyMentionScopeChange = useCallback((computeNext) => {
    // Optimistic update of the ref (not just once onPendingKnowledgeScopeChange's
    // prop round-trips back down through App.js's own effect), so a second
    // mention picked before that round-trip completes still computes its
    // `next` from the first mention's change instead of the same stale base.
    const next = computeNext(pendingKnowledgeScopeRef.current);
    pendingKnowledgeScopeRef.current = next;
    onPendingKnowledgeScopeChange?.(next);
  }, [onPendingKnowledgeScopeChange]);

  // Replaces the typed "@query" with the chosen mention's plain-text tag,
  // moves the caret right after it (plus one trailing space, so typing right
  // away starts a new word instead of extending the mention), and updates
  // scope -- additively for a file (addFileToKnowledgeScope), or straight to
  // "every file" for "@all" (mirrors ProjectPanel's own Select-all, which
  // stores this same `null`).
  const selectMentionCandidate = useCallback((candidate) => {
    const query = mentionQuery;
    if (!query) return;
    const tag = candidate.kind === 'all'
      ? '@all'
      : candidate.kind === 'folder'
        ? `@${candidate.folder.name}`
        : `@${candidate.file.name}`;
    const typedBefore = inputMessage.slice(0, query.start);
    // Same overhang on the pill's LEFT side swallows a single space before
    // it, so the pill would visually touch the preceding word -- pad any
    // mid-line mention to two leading spaces too. Plain textarea only -- the
    // rich box's pill has a real margin instead of a cancelled one.
    //
    // Two spaces is only enough to clear ONE pill's worth of bleed. When the
    // token immediately before this new mention is ANOTHER completed mention
    // (typing "@a", picking it, then immediately typing "@b" right after),
    // there are two overhangs meeting in that gap -- the previous pill's own
    // right-side bleed AND this one's left-side bleed -- and two spaces isn't
    // wide enough to clear both, so the pills' rounded borders visually
    // overlap instead of reading as separate tags. Widen the requirement to
    // four spaces in that case only; ordinary typed text never trips this
    // (findMentionBeforeCaret only matches a real "@name" run, not a plain
    // word), so a mention that just follows text still gets the normal two.
    let before = typedBefore;
    if (inputRef.current && typedBefore !== '') {
      const trailingWs = typedBefore.match(/\s*$/)[0];
      if (!trailingWs.includes('\n')) {
        const beforeTrimmed = typedBefore.slice(0, typedBefore.length - trailingWs.length);
        const prevIsMention = !!findMentionBeforeCaret(beforeTrimmed, beforeTrimmed.length, highlightNames);
        const minGap = prevIsMention ? 4 : 2;
        if (trailingWs.length < minGap) before = `${beforeTrimmed}${' '.repeat(minGap)}`;
      }
    }
    const afterStart = query.start + 1 + query.text.length;
    const after = inputMessage.slice(afterStart);
    // Two trailing spaces, not one -- the plain composer's own pill look
    // (.message-input-mention) bleeds a few px past its own text via a
    // padding/negative-margin trick (see its own comment), which must net to
    // EXACTLY zero added width or every later character drifts out of
    // alignment with the real, invisible textarea underneath. That bleed
    // still visually PAINTS past the mention's true text boundary though --
    // with only one trailing space, the real (invisible) caret landed inside
    // that bled region right after inserting a mention, rendering as if it
    // were sitting on the pill instead of past it. A second space pushes the
    // real caret far enough right to clear it.
    const insertion = `${tag}  `;
    setInputMessage(`${before}${insertion}${after}`);
    setMentionQuery(null);
    const draftConversationId = activeConversation?.conversation_id;
    if (draftConversationId) {
      const pick = candidate.kind === 'all'
        ? { op: 'all' }
        : candidate.kind === 'folder'
          ? { op: 'add_folder', id: candidate.folder.folder_id }
          : { op: 'add_file', id: candidate.file.file_id };
      updateMentionDraft(draftConversationId, (picks) => [...picks, pick]);
    } else if (candidate.kind === 'all') {
      applyMentionScopeChange(() => null);
    } else if (candidate.kind === 'folder') {
      const folderId = candidate.folder.folder_id;
      // Only claim ownership (and so, later, permission to undo this on
      // Backspace) when this mention is actually what's about to add the
      // id -- addFolderToKnowledgeScope's own no-op case (already an
      // explicit member of a selected scope) means some OTHER selection
      // (ProjectPanel's checkboxes, an earlier mention) owns it instead.
      const alreadySelected = currentKnowledgeScope?.mode === 'selected'
        && (currentKnowledgeScope.folder_ids || []).includes(folderId);
      if (!alreadySelected) markMentionOwned('folder', folderId);
      applyMentionScopeChange((scope) => addFolderToKnowledgeScope(scope, folderId));
    } else {
      const fileId = candidate.file.file_id;
      const alreadySelected = currentKnowledgeScope?.mode === 'selected'
        && (currentKnowledgeScope.file_ids || []).includes(fileId);
      if (!alreadySelected) markMentionOwned('file', fileId);
      applyMentionScopeChange((scope) => addFileToKnowledgeScope(scope, fileId));
    }
    const caret = before.length + insertion.length;
    const el = inputRef.current;
    if (el) {
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(caret, caret);
        autoResizeTextarea(el);
        if (messageHighlightRef.current) messageHighlightRef.current.scrollTop = el.scrollTop;
      });
    } else if (richInputRef.current) {
      // The plain textarea isn't mounted -- a custom rule is attached, so
      // the rich contentEditable box is the active editor instead. Its own
      // "pull inputMessage into DOM" effect will rebuild this box's text
      // nodes right after the setInputMessage above lands; hand it the
      // offset to restore the caret to once it does (see
      // pendingRichCaretOffsetRef/setRichInputCaretTextOffset).
      pendingRichCaretOffsetRef.current = caret;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionQuery, inputMessage, applyMentionScopeChange, currentKnowledgeScope, highlightNames, activeConversation?.conversation_id, updateMentionDraft]);

  // Places the "@" popover off the composer itself (same self-flipping
  // approach as positionPlusMenu/positionModelMenu above). Reads ref.current
  // at call time rather than captured values, so an empty deps array is safe.
  const positionMentionMenu = useCallback(() => {
    const MARGIN = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const clamp = (v, min, max) => Math.max(min, Math.min(v, max));
    const anchor = inputRef.current || richInputRef.current;
    const menu = mentionMenuRef.current;
    if (!anchor || !menu) return;
    const b = anchor.getBoundingClientRect();
    menu.style.width = `${Math.min(320, vw - 2 * MARGIN)}px`;
    const mh = menu.offsetHeight;
    const spaceAbove = b.top - 8 - MARGIN;
    const spaceBelow = vh - b.bottom - 8 - MARGIN;
    const openDown = spaceAbove < mh && spaceBelow > spaceAbove;
    menu.style.top = `${openDown ? b.bottom + 8 : b.top - 8 - mh}px`;
    menu.style.left = `${clamp(b.left, MARGIN, vw - menu.offsetWidth - MARGIN)}px`;
  }, []);

  // Runs before paint, so the popover never flashes at its unpositioned spot.
  useLayoutEffect(() => {
    if (!mentionQuery) return undefined;
    positionMentionMenu();
    window.addEventListener('resize', positionMentionMenu);
    window.addEventListener('scroll', positionMentionMenu, true);
    return () => {
      window.removeEventListener('resize', positionMentionMenu);
      window.removeEventListener('scroll', positionMentionMenu, true);
    };
  }, [mentionQuery, positionMentionMenu, mentionCandidates.length]);

  // onChange's own scroll sync runs before React has rendered the new text
  // into the highlight backdrop, so its scrollTop gets clamped to the OLD,
  // shorter content -- once the textarea hits its max height, the newest
  // lines then never scroll into view in the backdrop (the only visible text).
  useLayoutEffect(() => {
    const hl = messageHighlightRef.current;
    const textarea = inputRef.current;
    if (hl && textarea) hl.scrollTop = textarea.scrollTop;
  }, [inputMessage]);

  // Closes the "@" popover on an outside click (mirrors closePlusMenu's own
  // pattern). Escape here only ever closes the popover (handled in
  // handleComposerKeyDown below), never also clearing the composer's text
  // the way the plain handleKeyDown's own Escape does -- that would be a
  // much bigger, surprising side effect of just dismissing a mention list.
  useEffect(() => {
    if (!mentionQuery) return undefined;
    const handleClickOutside = (event) => {
      const insideMenu = mentionMenuRef.current && mentionMenuRef.current.contains(event.target);
      const insideInput = (inputRef.current && inputRef.current.contains(event.target))
        || (richInputRef.current && richInputRef.current.contains(event.target));
      if (!insideMenu && !insideInput) setMentionQuery(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mentionQuery]);

  // Detects/updates the "@" trigger from the composer's current text + caret
  // position -- called from the plain textarea's onChange below. A valid
  // trigger is an "@" that starts at the beginning of the text or right
  // after whitespace (so "foo@bar" never fires), with no whitespace between
  // it and the caret (so the query is always a single in-progress word), and
  // only while a project (and so a knowledge base to scope) is active. Not
  // wrapped in useCallback -- it reads getActiveProject(), a plain function
  // recreated every render, so memoizing this against an empty dep array
  // would silently pin it to whichever project was active on first render.
  const updateMentionFromCaret = (value, caret) => {
    if (!getActiveProject()) {
      setMentionQuery(null);
      return;
    }
    const uptoCaret = value.slice(0, caret);
    const atIndex = uptoCaret.lastIndexOf('@');
    if (atIndex === -1) {
      setMentionQuery(null);
      return;
    }
    const query = uptoCaret.slice(atIndex + 1);
    if (/\s/.test(query)) {
      setMentionQuery(null);
      return;
    }
    const charBefore = atIndex === 0 ? '' : uptoCaret[atIndex - 1];
    if (atIndex !== 0 && !/\s/.test(charBefore)) {
      setMentionQuery(null);
      return;
    }
    setMentionQuery((prev) => (prev && prev.start === atIndex && prev.text === query ? prev : { start: atIndex, text: query }));
  };

  // Intercepts Up/Down/Enter/Tab/Escape while the "@" popover is open so they
  // navigate/select/dismiss it instead of doing their normal composer thing
  // (Enter sending the message, Escape clearing the box) -- falls through to
  // the plain handleKeyDown, untouched, once the popover is closed.
  const handleComposerKeyDown = (e) => {
    // Checked BEFORE the mentionQuery branch below, unconditionally -- not as
    // an `else if` gated on the popover being closed. A completed mention is
    // still a valid "@" trigger as far as updateMentionFromCaret is
    // concerned (it has no notion of "complete" vs. "in progress"), so once
    // a Backspace here has consumed a whole pill (see findMentionBeforeCaret,
    // which now eats the pill's own trailing padding along with it) any
    // further Backspace lands on ordinary preceding text -- an "@" it
    // uncovers there is a real, separate trigger the textarea's own onChange
    // needs to see. An `else if` would let THIS branch's own
    // `if (mentionQuery)` win on every subsequent keystroke instead,
    // permanently shadowing this check and silently falling back to
    // character-by-character deletion for the rest of the token.
    // e.target is the plain <textarea> (has a real selectionStart) while no
    // rule is attached, or the rich contentEditable box (no such property --
    // see getRichInputCaretTextOffset) once one is. Either way this resolves
    // to a caret position in the SAME coordinate space as inputMessage.
    const isTextareaTarget = typeof e.target.selectionStart === 'number';
    const caretCollapsed = isTextareaTarget
      ? e.target.selectionStart === e.target.selectionEnd
      : Boolean(window.getSelection()?.isCollapsed);
    const composerCaret = isTextareaTarget
      ? e.target.selectionStart
      : getRichInputCaretTextOffset(richInputRef.current);
    if (
      e.key === 'Backspace' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey
      && caretCollapsed && composerCaret !== null
    ) {
      // A completed "@mention" is atomic -- Backspace right at its end
      // deletes the whole pill (and un-mentions the file it added, mirroring
      // what selectMentionCandidate did) in one keystroke, not one character
      // at a time the way it would for ordinary typed text.
      const hit = findMentionBeforeCaret(inputMessage, composerCaret, highlightNames);
      if (hit) {
        e.preventDefault();
        const nextValue = inputMessage.slice(0, hit.start) + inputMessage.slice(hit.end);
        setInputMessage(nextValue);
        setMentionQuery(null); // whatever the popover was matching no longer exists
        const el = inputRef.current;
        if (el) {
          requestAnimationFrame(() => {
            el.setSelectionRange(hit.start, hit.start);
            autoResizeTextarea(el);
            if (messageHighlightRef.current) messageHighlightRef.current.scrollTop = el.scrollTop;
          });
        } else if (richInputRef.current) {
          // Same rich-input handoff as selectMentionCandidate -- see
          // pendingRichCaretOffsetRef.
          pendingRichCaretOffsetRef.current = hit.start;
        }
        const draftConversationId = activeConversation?.conversation_id;
        if (draftConversationId) {
          const removedFolder = hit.name === 'all' ? null : mentionFolders.find((f) => f.name === hit.name);
          const removedFile = hit.name === 'all' || removedFolder ? null : mentionFiles.find((f) => f.name === hit.name);
          if (hit.name === 'all') {
            updateMentionDraft(draftConversationId, (picks) => withoutPick(picks, 'all'));
          } else if (removedFolder) {
            updateMentionDraft(draftConversationId, (picks) => withoutPick(picks, 'add_folder', removedFolder.folder_id));
          } else if (removedFile) {
            updateMentionDraft(draftConversationId, (picks) => withoutPick(picks, 'add_file', removedFile.file_id));
          }
        } else if (hit.name === 'all') {
          applyMentionScopeChange(() => null);
        } else {
          const removedFolder = mentionFolders.find((f) => f.name === hit.name);
          if (removedFolder) {
            // Only undo the scope change if THIS mention was what made it --
            // see markMentionOwned/consumeMentionOwned. Otherwise the id was
            // already selected some other way, and backspacing the pill
            // should just remove the text, leaving that selection alone.
            if (consumeMentionOwned('folder', removedFolder.folder_id)) {
              applyMentionScopeChange((scope) => removeFolderFromKnowledgeScope(scope, removedFolder.folder_id));
            }
          } else {
            const removedFile = mentionFiles.find((f) => f.name === hit.name);
            if (removedFile && consumeMentionOwned('file', removedFile.file_id)) {
              applyMentionScopeChange((scope) => removeFileFromKnowledgeScope(scope, removedFile.file_id));
            }
          }
        }
        return;
      }
    }
    if (mentionQuery) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionActiveIndex((i) => Math.min(i + 1, Math.max(mentionCandidates.length - 1, 0)));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && mentionCandidates.length > 0) {
        e.preventDefault();
        selectMentionCandidate(mentionCandidates[Math.min(mentionActiveIndex, mentionCandidates.length - 1)]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionQuery(null);
        return;
      }
    }
    handleKeyDown(e);
  };

  // True for the one frame between mount and the auto-send effect's
  // sendMessage() call actually landing (see autoSentPendingFirstMessageRef
  // above) -- messages is still [] at that point, so without this the
  // generic "Ready to Supercharge Your Workflow?" focus-mode welcome screen
  // would flash on screen and immediately get replaced once the real
  // messages arrive. A lightweight spinner reads as "this is loading", not
  // as an unrelated screen the UI briefly detoured through.
  //
  // The render below ANDs this with messages.length === 0, which matters:
  // these three stay true until conversation_created arrives, so on its own
  // this would keep the spinner up over the user's own message for the whole
  // first request -- and hide the message and its error permanently if that
  // request failed before ever creating a conversation.
  const isAutoSendingProjectFirstMessage = Boolean(
    pendingProjectFirstMessage && pendingProjectForNextConversation && !activeConversation
  );

  // Auto-focuses the composer the instant a project's own empty "Start a
  // conversation" screen is showing (the exact same condition that renders
  // it below: not loading history, not mid-auto-send, and no messages yet).
  // A blinking cursor there is the clearest possible signal that this box
  // is where to type, instead of a user having to notice it and click in
  // themselves first. Deliberately scoped to a PROJECT's landing screen
  // only (getActiveProject()) -- the plain, non-project welcome screen
  // doesn't get this, since stealing focus there on every fresh chat would
  // be a much more noticeable/surprising change of established behavior.
  useEffect(() => {
    if (isLoadingHistory || isAutoSendingProjectFirstMessage || messages.length !== 0) return;
    if (!getActiveProject()) return;
    const el = inputRef.current || richInputRef.current;
    el?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingHistory, isAutoSendingProjectFirstMessage, messages.length]);

  const getCurrentFocusMode = () => {
    if (!focusModesLoaded || focusModes.length === 0) {
      return null;
    }
    const focusMode = focusModes.find(mode => mode.id === selectedFocusMode);
    if (!focusMode) {
      // Fallback to default if selected mode not found
      return focusModes.find(mode => mode.id === 'default');
    }
    return focusMode;
  };

  const getWelcomeTitle = () => {
    const focusMode = getCurrentFocusMode();
    if (!focusMode) {
      return "Ready to Supercharge Your Workflow?";
    }
    return focusMode.welcome_title || "Ready to Supercharge Your Workflow?";
  };

  const getWelcomeMessage = () => {
    const focusMode = getCurrentFocusMode();
    if (!focusMode) {
      return "Loading...";
    }
    return focusMode.welcome_message || "I'm your AI assistant powered by specialized tools! I can search KPF data & proposals, perform complex calculations & date operations, get real-time weather forecasts, and conduct web research to help you work smarter and faster.";
  };

  const getExamplePrompts = () => {
    const focusMode = getCurrentFocusMode();
    if (!focusMode) {
      return [];
    }
    return focusMode.example_prompts || [];
  };

  // Memoized so that MessageItem (which receives this callback) can stay
  // memoized across parent re-renders caused by input typing.
  const copyMessageToClipboard = useCallback(async (messageId) => {
    try {
      const contentEl = document.querySelector(`.message-content[data-message-id="${messageId}"]`);
      if (!contentEl) return;

      const html = contentEl.innerHTML;
      const plainText = contentEl.innerText;

      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
          })
        ]);
      } catch {
        await navigator.clipboard.writeText(plainText);
      }

      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  }, []);

  // Select-to-reply (see .claude/plan/chatreply.md): quoting an entire
  // message via the message-footer button.
  const handleQuoteMessage = useCallback((message) => {
    setReplyContext({
      text: message.content,
      messageId: message.id,
      role: message.role,
      isFullMessage: true,
    });
    setSelectionPopup(null);
    window.getSelection()?.removeAllRanges();
    focusInput();
  }, [focusInput]);

  // Select-to-reply: a floating "Reply" button appears near an in-progress
  // text selection made inside a message's content (data-message-id lives on
  // the same .message-content div copyMessageToClipboard above already
  // queries by that attribute). A selection can also span multiple messages
  // (e.g. dragging from KAI's reply into your own next message) -- in that
  // case selection.toString() already stitches the in-between text together
  // (browsers insert a line break between block elements), so it's used as
  // the quote as-is; messageId/role are left null since there's no single
  // message or speaker to attribute a multi-message quote to.
  useEffect(() => {
    if (sharedConversation) return; // read-only view; nothing to reply into

    const clearPopup = () => setSelectionPopup(null);

    const handleMouseUp = () => {
      const selection = window.getSelection();
      const text = selection && selection.toString().trim();
      if (!selection || !text || selection.isCollapsed || selection.rangeCount === 0) {
        clearPopup();
        return;
      }

      const anchorEl = selection.anchorNode?.nodeType === 1
        ? selection.anchorNode
        : selection.anchorNode?.parentElement;
      const focusEl = selection.focusNode?.nodeType === 1
        ? selection.focusNode
        : selection.focusNode?.parentElement;
      const anchorMessage = anchorEl?.closest('[data-message-id]');
      const focusMessage = focusEl?.closest('[data-message-id]');

      if (!anchorMessage || !focusMessage) {
        clearPopup();
        return;
      }

      const spansMultipleMessages = anchorMessage !== focusMessage;

      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        clearPopup();
        return;
      }

      // Clamp both edges -- an approximate popup width (it's ~80px: icon +
      // "Reply" label + padding) keeps a selection near the right edge of
      // the viewport from placing the popup partly off-screen.
      const approxPopupWidth = 90;
      const idealLeft = rect.left + rect.width / 2 - 40;
      const maxLeft = Math.max(8, window.innerWidth - approxPopupWidth);

      setSelectionPopup({
        text,
        messageId: spansMultipleMessages ? null : anchorMessage.getAttribute('data-message-id'),
        role: spansMultipleMessages ? null : anchorMessage.getAttribute('data-role'),
        top: Math.max(8, rect.top - 44),
        left: Math.min(Math.max(8, idealLeft), maxLeft),
      });
    };

    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        clearPopup();
      }
    };

    const container = messagesContainerRef.current;
    // pointerup (not mouseup) so a touch/pen drag-selection on mobile/tablet
    // also surfaces the Reply popup, not just a mouse. Listens on document,
    // not just the messages container -- a drag started inside a message can
    // still be released over the composer or anywhere else in the page, and
    // handleMouseUp only ever acts on the selection's anchor/focus nodes, not
    // the event target, so it's safe regardless of where the pointer went up.
    const upEvent = window.PointerEvent ? 'pointerup' : 'mouseup';
    document.addEventListener(upEvent, handleMouseUp);
    document.addEventListener('selectionchange', handleSelectionChange);
    container?.addEventListener('scroll', clearPopup);
    window.addEventListener('resize', clearPopup);

    return () => {
      document.removeEventListener(upEvent, handleMouseUp);
      document.removeEventListener('selectionchange', handleSelectionChange);
      container?.removeEventListener('scroll', clearPopup);
      window.removeEventListener('resize', clearPopup);
    };
  }, [sharedConversation]);

  const handleReplyToSelection = useCallback(() => {
    if (!selectionPopup) return;
    setReplyContext({
      text: selectionPopup.text,
      messageId: selectionPopup.messageId,
      role: selectionPopup.role,
      isFullMessage: false,
    });
    setSelectionPopup(null);
    window.getSelection()?.removeAllRanges();
    focusInput();
  }, [selectionPopup, focusInput]);

  // Filter out tool_use / tool_result messages once per messages change
  // instead of on every keystroke.
  // In a group chat, our own unfinished turn waits below the question KAI
  // is answering, in queue order, instead of in the transcript above it
  // (see heldTurnIds) -- it joins the transcript, at the end, once it's done.
  const heldIds = useMemo(
    () => (groupChat ? heldTurnIds(messages, groupActivity.pending, localPendingIds) : null),
    [groupChat, messages, groupActivity.pending, localPendingIds],
  );
  const isHeld = useCallback((m) => Boolean(heldIds?.size && m.localTurnId && heldIds.has(m.localTurnId)), [heldIds]);
  const visibleMessages = useMemo(
    () => withoutOpeningNotes(messages.filter(m => !(m.type === 'tool_use' || m.type === 'tool_result') && !isHeld(m))),
    [messages, isHeld]
  );
  const heldMessages = useMemo(
    () => messages.filter((m) => isHeld(m) && !(m.type === 'tool_use' || m.type === 'tool_result')),
    [messages, isHeld],
  );

  // In a group chat, whose question each of KAI's answers is for -- aligned
  // with visibleMessages ("answering Priya").
  const answeringNames = useMemo(() => {
    if (!groupChat) return null;
    let asker = null;
    return visibleMessages.map((m) => {
      if (m.role === 'user') {
        const who = authorOf(m, groupChat);
        asker = who.isMe ? 'you' : who.name;
        return null;
      }
      return asker;
    });
  }, [visibleMessages, groupChat]);

  // A group chat just opened with messages the reader hadn't read: an
  // "Unread messages" line above the first of them, and the chat opens
  // there rather than at the bottom. The server counted them as it marked
  // the chat read (group.unread_count) -- questions and answers, the
  // messages shown here. Pinned to that message, so what arrives while the
  // chat is open doesn't move it; it goes after a few seconds in view, or
  // once another conversation opens.
  const [unreadFromId, setUnreadFromId] = useState(null);
  const [unreadLeaving, setUnreadLeaving] = useState(false);
  const scrollToUnreadRef = useRef(null);
  const openConversationId = activeConversation?.conversation_id;
  useEffect(() => {
    setUnreadFromId(null);
    scrollToUnreadRef.current = null;
    const unread = groupChat?.unread_count || 0;
    if (!unread) return;
    let index = visibleMessages.length;
    for (let left = unread; left > 0 && index > 0; index -= 1) {
      const m = visibleMessages[index - 1];
      if (m.role === 'user' || m.role === 'assistant') left -= 1;
    }
    // All of it unread: nothing above to set it apart from.
    if (index <= 0 || index >= visibleMessages.length) return;
    setUnreadFromId(visibleMessages[index].id);
    scrollToUnreadRef.current = index;
  // Once per conversation opened: a later copy of the group (after it's been
  // read) says 0.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openConversationId]);
  const unreadFromIndex = useMemo(
    () => (unreadFromId ? visibleMessages.findIndex((m) => m.id === unreadFromId) : -1),
    [visibleMessages, unreadFromId],
  );
  // Counted from when the window is in view: a chat opened and then left in
  // a background tab still shows it on coming back.
  useEffect(() => {
    setUnreadLeaving(false);
    if (!unreadFromId) return undefined;
    let timer = null;
    const schedule = () => {
      clearTimeout(timer);
      if (document.visibilityState !== 'visible') return;
      timer = setTimeout(() => {
        setUnreadLeaving(true);
        timer = setTimeout(() => setUnreadFromId(null), UNREAD_DIVIDER_LEAVE_MS);
      }, UNREAD_DIVIDER_MS);
    };
    schedule();
    document.addEventListener('visibilitychange', schedule);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', schedule);
    };
  }, [unreadFromId]);

  const { cancelQuestion: cancelGroupQuestion } = groupActivity;
  const handleCancelQueued = useCallback(async (message) => {
    try {
      await cancelGroupQuestion(message.pendingId);
    } catch (e) {
      // Already started, or gone -- the stream says which.
    }
  }, [cancelGroupQuestion]);
  // "Send again" for a group chat question that was never sent: drop the
  // old one and ask it again -- or, with attachments that can't be re-sent
  // from here, put the text back in the composer.
  const sendAgain = useCallback(async ({ text, pendingId, localTurnId, hasAttachments, knowledgeScopePicks }) => {
    if (pendingId) {
      try {
        await cancelGroupQuestion(pendingId);
      } catch (e) {
        // Already gone.
      }
    }
    if (localTurnId) setMessages((prev) => prev.filter((m) => m.localTurnId !== localTurnId));
    if (hasAttachments) {
      setInputMessage(text || '');
      // Its "@" picks come back with its text, unsent again.
      if (knowledgeScopePicks?.length && activeConversation?.conversation_id) {
        updateMentionDraft(activeConversation.conversation_id, () => knowledgeScopePicks);
      }
      focusInput();
      return;
    }
    sendMessage({ message: text || '', knowledgeScopePicks });
  }, [cancelGroupQuestion, setMessages, focusInput, sendMessage, activeConversation?.conversation_id, updateMentionDraft]);
  const handleCancelPendingId = useCallback(
    (pendingId) => cancelGroupQuestion(pendingId).catch(() => {}),
    [cancelGroupQuestion],
  );
  const handleSendAgainMessage = useCallback((m) => sendAgain({
    text: m.content,
    pendingId: m.pendingId,
    localTurnId: m.localTurnId,
    hasAttachments: (m.images?.length || 0) + (m.files?.length || 0) > 0,
    knowledgeScopePicks: m.knowledgeScopePicks,
  }), [sendAgain]);
  const handleSendAgainQueued = useCallback((item) => sendAgain({
    text: item.text, pendingId: item.pending_id, hasAttachments: item.has_attachments,
  }), [sendAgain]);

  // ------------------------------------------------------------------
  // Virtualisation
  //
  // Using @tanstack/react-virtual to only mount the messages currently
  // visible in the scroll container. This keeps long conversations
  // (hundreds/thousands of messages) responsive. ``measureElement``
  // handles variable heights including code blocks and streaming
  // markdown; keeping ``message.id`` stable is critical for layout.
  // ------------------------------------------------------------------
  const rowVirtualizer = useVirtualizer({
    count: visibleMessages.length,
    getScrollElement: () => messagesContainerRef.current,
    estimateSize: () => 220,
    overscan: 8,
    getItemKey: (index) => visibleMessages[index]?.id ?? index,
  });

  // When a *new* message row appears, align its start with the top of the
  // scrollport so the user reads from the beginning. Do not change scroll
  // while the last message is streaming: the row height grows downward and
  // fixed scrollTop keeps the top of the message in view; the user scrolls
  // on their own to see more. (A previous "pin to bottom" during stream
  // followed token output and was hard to read.)
  const scrollToNewMessage = useCallback(() => {
    if (visibleMessages.length === 0) return;
    rowVirtualizer.scrollToIndex(visibleMessages.length - 1, {
      align: 'start',
      behavior: 'auto',
    });
  }, [rowVirtualizer, visibleMessages.length]);

  // Opening a conversation lands at its end, where its latest answer is.
  // Not left to the count check below: switching to a conversation with
  // fewer messages than the last one never fired it, and one opened in a
  // project's shell (a fresh mount) only got as far as the top of its last
  // answer. Once per conversation, after its messages are in; not for the
  // one this component just created mid-answer (isLoading), whose answer is
  // read from its top as it streams.
  const scrolledOpenIdRef = useRef(null);

  // Only run when a new message is added (count changes), not on streaming updates.
  useEffect(() => {
    const currentCount = messages.length;
    const prevCount = prevMessageCountRef.current;
    // Not while "Loading conversation history..." still covers the list
    // (a page reload): there are no rows to scroll yet, and marking it done
    // then left the conversation at its top.
    const justOpened = Boolean(openConversationId)
      && scrolledOpenIdRef.current !== openConversationId
      && visibleMessages.length > 0
      && !isLoadingHistory;
    if (justOpened) scrolledOpenIdRef.current = openConversationId;

    if (justOpened && !isLoading) {
      // A group chat with unread messages opens at its "Unread messages" line.
      const unreadIndex = scrollToUnreadRef.current;
      scrollToUnreadRef.current = null;
      holdScrollRef.current = false;
      requestAnimationFrame(() => {
        if (unreadIndex !== null) {
          rowVirtualizer.scrollToIndex(unreadIndex, { align: 'start', behavior: 'auto' });
        } else {
          rowVirtualizer.scrollToIndex(visibleMessages.length - 1, { align: 'end', behavior: 'auto' });
        }
      });
    } else if (currentCount > prevCount) {
      if (holdScrollRef.current) {
        // Someone else's message in a group chat, arriving while the reader
        // is scrolled up through the history: leave them where they are.
        holdScrollRef.current = false;
      } else if (scrollToUnreadRef.current !== null) {
        // A group chat just opened: at its "Unread messages" line.
        const index = scrollToUnreadRef.current;
        scrollToUnreadRef.current = null;
        requestAnimationFrame(() => {
          rowVirtualizer.scrollToIndex(index, { align: 'start', behavior: 'auto' });
        });
      } else {
        // Defer until after layout / virtualizer can measure the new row.
        requestAnimationFrame(() => {
          scrollToNewMessage();
        });
      }
    }

    prevMessageCountRef.current = currentCount;
  // isLoading is read, not a trigger: the stream ending isn't an opening.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, openConversationId, visibleMessages.length, isLoadingHistory, scrollToNewMessage, rowVirtualizer]);

  // Our own question just went below the queue (heldTurnIds): show it there,
  // at the very bottom, with the answer KAI is busy on just above it. After
  // the count check above, which only knows the transcript.
  const heldCount = heldIds?.size || 0;
  const prevHeldCountRef = useRef(0);
  useEffect(() => {
    if (heldCount > prevHeldCountRef.current) {
      requestAnimationFrame(() => {
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    }
    prevHeldCountRef.current = heldCount;
  }, [heldCount]);

  // The "N new messages" pill goes once the reader reaches the bottom.
  const { newMessages: groupNewMessages, clearNewMessages: clearGroupNewMessages } = groupActivity;
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el || !groupNewMessages) return undefined;
    const onScroll = () => {
      if (isNearBottom()) clearGroupNewMessages();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [groupNewMessages, clearGroupNewMessages, isNearBottom]);

  // Navigate back to the source conversation (used by the handover seed banner)
  const handleOpenSource = useCallback((sourceId) => {
    if (!sourceId) return;
    if (typeof onOpenConversation === 'function') {
      onOpenConversation(sourceId);
    }
  }, [onOpenConversation]);

  // Preview panel resize logic
  useEffect(() => {
    if (!isResizingPreview) return;
    const handleMouseMove = (e) => {
      if (!wrapperRef.current) return;
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const newWidth = wrapperRect.right - e.clientX;
      const clamped = Math.max(280, Math.min(newWidth, wrapperRect.width - 380));
      setPreviewWidth(clamped);
    };
    const handleMouseUp = () => {
      setIsResizingPreview(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingPreview]);

  const handlePreviewResizeStart = (e) => {
    e.preventDefault();
    setIsResizingPreview(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleClosePreview = () => {
    if (setWebsitePreview) setWebsitePreview(null);
  };

  const handleRefreshPreview = () => {
    if (setWebsitePreview && websitePreview) {
      setWebsitePreview({ ...websitePreview, refreshKey: Date.now() });
    }
  };

  return (
    <MemberPhotosProvider projectId={groupChat ? activeConversation?.project_id : null}>
    <div className={`chat-interface-wrapper ${websitePreview ? 'with-preview' : ''}`} ref={wrapperRef}>
      {groupChat && (
        <GroupChatPeopleModal
          isOpen={showGroupPeople}
          conversationId={activeConversation?.conversation_id}
          projectId={activeConversation?.project_id}
          group={groupChat}
          people={groupActivity.people}
          hasMessages={messages.length > 0}
          onClose={() => setShowGroupPeople(false)}
          onInvite={inviteFromGroupChat ? () => { setShowGroupPeople(false); inviteFromGroupChat(); } : undefined}
          onLeave={() => { setShowGroupPeople(false); setLeavingGroup(true); }}
          onSaved={(group) => {
            setShowGroupPeople(false);
            const id = activeConversation?.conversation_id;
            setActiveConversation?.((prev) => (prev && prev.conversation_id === id ? { ...prev, group } : prev));
          }}
        />
      )}
      {groupChat && (
        <GroupChatLeaveModal
          chat={leavingGroup && activeConversation
            ? { conversation_id: activeConversation.conversation_id, name: activeConversation.name || 'this group chat' }
            : null}
          alone={(groupMembers || []).length > 0
            && groupMembers.every((m) => m.user_id === groupChat.my_user_id)}
          manages={managesGroupPeople}
          empty={messages.length === 0}
          onClose={() => setLeavingGroup(false)}
          onLeft={(id) => { setLeavingGroup(false); onGroupChatLeft?.(id); }}
        />
      )}
      <GroupChatOriginalCopyModal
        chat={originalOffer?.conversation_id === activeConversation?.conversation_id ? originalOffer : null}
        onClose={() => setOriginalOffer(null)}
        onDone={(removedId) => {
          setOriginalOffer(null);
          if (removedId) onOriginalCopyRemoved?.(removedId);
        }}
      />
    {isResizingPreview && <div className="preview-resize-overlay" />}
    <div
      className="chat-interface"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Select-to-reply: floating "Reply" button near an in-progress text
          selection (see .claude/plan/chatreply.md). Fixed-position, so where
          it sits in the tree doesn't matter. */}
      {selectionPopup && (
        <div
          className="selection-reply-popup"
          style={{ top: selectionPopup.top, left: selectionPopup.left }}
        >
          <button type="button" onPointerDown={(e) => e.preventDefault()} onClick={handleReplyToSelection}>
            <FiCornerUpLeft size={13} />
            Reply
          </button>
        </div>
      )}
      {/* Drag-and-drop overlay */}
      {isDragging && (
        <div className="drag-drop-overlay">
          <div className="drag-drop-overlay-inner">
            <FiPaperclip size={28} />
            <span>Drop files to attach</span>
          </div>
        </div>
      )}
      {/* Chat Header */}
      <div
        className={`chat-header${hideHeaderTitle ? ' chat-header--embedded' : ''}${
          headerActionsPortalTarget && headerSubtitlePortalTarget ? ' chat-header--empty' : ''
        }`}
      >
        <div className="chat-header-left">

          {!hideHeaderTitle && (
          <div className="chat-info">
            {/* Inside a project, the project's own name is the PRIMARY
                heading here -- not the individual conversation's title --
                so the "you're in a project" identity carries through every
                conversation the same way it does on the project's own page
                (ProjectView.js), instead of collapsing into a normal-looking
                chat the moment a conversation actually exists. The
                conversation's own title is demoted to the subtitle line
                below, alongside the model and message count. */}
            {!sharedConversation && getActiveProject() ? (
              <button
                type="button"
                className="chat-title chat-title-project"
                title="Open this project's page (instructions, knowledge base, and history)."
                onClick={() => {
                  const project = getActiveProject();
                  if (project) onOpenProjectView?.(project);
                }}
              >
                <FiLayers size={14} className="chat-title-project-icon" />
                {getActiveProjectName()}
              </button>
            ) : (
              (activeConversation || sharedConversation) && (
                <div className="chat-title">
                  {(sharedConversation ? sharedConversation.conversation : activeConversation).name}
                </div>
              )
            )}
            {(activeConversation || sharedConversation) && (
              <div className="chat-subtitle">
                {!sharedConversation && getActiveProject() && (
                  <>
                    <span>{activeConversation.name}</span>
                    <span> • </span>
                  </>
                )}
                {!sharedConversation && getFocusModeName() && (
                  <>
                    <span className="chat-focus-mode-inline">{getFocusModeName()}</span>
                    <span> • </span>
                  </>
                )}
                {sharedConversation ? 'View only' : `${formatModelName(selectedModel)} • ${messages.length} messages`}
                {sharedConversation && (
                    <span className="shared-badge">Shared with you</span>
                  )}
              </div>
            )}
          </div>
          )}

          {/* Embedded inside ProjectView's shell (hideHeaderTitle true): the
              title above is suppressed since ProjectView's own h1 already
              shows the conversation's name, so only the model/message-count
              line renders -- portaled up directly under that h1 once its
              slot exists (headerSubtitlePortalTarget), matching a regular
              conversation's title-over-subtitle layout. */}
          {hideHeaderTitle && activeConversation && !sharedConversation && (() => {
            const subtitle = (
              <div className="chat-subtitle">
                {getFocusModeName() && (
                  <>
                    <span className="chat-focus-mode-inline">{getFocusModeName()}</span>
                    <span> • </span>
                  </>
                )}
                {formatModelName(selectedModel)} • {messages.length} messages
              </div>
            );
            return headerSubtitlePortalTarget
              ? createPortal(subtitle, headerSubtitlePortalTarget)
              : <div className="chat-info">{subtitle}</div>;
          })()}
        </div>

        {/* Portaled up into ProjectView's breadcrumb row when embedded in
            the project shell (headerActionsPortalTarget set) -- these
            controls (context meter, Handover, Share) stay exactly this
            component's own state/handlers either way, only WHERE they
            render in the DOM changes. Outside the project shell (target
            null), this renders inline in this component's own header,
            unchanged from before. */}
        {(() => {
          const headerRightControls = (
            <>
              {contextUsage.limit > 0 && (
                <div className={`token-display context-usage-bar context-${getContextLevel(contextUsage.percentage)}`} title={`Context: ${contextUsage.used.toLocaleString()} / ${contextUsage.limit.toLocaleString()} tokens`}>
                  <div className="context-bar-row">
                    <div className="context-bar-track">
                      <div className="context-bar-fill" style={{ width: `${Math.min(contextUsage.percentage, 100)}%` }} />
                    </div>
                    <span className="context-bar-percent">{contextUsage.percentage}%</span>
                  </div>
                  <span className="context-bar-label">{formatTokenCount(contextUsage.used)} / {formatTokenCount(contextUsage.limit)}</span>
                </div>
              )}

              {/* A group chat: who's in it and an invite, instead of a
                  snapshot share (anyone in the project can already read it). */}
              {groupChat && (
                <GroupChatHeaderBits
                  group={groupChat}
                  people={groupActivity.people}
                  onShowPeople={() => setShowGroupPeople(true)}
                />
              )}

              {/* Not in a copy of a group chat you're no longer in: it holds
                  other people's questions, and nobody asks KAI in it again.
                  A group chat's continues as your own or as a new group chat. */}
              {activeConversation && !sharedConversation && !leftGroup && (
                <button
                  type="button"
                  className={`handover-header-btn ${contextUsage.percentage >= 70 ? 'handover-header-btn--urgent' : ''}`}
                  onClick={() => setShowHandoverModal(true)}
                  title={contextUsage.percentage >= 70
                    ? `Context usage is at ${contextUsage.percentage}% — generate a handover and continue in a fresh chat.`
                    : 'Generate a handover summary to download or continue in a fresh chat.'}
                  aria-label="Handover"
                >
                  <FiFileText size={16} />
                  <span className="handover-header-btn-label">Handover</span>
                </button>
              )}

              {activeConversation && !sharedConversation && !groupChat && !leftGroup && (
                <button
                  className="share-conversation-btn"
                  onClick={() => setShowShareModal(true)}
                  title="Share conversation"
                >
                  <FiShare2 size={16} />
                </button>
              )}
            </>
          );
          return headerActionsPortalTarget
            ? createPortal(headerRightControls, headerActionsPortalTarget)
            : <div className="chat-header-right">{headerRightControls}</div>;
        })()}
      </div>

      {/* Messages Area */}
      <div className="messages-container" ref={messagesContainerRef}>
        {isLoadingHistory ? (
          <div className="loading-history loading-history--initial">
                            <div className="loading-spinner"></div>
            <div>Loading conversation history...</div>
          </div>
        ) : isAutoSendingProjectFirstMessage && messages.length === 0 ? (
          <div className="loading-history">
            <div className="loading-spinner"></div>
          </div>
        ) : showEmptyChat ? (
          <div className={`empty-chat${getActiveProject() ? ' empty-chat--project' : ''}`}>
            {/* A project's blank conversation gets its own welcome rather
                than the focus mode's org-wide one: the project heading right
                above already says where the user is, so repeating a generic
                "Ready to Supercharge Your Workflow?" here both contradicts it
                and buries the one thing worth saying -- that this chat can
                already see the project's instructions and files. The purple
                accent (eyebrow tag, title tint) matches the project's own
                box icon/color elsewhere (ProjectView.js, ProjectPanel.js) so
                this reads as a continuation of that screen, not a plain chat
                that happens to mention a project. */}
            {/*getActiveProject() && <div className="empty-project-tag">Project workspace</div>*/}
            {getActiveProject() ? (
              <div className="empty-icon empty-icon--rocket">
                {/* rocket-launch.webp is a chroma-keyed rebuild of the
                    original GIF (see scripts/keyout in the commit that added
                    this) -- every one of its 66 frames had its actual white
                    background pixels made truly transparent (real alpha, not
                    a CSS trick), while every non-white pixel -- outline,
                    fins, flame, window, sparkles -- was left at full opacity
                    exactly as authored. Two earlier CSS-only attempts
                    (mix-blend-mode: multiply, then an SVG luminance-based
                    alpha filter) each either darkened the artwork's own
                    colors against a dark background or partially faded its
                    own pastel/soft-shaded pixels -- neither problem is
                    possible now since there's nothing left to key out or
                    blend at render time. */}
                <img src={rocketLaunchIcon} alt="" className="empty-icon-rocket-img" />
              </div>
            ) : (
              <div className="empty-icon">✨</div>
            )}
            <div className={`empty-title${getActiveProject() ? ' empty-title--project' : ''}`}>
              {getActiveProject() ? `Start a conversation in ${getActiveProjectName()}` : getWelcomeTitle()}
            </div>
            <div className="empty-description">
              {getActiveProject()
                ? "This chat already follows this project's instructions and can search its knowledge base. Ask anything about it."
                : getWelcomeMessage()}
            </div>
            {/* The mode this conversation will run in, shown before the
                first message since a project chat now runs in whichever
                mode the menu is on. General Focus shows nothing, as in the
                header (getFocusModeName). */}
            {getActiveProject() && getFocusModeName() && (
              <div className="empty-focus-mode">
                <span className="chat-focus-mode-inline">{getFocusModeName()}</span>
              </div>
            )}
            {/* Which sources this about-to-be-created conversation can search
                is picked in ProjectPanel.js's own Files tab (checkboxes,
                mirroring the Rules tab), not here -- see App.js's
                pendingKnowledgeScope and .claude/plan/plan_knowledge_scope.md. */}
            {/* The current focus mode's example prompts themselves now render
                below the composer (see .example-prompts-footer, .input-
                container's own next sibling) instead of here -- matching a
                project's own empty screen, where the composer sits right
                under the description with nothing between them. Skipped
                for a project in General Focus (redundant next to its own
                Instructions/Knowledge base), shown in any other mode. */}
          </div>
        ) : (
          <div className="messages-list messages-list--virtual"
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              position: 'relative',
              width: '100%',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const message = visibleMessages[virtualRow.index];
              if (!message) return null;
              const isLast = virtualRow.index === visibleMessages.length - 1;
              // If this message holds an options card, the user's reply is the
              // next message. Passing it down lets the card highlight what was
              // actually chosen: the card's own state does not survive the
              // remount that answering triggers, so it would otherwise fall
              // back to showing the pre-selected default as if that were the
              // answer. Reading it from the conversation also survives a reload.
              const nextMessage = visibleMessages[virtualRow.index + 1];
              const answerText =
                nextMessage && nextMessage.role === 'user' ? nextMessage.content : null;
              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  className="virtual-message-row"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {virtualRow.index === unreadFromIndex && (
                    <div
                      className={`gc-unread-divider${unreadLeaving ? ' leaving' : ''}`}
                      role="separator"
                      aria-label="Unread messages"
                    >
                      <span>Unread messages</span>
                    </div>
                  )}
                  <MessageItem
                    message={message}
                    answerText={answerText}
                    isLastMessage={isLast}
                    lastMessageRef={newMessageStartRef}
                    sharedConversation={sharedConversation}
                    isDarkMode={isDarkMode}
                    copiedMessageId={copiedMessageId}
                    onCopyMessage={copyMessageToClipboard}
                    onRetry={leftGroup || groupContinuedIn ? undefined : retryLastMessage}
                    isRetrying={retryingMessageId === message.id}
                    onOpenSource={handleOpenSource}
                    websitePreview={websitePreview}
                    setWebsitePreview={setWebsitePreview}
                    onQuickSubmit={submitQuickOptions}
                    onQuoteMessage={sharedConversation || leftGroup || groupContinuedIn ? undefined : handleQuoteMessage}
                    onPreviewPdf={setPdfPreview}
                    mentionableNames={highlightNames}
                    groupChat={groupChat}
                    leftGroup={leftGroup}
                    answeringName={answeringNames ? answeringNames[virtualRow.index] : null}
                    onCancelQueued={groupChat ? handleCancelQueued : undefined}
                    onSendAgain={groupChat && !groupContinuedIn ? handleSendAgainMessage : undefined}
                    myUserId={authUser?.id}
                    groupMemberIds={groupMemberIds}
                    onAddBack={canAddBack ? handleAddBack : undefined}
                    addBackError={addBackError}
                  />
                </div>
              );
            })}
            <div ref={messagesEndRef} style={{ position: 'absolute', top: rowVirtualizer.getTotalSize(), left: 0, width: '100%', height: 1 }} />
          </div>
        )}
        {/* A group chat's queue: other people's questions waiting for KAI
            (or being answered right now), below the transcript. */}
        {groupChat && (
          <GroupChatQueue
            pending={groupActivity.pending}
            group={groupChat}
            localPendingIds={localPendingIds}
            onCancel={handleCancelPendingId}
            onSendAgain={groupContinuedIn ? undefined : handleSendAgainQueued}
            live={liveAnswer}
            renderRunning={(item, live) => {
              // Someone else's question KAI is answering, and the answer so
              // far, drawn as the messages they'll be once it's done -- so
              // nothing jumps when the finished ones replace them.
              const files = pendingFiles(item);
              const question = {
                id: `gc-running-${item.pending_id}`, role: 'user', author: item.author,
                content: `${item.text}${item.has_attachments && !files ? ' 📎' : ''}`, timestamp: item.created_at,
                replyContext: pendingReplyContext(item),
                ...(files ? { files } : {}),
              };
              const answer = {
                id: `gc-running-${item.pending_id}-answer`, role: 'assistant', isStreaming: true,
                content: live ? `${live.text}${live.truncated ? '\n\n…' : ''}` : '', timestamp: item.created_at,
              };
              return (
                <div data-testid="gc-running">
                  {[question, answer].map((m) => (
                    <div key={m.id} className="virtual-message-row">
                      <MessageItem
                        message={m}
                        isDarkMode={isDarkMode}
                        onOpenSource={handleOpenSource}
                        websitePreview={websitePreview}
                        setWebsitePreview={setWebsitePreview}
                        onPreviewPdf={setPdfPreview}
                        mentionableNames={highlightNames}
                        groupChat={groupChat}
                        answeringName={m.role === 'assistant' ? item.author?.display_name : null}
                        myUserId={authUser?.id}
                      />
                    </div>
                  ))}
                </div>
              );
            }}
            held={heldMessages}
            renderHeld={(turn) => (
              // Our own question waiting its turn (Cancel as ever), or the
              // answer KAI has just started while someone else's is still
              // drawn above it.
              <div data-testid="gc-held">
                {turn.map((m) => (
                  <div key={m.id} className="virtual-message-row">
                    <MessageItem
                      message={m}
                      isLastMessage={m === turn[turn.length - 1]}
                      isDarkMode={isDarkMode}
                      copiedMessageId={copiedMessageId}
                      onCopyMessage={copyMessageToClipboard}
                      onOpenSource={handleOpenSource}
                      websitePreview={websitePreview}
                      setWebsitePreview={setWebsitePreview}
                      onPreviewPdf={setPdfPreview}
                      mentionableNames={highlightNames}
                      groupChat={groupChat}
                      answeringName={m.role === 'assistant' ? 'you' : null}
                      onCancelQueued={handleCancelQueued}
                      onSendAgain={groupContinuedIn ? undefined : handleSendAgainMessage}
                      myUserId={authUser?.id}
                      groupMemberIds={groupMemberIds}
                    />
                  </div>
                ))}
              </div>
            )}
          />
        )}
        {groupChat && (
          <NewMessagesPill
            count={groupNewMessages}
            onClick={() => {
              clearGroupNewMessages();
              // Other people's questions sit below the transcript, so go to
              // the very bottom when there are any; else to the newest message.
              const below = (groupActivity.pending || []).some((p) => !localPendingIds.has(p.pending_id));
              const el = messagesContainerRef.current;
              if (below && el) el.scrollTop = el.scrollHeight;
              else scrollToNewMessage();
            }}
          />
        )}
      </div>

      {/* Input Area */}
      <div className="input-container">
        {/* A group chat's status -- who's typing, KAI answering someone --
            sits just above the composer, outside its box. */}
        {groupChat && (
          <div className="gc-composer-status">
            {groupActivity.gone && (
              <div className="gc-turn-banner" role="status">
                This group chat isn&apos;t available any more: it was deleted, or you&apos;re no longer in it or
                its project.
              </div>
            )}
            <GroupChatTurnBanner
              turn={groupActivity.turn}
              group={groupChat}
              answerShown={(groupActivity.pending || []).some((p) => p.status === 'running')}
            />
            {!groupActivity.gone && <GroupChatTyping typing={groupActivity.typing} />}
          </div>
        )}
        <div className="input-wrapper">
          {/* A SharePoint connect refused for want of a Microsoft token -- see
              graphSignIn above. */}
          {graphSignIn && (
            <div className="held-files-notice" role="status">
              <FiLock size={15} className="held-files-notice-icon" aria-hidden="true" />
              <div className="held-files-notice-info">
                <div className="held-files-notice-title">Confirm your Microsoft sign-in</div>
                <div className="held-files-notice-hint">
                  Microsoft asks for this about once a day before KAI can read SharePoint files.
                  KAI will retry your last message once it's done.
                </div>
                {graphSignIn.error && <div className="held-files-notice-error">{graphSignIn.error}</div>}
              </div>
              <div className="held-files-notice-actions">
                <button
                  type="button"
                  className="held-files-notice-btn"
                  onClick={() => setGraphSignIn(null)}
                  disabled={graphSignIn.phase === 'busy'}
                >
                  Not now
                </button>
                <button
                  type="button"
                  className="held-files-notice-btn held-files-notice-btn--primary"
                  onClick={confirmGraphSignIn}
                  disabled={graphSignIn.phase === 'busy' || isLoading}
                  title={isLoading ? 'Wait for the reply to finish' : 'Confirm with Microsoft and retry'}
                >
                  {graphSignIn.phase === 'busy' ? 'Confirming…' : 'Confirm sign-in'}
                </button>
              </div>
            </div>
          )}
          {/* Knowledge base files someone else added after this conversation
              started -- left out of it until the user adds them (see
              heldFiles above). */}
          {heldNotice && (
            <div className="held-files-notice" role="status">
              <FiFileText size={15} className="held-files-notice-icon" aria-hidden="true" />
              <div className="held-files-notice-info">
                <div className="held-files-notice-title">{heldNotice.title}</div>
                <div className="held-files-notice-names" title={heldNotice.allNames}>{heldNotice.names}</div>
                <div className="held-files-notice-hint">{heldNotice.hint}</div>
                {heldFilesError && <div className="held-files-notice-error">{heldFilesError}</div>}
              </div>
              <div className="held-files-notice-actions">
                <button
                  type="button"
                  className="held-files-notice-btn"
                  onClick={() => answerVisibleHeldFiles('dismiss')}
                  disabled={heldFilesBusy || isLoading}
                  title="Keep these files out of this conversation"
                >
                  Not now
                </button>
                <button
                  type="button"
                  className="held-files-notice-btn held-files-notice-btn--primary"
                  onClick={() => answerVisibleHeldFiles('accept')}
                  disabled={heldFilesBusy || isLoading}
                  title={isLoading ? 'Wait for the reply to finish' : 'Let KAI use these files in this conversation'}
                >
                  {heldFilesBusy ? 'Adding…' : 'Use in this chat'}
                </button>
              </div>
            </div>
          )}
          {/* Select-to-reply quote preview -- see .claude/plan/chatreply.md.
              One active quote at a time; picking a new selection/message
              replaces it rather than stacking. */}
          {replyContext && (
            <div className="reply-context-preview">
              <FiCornerUpLeft size={14} className="reply-context-icon" />
              <div className="reply-context-info">
                <div className="reply-context-label">
                  {replyLabel(replyContext, Boolean(groupChat))}
                </div>
                <div className="reply-context-snippet">{replyContext.text}</div>
              </div>
              <button
                type="button"
                className="reply-context-remove"
                onClick={() => setReplyContext(null)}
                title="Remove quote"
              >
                ×
              </button>
            </div>
          )}
          {/* File and Image preview section - Top */}
          {(selectedImages.length > 0 || selectedFiles.length > 0 || uploadingFiles.length > 0) && (
            <div className="file-preview-container">
              {/* Uploading file previews (with loading indicator) */}
              {uploadingFiles.map((file) => (
                <div key={file.id} className="file-preview file-preview-loading">
                  <div className="file-preview-icon">
                    {file.type === 'pdf' && '📄'}
                    {file.type === 'docx' && '📝'}
                    {file.type === 'xlsx' && '📊'}
                    {file.type === 'csv' && '📋'}
                    {file.type === 'txt' && '📃'}
                    {file.type === 'md' && '📑'}
                    {(file.type === 'eml' || file.type === 'msg') && '✉️'}
                    {['glb', 'gltf', 'obj', 'fbx', 'stl', '3ds', 'ply', '3dm'].includes(file.type) && '🧊'}
                    {file.type === 'mp4' && '🎬'}
                    {['mp3', 'm4a'].includes(file.type) && '🎵'}
                    {!['pdf', 'docx', 'xlsx', 'csv', 'txt', 'md', 'eml', 'msg', 'glb', 'gltf', 'obj', 'fbx', 'stl', '3ds', 'ply', '3dm', 'mp4', 'mp3', 'm4a'].includes(file.type) && '📎'}
                  </div>
                  <div className="file-preview-info">
                    <div className="file-preview-name">{file.name}</div>
                    <div className="file-preview-meta file-preview-loading-text">
                      <span className="file-loading-spinner"></span>
                      {file.stage === 'awaiting-kb-choice'
                        ? 'Waiting for your choice…'
                        : ['mp4', 'mp3', 'm4a'].includes(file.type)
                          ? (file.stage === 'transcribing'
                            ? `Transcribing ${file.type === 'mp4' ? 'video' : 'audio'} (this can take a few minutes)...`
                            : (file.progress !== null && file.progress !== undefined
                              ? `Uploading... ${file.progress}%`
                              : 'Uploading...'))
                          : file.toKnowledgeBase
                            ? (file.stage === 'uploading' && file.progress !== null && file.progress !== undefined
                              ? `Adding to knowledge base... ${file.progress}%`
                              : 'Adding to knowledge base...')
                            : (file.stage === 'indexing'
                              ? 'Indexing large PDF for search...'
                              : 'Extracting text...')}
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Completed file previews */}
              {selectedFiles.map((file) => (
                <div key={file.id} className="file-preview">
                  <div className="file-preview-icon">
                    {file.type === 'pdf' && '📄'}
                    {file.type === 'docx' && '📝'}
                    {file.type === 'xlsx' && '📊'}
                    {file.type === 'csv' && '📋'}
                    {file.type === 'txt' && '📃'}
                    {file.type === 'md' && '📑'}
                    {(file.type === 'eml' || file.type === 'msg') && '✉️'}
                    {['glb', 'gltf', 'obj', 'fbx', 'stl', '3ds', 'ply', '3dm'].includes(file.type) && '🧊'}
                    {file.type === 'mp4' && '🎬'}
                    {['mp3', 'm4a'].includes(file.type) && '🎵'}
                    {!['pdf', 'docx', 'xlsx', 'csv', 'txt', 'md', 'eml', 'msg', 'glb', 'gltf', 'obj', 'fbx', 'stl', '3ds', 'ply', '3dm', 'mp4', 'mp3', 'm4a'].includes(file.type) && '📎'}
                  </div>
                  <div className="file-preview-info">
                    <div className="file-preview-name">{file.name}</div>
                    <div className="file-preview-meta">
                      {file.type.toUpperCase()} • {Math.round(file.size / 1024)}KB • ~{file.tokens} tokens
                    </div>
                    {/* Only set for a very large indexed PDF — see
                        _project_hint in pdf_index_service.py. */}
                    {file.notice && (
                      <div className="file-preview-notice">💡 {file.notice}</div>
                    )}
                  </div>
                  <button
                    className="file-preview-remove"
                    onClick={() => removeFile(file.id)}
                    title="Remove file"
                  >
                    ×
                  </button>
                </div>
              ))}
              
              {/* Image previews */}
              {selectedImages.map((image) => (
                <div key={image.id} className="image-preview">
                  <img src={image.url} alt={image.name} />
                  <div className="image-preview-info">
                    <div className="image-preview-name">{image.name}</div>
                    {image.dimensions && (
                      <div className="image-preview-dims">{image.dimensions}</div>
                    )}
                  </div>
                  <button
                    className="image-preview-remove"
                    onClick={() => removeImage(image.id)}
                    title="Remove image"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Shared view: Import button; otherwise normal input */}
          {sharedConversation ? (
            <div className="shared-import-section">
              <p className="shared-import-text">
                {isImportingShared
                  ? 'Importing conversation… this can take a minute for long conversations. Please keep this tab open.'
                  : sharedConversation.projectName
                    ? `This conversation is from the "${sharedConversation.projectName}" project, so it will be imported into that project. You can continue it there with the project's files and instructions.`
                    : 'Import this conversation into your list to continue the discussion.'}
              </p>
              <button
                type="button"
                className="shared-import-button"
                onClick={() => onImportShared(sharedConversation.shareId)}
                disabled={isImportingShared}
                aria-busy={isImportingShared}
                title={sharedConversation.projectName ? `Import into "${sharedConversation.projectName}" Project` : undefined}
              >
                {isImportingShared ? (
                  <>
                    <span className="shared-import-spinner" aria-hidden="true" />
                    Importing…
                  </>
                ) : (
                  <span className="shared-import-button-label">
                    {sharedConversation.projectName
                      ? `Import into "${sharedConversation.projectName}" Project`
                      : 'Import into my conversations'}
                  </span>
                )}
              </button>
            </div>
          ) : leftGroup ? (
            <LeftGroupChatBar left={leftGroup} />
          ) : groupContinuedIn ? (
            <ContinuedGroupChatBar
              continued={groupContinuedIn}
              myUserId={groupChat?.my_user_id || authUser?.id}
              onOpen={handleOpenSource}
            />
          ) : (
            <div className="input-section">
              {speechError && (
                <div className="speech-error" role="alert">
                  {speechError}
                </div>
              )}
              <div className="input-actions">
                {/* One "+" button (rotates into an x while open) for both
                    attachment kinds: Attach files, and Custom rules, whose
                    existing checklist opens as a flyout beside the menu. */}
                <div className="rules-popover-container" ref={rulesPopoverRef}>
                  <button
                    type="button"
                    className={`attach-button plus-menu-button${plusMenuOpen ? ' plus-menu-button--open' : ''}`}
                    onClick={() => (plusMenuOpen ? closePlusMenu() : setPlusMenuOpen(true))}
                    title={plusMenuOpen ? 'Close' : 'Attach files or custom rules'}
                    aria-label={plusMenuOpen ? 'Close menu' : 'Attach files or custom rules'}
                    aria-haspopup="menu"
                    aria-expanded={plusMenuOpen}
                    disabled={isListening}
                  >
                    <FiPlus size={18} />
                  </button>
                  {/* Portaled to <body> so an overflow:hidden ancestor can't
                      clip it -- positionPlusMenu places both pieces. */}
                  {plusMenuOpen && createPortal(
                    <>
                    <div className="plus-menu" role="menu" ref={plusMenuRef}>
                      <button
                        type="button"
                        role="menuitem"
                        className="plus-menu-item"
                        onMouseEnter={() => setRulesPopoverOpen(false)}
                        onClick={() => {
                          closePlusMenu();
                          fileInputRef.current?.click();
                        }}
                      >
                        <FiPaperclip size={16} />
                        <span>Attach files</span>
                      </button>
                      <button
                        ref={rulesMenuItemRef}
                        type="button"
                        role="menuitem"
                        className={`plus-menu-item${rulesPopoverOpen ? ' plus-menu-item--active' : ''}${groupChat ? ' plus-menu-item--blocked' : ''}`}
                        aria-haspopup="true"
                        aria-expanded={rulesPopoverOpen}
                        // Nobody's personal rules apply in a group chat:
                        // they'd shape everyone's answers. A "not allowed"
                        // sign takes the place of the chevron there.
                        disabled={Boolean(groupChat)}
                        title={groupChat ? "Personal rules don't apply in a group chat. The project's instructions do." : undefined}
                        onMouseEnter={() => { if (!groupChat) setRulesPopoverOpen(true); }}
                        onClick={() => { if (!groupChat) setRulesPopoverOpen(true); }}
                      >
                        <FiList size={16} />
                        <span>Custom rules</span>
                        {groupChat ? (
                          <FiSlash size={14} className="plus-menu-item-chevron plus-menu-item-blocked-icon" aria-hidden="true" />
                        ) : (
                          <FiChevronRight size={14} className="plus-menu-item-chevron" />
                        )}
                      </button>
                    </div>
                      {rulesPopoverOpen && (
                        <div className="rules-popover rules-popover--flyout" ref={rulesFlyoutRef}>
                          {/* Shown with no rules saved too, so the very first
                              one can be created from right here. */}
                          <div className="rules-popover-title-row">
                            <div className="rules-popover-title">Attach rules to this conversation</div>
                            <button
                              type="button"
                              className="rules-popover-add-btn"
                              title="Create a new custom rule"
                              aria-label="Create a new custom rule"
                              onClick={() => {
                                closePlusMenu();
                                onOpenCustomRules?.();
                              }}
                            >
                              <FiPlus size={12} />
                            </button>
                          </div>
                          {customRules.length === 0 ? (
                            <div className="rules-popover-empty">
                              {customRulesLoadFailed
                                ? "Couldn't load your rules right now. Please try again."
                                : 'No saved rules yet. Click + to create your first one.'}
                            </div>
                          ) : (
                            <>
                              {projectDefaultRules.length > 0 && (
                                <div className="rules-popover-project-hint">
                                  <FiLayers size={10} />
                                  {projectDefaultRules.length === 1
                                    ? "1 rule from this project's defaults is always on here. Edit it from the project in the sidebar."
                                    : `${projectDefaultRules.length} rules from this project's defaults are always on here. Edit them from the project in the sidebar.`}
                                </div>
                              )}
                              {/* Pinned above the scrollable list (see
                                  .rules-popover-list's flex:1/overflow-y:auto),
                                  not below it -- a long rule list must never be
                                  able to scroll this out of view. */}
                              {checkingAttachConflict && (
                                <div className="rules-popover-cap-hint">Checking for conflicts…</div>
                              )}
                              {rulesAttachError && (
                                <div className="rules-popover-conflict-error">{rulesAttachError}</div>
                              )}
                              <ul className="rules-popover-list">
                                {customRules.map((rule) => {
                                  // A project default is shown checked and locked
                                  // here -- it's already active for every
                                  // conversation in the project (see
                                  // resolve_effective_rule_ids on the backend),
                                  // and un-checking it here would look like it
                                  // detached the rule without actually doing so.
                                  const isProjectDefault = projectDefaultRuleIds.includes(rule.rule_id);
                                  const checked = isProjectDefault || attachedRuleIds.includes(rule.rule_id);
                                  const capReached = !checked && attachedRuleIds.length >= MAX_ACTIVE_RULES_PER_CONVERSATION;
                                  const disabled = isProjectDefault || capReached || checkingAttachConflict;
                                  return (
                                    <li key={rule.rule_id} className="rules-popover-item">
                                      <label
                                        className={disabled ? 'disabled' : ''}
                                        title={isProjectDefault ? 'Always on in this project' : undefined}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          disabled={disabled}
                                          onChange={() => toggleAttachedRule(rule.rule_id)}
                                        />
                                        <span className="rules-popover-item-text" title={rule.text}>
                                          <span className="rules-popover-item-title">
                                            {rule.title || rule.text}
                                            {isProjectDefault && (
                                              <span className="rules-popover-project-tag">project default</span>
                                            )}
                                          </span>
                                          {rule.title && (
                                            <span className="rules-popover-item-desc">{rule.text}</span>
                                          )}
                                        </span>
                                      </label>
                                    </li>
                                  );
                                })}
                              </ul>
                              {attachedRuleIds.length >= MAX_ACTIVE_RULES_PER_CONVERSATION && (
                                <div className="rules-popover-cap-hint">
                                  Up to {MAX_ACTIVE_RULES_PER_CONVERSATION} rules per conversation
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </>,
                    document.body
                  )}
                </div>
              </div>

              {(() => {
                const groupTurnIsSomeoneElses = Boolean(
                  groupChat && groupActivity.turn && groupActivity.turn.holder_user_id !== groupChat.my_user_id,
                );
                const currentPlaceholder = isListening
                  ? 'Listening…'
                  : groupChat
                    ? (groupTurnIsSomeoneElses
                      ? 'Ask KAI. Your question will be queued.'
                      : 'Ask KAI. Everyone in this chat will see it.')
                    : (getActiveProject()
                    // Shorter on phones: the composer row is narrow and also
                    // holds the model picker, so the full sentence wrapped to
                    // three lines.
                    ? (isMobile ? 'Explore project…' : 'Ask me anything about this project...')
                    : 'How can I help you today?');

                // Only one of these two is ever mounted (see richInputRef):
                // the plain textarea below is completely untouched when no
                // rule is attached, so dictation/autoresize/paste behave
                // exactly as they always have in that (overwhelmingly
                // common) case. The contentEditable box only exists so the
                // rule pill(s) can render inline with the prompt text.
                //
                // The distinct `key`s below are load-bearing, not cosmetic:
                // both branches return a plain <div> at this same position,
                // so without them React treats a rich-input <-> plain-input
                // switch as an UPDATE of the same host node rather than an
                // unmount/remount. That's a real bug, not just a perf
                // nitpick -- rebuildRichInputPills's pill spans are raw DOM
                // nodes appended imperatively (React never rendered them, so
                // it has no idea they exist), and React's reconciler doesn't
                // clear a reused node's actual DOM contents before grafting
                // in its own new children; it just appends them. Detaching
                // the last rule dropped attachedRules to 0 and correctly
                // swapped this to the plain-input branch, but the OLD pill
                // (and richInputRef itself, now unreferenced) stayed behind
                // as an invisible-to-React orphan, so the "x" looked
                // completely dead even though the underlying state was
                // toggling correctly the whole time. Different keys force a
                // real unmount, which discards that orphaned subtree
                // instead of grafting a textarea in beside it.
                if (attachedRules.length > 0) {
                  return (
                    <div
                      key="rich-input"
                      ref={richInputRef}
                      contentEditable={!isLoading}
                      suppressContentEditableWarning
                      onInput={handleRichInput}
                      onKeyDown={handleRichKeyDown}
                      onPaste={handleRichPaste}
                      data-placeholder={currentPlaceholder}
                      data-empty={inputMessage === '' ? 'true' : 'false'}
                      className={`message-input rich-input${isListening ? ' message-input-listening' : ''}`}
                      role="textbox"
                      aria-multiline="true"
                      aria-label="Message"
                    />
                  );
                }

                // Real text is invisible (see .message-input--overlay's
                // color: transparent) -- everything the user actually SEES
                // is the backdrop div behind it, built fresh from
                // inputMessage every render via buildMentionHighlightSegments,
                // so an "@mention" can get its own pill styling a plain
                // <textarea> has no way to apply to just part of its text.
                // The two must stay pixel-identical (font/padding/line-height/
                // white-space, see the CSS) so the real caret always lands
                // exactly where the visible character underneath it is, and
                // scrollTop is mirrored below since the backdrop is a
                // separate DOM node the browser won't scroll in lockstep on
                // its own.
                const syncHighlightScroll = (textarea) => {
                  const hl = messageHighlightRef.current;
                  if (hl && textarea) hl.scrollTop = textarea.scrollTop;
                };
                return (
                  <div key="plain-input" className={`message-input-wrap${isLoading ? ' message-input-wrap--disabled' : ''}`}>
                    <div className="message-input-highlight" aria-hidden="true" ref={messageHighlightRef}>
                      {buildMentionHighlightSegments(inputMessage, highlightNames).map((seg, idx) => (
                        seg.mention
                          ? <span key={idx} className="message-input-mention">{seg.text}</span>
                          : <React.Fragment key={idx}>{seg.text}</React.Fragment>
                      ))}
                    </div>
                    <textarea
                      ref={inputRef}
                      value={inputMessage}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        const textarea = e.target;
                        setInputMessage(nextValue);
                        autoResizeTextarea(textarea);
                        updateMentionFromCaret(nextValue, textarea.selectionStart);
                        syncHighlightScroll(textarea);
                        if (isListening) {
                          void stopListening().finally(() => {
                            setInputMessage(nextValue);
                            autoResizeTextarea(textarea);
                          });
                        }
                      }}
                      onScroll={(e) => syncHighlightScroll(e.target)}
                      onKeyDown={handleComposerKeyDown}
                      placeholder={currentPlaceholder}
                      className={`message-input message-input--overlay${isListening ? ' message-input-listening' : ''}`}
                      rows={1}
                      disabled={isLoading}
                    />
                  </div>
                );
              })()}

              {!sharedConversation && (
                <button
                  type="button"
                  ref={modelButtonRef}
                  className={`model-picker-button${modelMenuOpen ? ' model-picker-button--open' : ''}`}
                  onClick={() => setModelMenuOpen((open) => !open)}
                  title="Choose a model"
                  aria-haspopup="menu"
                  aria-expanded={modelMenuOpen}
                >
                  <span className="model-picker-label">{formatModelNickname(selectedModel)}</span>
                  <FiChevronDown size={14} className="model-picker-chevron" />
                </button>
              )}
              {modelMenuOpen && createPortal(
                <div className="model-menu" role="menu" ref={modelMenuRef}>
                  {availableModels.length === 0 ? (
                    <div className="model-menu-empty">Loading models…</div>
                  ) : (
                    availableModels.map((model) => {
                      const modelId = model.id || model;
                      const isActive = modelId === selectedModel;
                      const tagline = MODEL_TAGLINES[modelId];
                      return (
                        <button
                          key={modelId}
                          type="button"
                          role="menuitem"
                          className={`model-menu-item${isActive ? ' model-menu-item--active' : ''}`}
                          onClick={() => {
                            setModelMenuOpen(false);
                            if (modelId === OPUS_5_5_MODEL_ID && modelId !== selectedModel) {
                              setPendingModelSwitch(modelId);
                              return;
                            }
                            setSelectedModel?.(modelId);
                          }}
                        >
                          <span className="model-menu-item-text">
                            <span className="model-menu-item-label">{getModelVendorLabel(modelId)}</span>
                            {tagline && <span className="model-menu-item-tagline">{tagline}</span>}
                          </span>
                          {isActive && <FiCheck size={14} className="model-menu-item-check" />}
                        </button>
                      );
                    })
                  )}
                </div>,
                document.body
              )}

              {/* "@" mention popover -- lets the composer narrow (or, via
                  "@all", reset) this conversation's knowledge-base scope
                  without leaving the composer for ProjectPanel's own Files
                  tab. Same portal-to-<body>/self-flipping-position pattern
                  as the "+" menu and model picker above. See mentionQuery
                  and .claude/plan/plan_knowledge_scope.md. */}
              {mentionQuery && createPortal(
                <div className="mention-menu" role="listbox" ref={mentionMenuRef}>
                  {activeProjectForMention && !projectFilesById[activeProjectForMention.project_id] ? (
                    projectFilesFailedIds.has(activeProjectForMention.project_id) ? (
                      <div className="mention-menu-empty">Couldn't load this project's files.</div>
                    ) : (
                      <div className="mention-menu-empty">Loading files…</div>
                    )
                  ) : mentionCandidates.length === 0 ? (
                    <div className="mention-menu-empty">
                      {mentionFiles.length === 0 && mentionFolders.length === 0
                        ? "This project's knowledge base has no files yet."
                        : 'No matching files or folders'}
                    </div>
                  ) : (
                    mentionCandidates.map((candidate, index) => {
                      const isActive = index === mentionActiveIndex;
                      const isAll = candidate.kind === 'all';
                      const isFolder = candidate.kind === 'folder';
                      const alreadyMentioned = isAll
                        ? false
                        : isFolder
                          ? isFolderMentioned(currentKnowledgeScope, candidate.folder.folder_id)
                          : isFileMentioned(currentKnowledgeScope, candidate.file.file_id)
                            // A held file isn't in this conversation under
                            // "All files" -- only once picked by name.
                            && !(currentKnowledgeScope?.mode !== 'selected'
                              && heldFiles.some((f) => f.file_id === candidate.file.file_id));
                      const key = isAll ? '__all__' : isFolder ? candidate.folder.folder_id : candidate.file.file_id;
                      const label = isAll ? 'All files' : isFolder ? candidate.folder.name : candidate.file.name;
                      return (
                        <button
                          key={key}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          className={`mention-menu-item${isActive ? ' mention-menu-item--active' : ''}`}
                          onMouseEnter={() => setMentionActiveIndex(index)}
                          // Same reasoning as the rules popover's own Cancel/Save
                          // buttons -- without this, the textarea's blur (focus
                          // moving to this button) would fire before onClick
                          // does, and updateMentionFromCaret has already run
                          // by then, so nothing about this click actually breaks --
                          // but preventing it keeps focus (and the caret) in the
                          // textarea the whole time, which is the more expected feel.
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectMentionCandidate(candidate)}
                        >
                          {isAll ? <FiLayers size={14} /> : isFolder ? <FiFolder size={14} /> : <FiFileText size={14} />}
                          <span className="mention-menu-item-text">{label}</span>
                          {alreadyMentioned && <FiCheck size={13} className="mention-menu-item-check" />}
                        </button>
                      );
                    })
                  )}
                </div>,
                document.body
              )}

              {showSpeechInput && !isMobile && (
                <button
                  type="button"
                  className={`mic-button${isListening ? ' mic-button-listening' : ''}${isListening && !hasTranscript ? ' mic-button-waiting' : ''}${isListening && hasTranscript ? ' mic-button-can-stop' : ''}`}
                  onClick={handleMicToggle}
                  disabled={isLoading}
                  title={
                    isListening
                      ? (hasTranscript ? 'Stop dictation' : 'Cancel dictation (Esc)')
                      : 'Dictate prompt (English only)'
                  }
                  aria-pressed={isListening}
                  aria-busy={isListening && !hasTranscript}
                >
                  {isListening && hasTranscript ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <rect x="6" y="6" width="12" height="12" rx="1" fill="currentColor" />
                    </svg>
                  ) : (
                    <FiMic size={16} />
                  )}
                </button>
              )}

              <button
                onClick={isLoading ? stopGeneration : () => sendMessage()}
                disabled={isLoading ? false : (attachmentUploads.length > 0 || (!inputMessage.trim() && selectedImages.length === 0 && selectedFiles.length === 0))}
                className="send-button"
              >
                {isLoading ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 11L12 6L17 11M12 18V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.txt,.md,.markdown,.csv,.docx,.xlsx,.pptx,.pdf,.eml,.msg,.glb,.gltf,.obj,.fbx,.stl,.3ds,.ply,.3dm,.mp4,.mp3,.m4a"
          multiple
          onChange={handleFileChange}
          className="file-input"
        />

        <ShareConversationModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          conversationName={activeConversation?.name}
          conversationId={activeConversation?.conversation_id}
          onShare={shareConversation}
          projectId={activeConversation?.project_id || null}
          projectName={getActiveProjectName() || ''}
          canManageMembers={['owner', 'edit'].includes(getActiveProject()?.my_role)}
          onManageMembers={onManageProjectMembers && getActiveProject()
            ? () => onManageProjectMembers(getActiveProject())
            : undefined}
          onAddToProject={(conversationId, project) => setConversationProject(conversationId, project.project_id)}
          onAddedToProject={handleAddedToProject}
          groupChatProject={activeConversation?.project_id ? getActiveProject() : null}
          conversation={sharedConversation ? null : activeConversation}
          onMadeGroupChat={onMadeGroupChat
            ? (conversation) => onMadeGroupChat(conversation.conversation_id)
            : undefined}
        />

        <HandoverModal
          isOpen={showHandoverModal}
          onClose={() => setShowHandoverModal(false)}
          conversationId={activeConversation?.conversation_id}
          conversationName={activeConversation?.name}
          groupChat={Boolean(groupChat) && !groupContinuedIn}
          groupAudience={(() => {
            const count = (groupActivity.people?.members || groupChat?.members || []).length;
            return count > 0 ? `the ${count} people in this chat` : 'the people in this chat';
          })()}
          onContinued={(newConversation) => {
            if (typeof onConversationContinued === 'function') {
              onConversationContinued(newConversation);
            }
          }}
        />

        {kbChoicePrompt && (
          <div className="chat-kb-modal-overlay" role="presentation">
            <div
              className="chat-kb-modal"
              role="dialog"
              aria-labelledby="chat-kb-modal-title"
              aria-modal="true"
            >
              {kbChoicePrompt.reason === 'duplicate' && (
                <>
                  <h4 id="chat-kb-modal-title">"{kbChoicePrompt.fileName}" is already in the knowledge base</h4>
                  <p>
                    A file named "{kbChoicePrompt.duplicate.name}" is already in this
                    project's knowledge base, so it hasn't been attached again. KAI uses
                    it in this chat when it's highlighted in the Knowledge base tab.
                    {kbChoicePrompt.canEdit && (
                      ' To use this copy instead, remove the old one there and upload this one.'
                    )}
                  </p>
                </>
              )}
              {kbChoicePrompt.reason === 'too-large' && (
                <>
                  <h4 id="chat-kb-modal-title">"{kbChoicePrompt.fileName}" is too large to attach</h4>
                  <p>
                    It's {formatSize(kbChoicePrompt.size)}, and chat attachments are limited
                    to {formatSize(kbChoicePrompt.limit)}, so it hasn't been attached. To use
                    it here, upload it to this project's knowledge base on the side panel instead.
                  </p>
                </>
              )}
              {kbChoicePrompt.reason === 'choose' && (
                <>
                  <h4 id="chat-kb-modal-title">Add "{kbChoicePrompt.fileName}" to the knowledge base?</h4>
                  <p>
                    Add it to the project's knowledge base, where KAI can use it in every
                    chat here without attaching it to your message, or attach it to this
                    conversation only?
                  </p>
                </>
              )}
              <div className="chat-kb-modal-actions">
                {kbChoicePrompt.reason === 'duplicate' && (
                  <button
                    type="button"
                    className="chat-kb-modal-primary"
                    onClick={() => answerKbChoicePrompt('cancel')}
                  >
                    OK
                  </button>
                )}
                {kbChoicePrompt.reason === 'too-large' && (
                  <button
                    type="button"
                    className="chat-kb-modal-secondary"
                    onClick={() => answerKbChoicePrompt('cancel')}
                  >
                    Cancel
                  </button>
                )}
                {kbChoicePrompt.reason === 'choose' && (
                  <>
                    <button
                      type="button"
                      className="chat-kb-modal-secondary"
                      onClick={() => answerKbChoicePrompt('chat')}
                    >
                      This conversation only
                    </button>
                    <button
                      type="button"
                      className="chat-kb-modal-primary"
                      onClick={() => answerKbChoicePrompt('add')}
                    >
                      Add to knowledge base
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {uploadNotices.length > 0 && (
          <div className="chat-kb-modal-overlay" role="presentation">
            <div
              className="chat-kb-modal"
              role="alertdialog"
              aria-labelledby="chat-upload-notice-title"
              aria-describedby="chat-upload-notice-body"
              aria-modal="true"
            >
              <h4 id="chat-upload-notice-title">{uploadNotices[0].title}</h4>
              <p id="chat-upload-notice-body" className="chat-upload-notice-body">
                {uploadNotices[0].body}
              </p>
              <div className="chat-kb-modal-actions">
                <button
                  type="button"
                  className="chat-kb-modal-primary"
                  onClick={dismissUploadNotice}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {pendingModelSwitch && (
          <div
            className="model-switch-modal-overlay"
            role="presentation"
            onClick={() => setPendingModelSwitch(null)}
          >
            <div
              className="model-switch-modal"
              role="dialog"
              aria-labelledby="model-switch-modal-title"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 id="model-switch-modal-title">Switch to Claude Opus 5.5?</h4>
              <p>
                Opus is great for complex tasks but comes with a higher cost
                per response. Continue?
              </p>
              <div className="model-switch-modal-actions">
                <button
                  type="button"
                  className="model-switch-modal-cancel"
                  onClick={() => setPendingModelSwitch(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="model-switch-modal-confirm"
                  onClick={() => {
                    setSelectedModel?.(pendingModelSwitch);
                    setPendingModelSwitch(null);
                  }}
                >
                  Yes, switch to Opus
                </button>
              </div>
            </div>
          </div>
        )}

        {pdfPreview && (
          <div
            className="pdf-preview-modal-overlay"
            role="presentation"
            onClick={() => setPdfPreview(null)}
          >
            <div
              className="pdf-preview-modal"
              role="dialog"
              aria-labelledby="pdf-preview-modal-title"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pdf-preview-modal-header">
                <span id="pdf-preview-modal-title" className="pdf-preview-modal-title">
                  {pdfPreview.name} · page {pdfPreview.page}
                </span>
                <div className="pdf-preview-modal-header-actions">
                  <a
                    href={`${pdfPreview.url}#page=${pdfPreview.page}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pdf-preview-modal-open-tab"
                    title="Open the whole PDF in a new tab"
                  >
                    {pdfPreview.fileId ? 'Open PDF' : 'Open in new tab'}
                  </a>
                  <button
                    type="button"
                    className="pdf-preview-modal-close"
                    onClick={() => setPdfPreview(null)}
                    title="Close preview"
                  >
                    ×
                  </button>
                </div>
              </div>
              {pdfPreview.fileId && !pdfPageDrawing?.failed ? (
                <div className="pdf-preview-modal-page">
                  {pdfPageDrawing?.url ? (
                    <a href={pdfPageDrawing.url} target="_blank" rel="noopener noreferrer" title="Open full size">
                      <img src={pdfPageDrawing.url} alt={`${pdfPreview.name} page ${pdfPreview.page}`} />
                    </a>
                  ) : (
                    <span className="pdf-preview-modal-loading">Loading page {pdfPreview.page}…</span>
                  )}
                </div>
              ) : (
                /* An <iframe>, not <embed>/react-pdf: every major browser's
                   built-in PDF viewer already honors a #page=N fragment, so
                   this needs no bundled PDF-rendering library to land on the
                   right page. Used when the page can't be drawn. */
                <iframe
                  src={`${pdfPreview.url}#page=${pdfPreview.page}`}
                  title={`${pdfPreview.name} page ${pdfPreview.page}`}
                  className="pdf-preview-modal-frame"
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* The plain (non-project) empty-chat screen's quick-start shortcuts --
          rendered as .input-container's own next sibling, so they sit below
          the composer instead of above it, matching a project's own empty
          screen (icon/title/description, then the composer, with nothing
          else competing for attention above it). Same visibility condition
          as the empty-chat branch itself (see the messages-container render
          above): zero messages, not mid-history-load, not mid-auto-send.
          A project's empty screen shows them too when a focus mode other
          than General is on (getFocusModeName), since that mode's prompts
          work there; General Focus in a project keeps its own plain screen. */}
      {showEmptyChat && !isLoadingHistory && !isAutoSendingProjectFirstMessage
        && (!getActiveProject() || getFocusModeName()) && !sharedConversation && (
        <div className="example-prompts-footer">
          <div className="example-prompts-heading">Few things you could try</div>
          <div className="example-prompts">
            {getExamplePrompts().map((example, index) => (
              <button
                key={index}
                className="example-prompt"
                onClick={() => {
                  if (example.prompt) {
                    setInputMessage(example.prompt);
                    focusInput();
                  } else {
                    // Special case for file upload
                    fileInputRef.current?.click();
                  }
                }}
              >
                {example.icon} {example.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
    {websitePreview && (
      <>
        <div
          className="preview-resize-handle"
          onMouseDown={handlePreviewResizeStart}
        />
        <div
          className="preview-panel-resizable"
          style={previewWidth ? { width: previewWidth, flex: 'none' } : undefined}
        >
          <WebsitePreviewPanel
            websitePreview={websitePreview}
            onClose={handleClosePreview}
            onRefresh={handleRefreshPreview}
            isBuilding={isLoading}
          />
        </div>
      </>
    )}
    </div>
    </MemberPhotosProvider>
  );
};

export default ChatInterface;
